import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  canEngineNameRunPhase,
  getDefaultPipelineDefinition,
} from '@internal/spassu-code-contract';
import { useProjectRole } from '../../hooks/useProjectRole';
import {
  Content,
  Header,
  MarkdownContent,
  Page,
  Progress,
  EmptyState,
  WarningPanel,
} from '@backstage/core-components';
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReplayIcon from '@material-ui/icons/Replay';
import { Alert } from '@material-ui/lab';
import Skeleton from '@material-ui/lab/Skeleton';
import { isResumeActivityStreamError, spassuCodeApiRef } from '../../api';
import {
  Activity,
  Approach,
  Artifact,
  ChatMessage,
  InteractionState,
  ResumeStreamConflict,
  PipelineDefinition,
  Phase,
  PHASE_LABELS,
  Question,
  StreamEvent,
} from '../../api/types';
import { rootRouteRef } from '../../routes';
import { PhaseStepper } from '../PhaseStepper';
import { ChatPanel } from '../ChatPanel';
import { StreamingIndicator } from '../ChatPanel/StreamingIndicator';
import { MetricsPanel } from '../MetricsPanel';
import { PrdBriefingComposer } from '../PrdBriefingComposer';
import { TaskListPanel } from '../TaskListPanel';
import { QuestionCard } from '../QuestionCard';
import { ApproachViewer } from '../ApproachViewer';
import { PendingInteractionSurface } from '../PendingInteractionSurface';
import { isPhaseConformityReadyForApproval } from './phaseConformityApproval';
import { buildPrReadingContent } from './prReadingContent';
import { getPhaseConformityRecoveryView } from './phaseConformityRecovery';
import {
  buildPhaseConformityRepairNotice,
  isPhaseConformityRepairNoticeCurrent,
  type PhaseConformityRepairNotice,
} from './phaseConformityRepairNotice';
import {
  formatUserEntityRef,
  getCatalogEntityDisplayName,
} from '../../utils/format';
import { normalizeRouteRefPath } from '../../utils/routes';
import { ActivityHeader, AI_ENGINE_LABELS } from '../ActivityHeader';
import { ControlBar } from '../ControlBar';
import {
  mergeRecoveryStreamingContent,
  normalizeRecoverySnapshotContent,
  sanitizeArtifactDraftContent,
  summarizeToolUseMarkersForDisplay,
  type ToolUseSummary,
} from './recoveryContent';
import {
  PostAnswerRecoveryStage,
  getPostAnswerRecoverySettleAction,
  getPostAnswerRecoveryUiState,
  initialPostAnswerRecoveryState,
  postAnswerRecoveryReducer,
} from './postAnswerRecovery';
import { usePhaseDataLoaders } from './usePhaseDataLoaders';
import { usePilotRecoveryState } from './usePilotRecoveryState';
import { usePilotReplayCursor } from './usePilotReplayCursor';
import { usePilotStreamTransport } from './usePilotStreamTransport';
import { usePilotTabOwnership } from './usePilotTabOwnership';
import { ModelFallbackNotice } from './ModelFallbackNotice';

type ViewMode = 'reading' | 'chat';

/**
 * Origem do erro exibido na faixa da atividade (iniciativa
 * `erro-ui-por-origem`). `carregamento` é o único que `loadActivity` pode
 * limpar sozinho; `geral` cobre todo o resto — erro do agente, falha de ação
 * do usuário — e só sai no fechar manual (D-EUO-4).
 */
type ActivityErrorOrigin = 'carregamento' | 'geral';

interface ActivityErrorState {
  origem: ActivityErrorOrigin;
  mensagem: string;
}
type InteractionUiStatus =
  | 'idle'
  | 'streaming'
  | 'awaiting_user'
  | 'recovering'
  | 'resumable'
  | 'expired'
  | 'completed';

type RecoveryTrigger =
  | 'focus'
  | 'online'
  | 'answer_submitted'
  | 'stream_terminal'
  | 'reattach_failed';

type ReactiveRecoveryTrigger =
  | 'focus'
  | 'online'
  | 'remount'
  | 'answer_submitted'
  | 'visibility_change';

type RecoverySyncOutcome = 'resume' | 'rebuild' | 'noop';

type RecoveryRebuildReason =
  | 'cursor_mismatch'
  | 'stream_conflict'
  | 'terminal_state';

type SubmittedPendingInteractionType = 'question' | 'approach';

interface SubmittedPendingInteraction {
  phase: Phase;
  type: SubmittedPendingInteractionType;
  interactionId: string | null;
  signature: string | null;
}

export const hasAttachedPilotStreamTransport = ({
  chatStreamAbortController,
  recoveryStreamAbortController,
}: {
  chatStreamAbortController: AbortController | null;
  recoveryStreamAbortController: AbortController | null;
}): boolean =>
  Boolean(chatStreamAbortController) || Boolean(recoveryStreamAbortController);

interface WorkerStatusWakeSnapshot {
  connected: boolean;
  resolved: boolean;
}

interface RecoverySyncRequest {
  trigger: RecoveryTrigger;
  phase?: Phase;
  conflict?: ResumeStreamConflict;
  skipChatReconnect?: boolean;
  workerStatusAtWake?: WorkerStatusWakeSnapshot;
}

interface RecoveryResumeRequest {
  interactionStateId: string;
  token: number;
}

type ArtifactDisplaySource = 'approved' | 'draft' | 'none';

interface ArtifactDisplayState {
  source: ArtifactDisplaySource;
  content: string;
  updatedAt?: string;
}

interface AuthoritativeSyncFailureState {
  kind: 'transient' | 'persistent';
  attempt: number;
}

const READABILITY_PRIORITY_PHASES: Phase[] = [
  'gerando-prd',
  'gerando-techspec',
  'dividindo-tasks',
  'executando',
  'qa',
  'pronto',
  'finalizado',
];

const PASSIVE_STREAM_WARNING =
  'Outra aba está conduzindo este stream. Esta aba seguirá em modo passivo até a liberação do ownership.';

function getRecoveryTriggerPriority(trigger: RecoveryTrigger): number {
  switch (trigger) {
    case 'reattach_failed':
      return 5;
    case 'stream_terminal':
      return 4;
    case 'answer_submitted':
      return 3;
    case 'online':
      return 2;
    case 'focus':
    default:
      return 1;
  }
}

function mergeRecoverySyncRequest(
  current: RecoverySyncRequest | null,
  incoming: RecoverySyncRequest,
): RecoverySyncRequest {
  if (!current) {
    return incoming;
  }

  if (
    getRecoveryTriggerPriority(incoming.trigger) >=
    getRecoveryTriggerPriority(current.trigger)
  ) {
    return {
      ...incoming,
      phase: incoming.phase ?? current.phase,
      skipChatReconnect:
        incoming.skipChatReconnect ?? current.skipChatReconnect,
      workerStatusAtWake:
        incoming.workerStatusAtWake ?? current.workerStatusAtWake,
    };
  }

  return {
    ...current,
    phase: incoming.phase ?? current.phase,
    conflict: current.conflict ?? incoming.conflict,
    skipChatReconnect: current.skipChatReconnect ?? incoming.skipChatReconnect,
    workerStatusAtWake:
      current.workerStatusAtWake ?? incoming.workerStatusAtWake,
  };
}

const useStyles = makeStyles(theme => ({
  metricsBar: {
    marginBottom: theme.spacing(1),
  },
  chatContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: 'calc(100vh - 220px)',
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  artifactMainCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    minHeight: 'clamp(360px, 58vh, 760px)',
  },
  artifactMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  artifactMetaText: {
    color: theme.palette.text.secondary,
  },
  restartContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  errorAlert: {
    marginBottom: theme.spacing(1),
  },
  qaInfoAlert: {
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '& .MuiAlert-icon': {
      color: theme.palette.common.white,
    },
  },
  loadingSkeleton: {
    marginTop: theme.spacing(1),
  },
  questionsDialogContent: {
    padding: theme.spacing(2.5, 3, 3),
    overflowX: 'hidden' as const,
  },
  questionsDialogPaper: {
    width: 'calc(100vw - 32px)',
    maxWidth: 960,
    margin: theme.spacing(2),
  },
  finalizedTitle: {
    fontWeight: 700,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(0.25),
  },
  finalizedSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
  },
  finalizedTotalRow: {
    '& td': {
      fontWeight: 700,
      borderTop: `2px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
  },
  finalizedTotalCost: {
    color: theme.palette.secondary.main,
    fontWeight: 700,
  },
  debugContainer: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  debugHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0.5),
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  debugContent: {
    padding: theme.spacing(0, 0.5, 1),
  },
  prdReadingActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  prdReadingEditor: {
    flex: 1,
  },
  prdReadingPreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 3),
    minHeight: 'clamp(280px, 56vh, 720px)',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 600,
      lineHeight: 1.35,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      letterSpacing: 0,
      color: theme.palette.text.primary,
    },
    '& h1': {
      fontSize: '1.25rem',
    },
    '& h2': {
      fontSize: '1.1rem',
    },
    '& h3': {
      fontSize: '1rem',
    },
    '& h4, & h5, & h6': {
      fontSize: '0.875rem',
    },
    '& h1:first-child, & h2:first-child, & h3:first-child': {
      marginTop: 0,
    },
    '& p, & ul, & ol, & table, & pre': {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    '& p, & ul, & ol, & li': {
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.875rem',
      lineHeight: 1.65,
    },
    '& table': {
      fontSize: '0.8125rem',
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      overflowX: 'auto',
    },
    '& code': {
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: '0.8125rem',
    },
  },
  taskPlanReadingIndicator: {
    marginTop: theme.spacing(2),
  },
}));

import { streamUiReducer, initialStreamUiState } from './streamUiReducer';
import type { StreamUiState, StreamUiAction } from './streamUiReducer';

export type { StreamUiState, StreamUiAction };
export { streamUiReducer, initialStreamUiState };

function resolveActivityId(
  params: Readonly<Record<string, string | undefined>>,
  pathname: string,
): string {
  if (params.activityId) {
    return params.activityId;
  }

  const wildcard = params['*'] ?? '';
  if (wildcard && !wildcard.includes('/')) {
    return wildcard;
  }

  const pathMatch = pathname.match(/\/spassu-code\/([^/]+)$/);
  return pathMatch?.[1] ?? wildcard;
}

function buildInitialMessage(
  activity: Activity,
  artifacts: Artifact[],
  phase: Phase,
): string {
  const sections: string[] = [`# ${activity.title}\n\n${activity.description}`];

  const prd = selectPreferredArtifactForPhase(
    artifacts,
    'gerando-prd',
  )?.content;
  const techspec = selectPreferredArtifactForPhase(
    artifacts,
    'gerando-techspec',
  )?.content;
  const tasks = selectPreferredArtifactForPhase(
    artifacts,
    'dividindo-tasks',
  )?.content;

  if (
    phase === 'gerando-techspec' ||
    phase === 'dividindo-tasks' ||
    phase === 'executando' ||
    phase === 'qa'
  ) {
    if (prd) sections.push(`## PRD\n\n${prd}`);
  }

  if (phase === 'dividindo-tasks' || phase === 'executando' || phase === 'qa') {
    if (techspec) sections.push(`## TechSpec\n\n${techspec}`);
  }

  if (phase === 'executando' || phase === 'qa') {
    if (tasks) sections.push(`## Tasks\n\n${tasks}`);
  }

  if (phase === 'qa') {
    sections.push(
      '## Código\n\n[Código gerado na fase anterior - consulte o repositório]',
    );
  }

  return sections.join('\n\n');
}

function getArtifactTimestamp(value: string): number {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function selectPreferredArtifactForPhase(
  artifacts: Artifact[],
  phase: Phase,
): Artifact | undefined {
  let selected: Artifact | undefined;

  for (const artifact of artifacts) {
    if (artifact.phase !== phase || artifact.artifactType === 'context-file') {
      continue;
    }

    if (!selected) {
      selected = artifact;
      continue;
    }

    const shouldPreferApproved = artifact.approved && !selected.approved;
    const sameApprovalState = artifact.approved === selected.approved;
    const isSameOrNewer =
      getArtifactTimestamp(artifact.createdAt) >=
      getArtifactTimestamp(selected.createdAt);

    if (shouldPreferApproved || (sameApprovalState && isSameOrNewer)) {
      selected = artifact;
    }
  }

  return selected;
}

function buildAutoStartMessage(skill: string | null): string {
  const phaseSkill = skill?.trim();

  if (phaseSkill) {
    return `Fase iniciada automaticamente. Use o comando de fase \`${phaseSkill}\` e finalize via \`approve_phase\`.`;
  }

  return 'Fase iniciada automaticamente. Execute a fase conforme o contexto e finalize via `approve_phase`.';
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const PRONTO_PHASE_SYNC_RETRY_DELAYS_MS = [0, 600, 1500, 3000] as const;
const PILOT_WAKE_WORKER_STATUS_RETRY_DELAYS_MS = [500, 500, 500, 500] as const;
const PILOT_INTERACTION_PHASES: Phase[] = [
  'gerando-prd',
  'gerando-techspec',
  'dividindo-tasks',
];
const CHAT_DRAFT_STORAGE_PREFIX = 'spassu-code:chat-draft';
const DISMISSED_INTERACTION_STORAGE_PREFIX =
  'spassu-code:interaction-dismissed';
const PILOT_RECOVERY_SNAPSHOT_STORAGE_PREFIX =
  'spassu-code:pilot-recovery-snapshot';
const PILOT_RECOVERY_POLL_INTERVAL_MS = 2_000;
const PILOT_RECOVERY_POLL_MAX_ATTEMPTS = 30;
const PRD_STREAM_BATCH_FLUSH_MS = 60;
// Must outlive the bounded reattach probe plus the 2s -> 4s -> 8s
// authoritative-sync backoff ladder so the user can reach the persistent
// retry CTA before we classify the flow as expired.
const POST_ANSWER_RECOVERY_TIMEOUT_MS = 25_000;
const POST_ANSWER_FAST_POLL_INTERVAL_MS = 500;
const POST_ANSWER_SLOW_POLL_INTERVAL_MS = 2_000;
const POST_ANSWER_FAST_POLL_WINDOW_MS = 10_000;
const POST_ANSWER_POLL_MAX_ATTEMPTS = 180;
const POST_ANSWER_REATTACH_RETRY_INTERVAL_MS = 1_000;
const POST_ANSWER_REATTACH_MAX_RETRIES = 3;
const POST_ANSWER_ARTIFACT_LIVE_RETRY_INTERVAL_MS = 2_000;
const POST_ANSWER_REACTIVE_TRIGGER_COOLDOWN_MS = 2_000;
const POST_ANSWER_SYNC_FAILURE_BACKOFF_MS = [2_000, 4_000, 8_000] as const;
const INITIAL_HYDRATION_REPOLL_DELAY_MS = 1_000;
const APPROVAL_CONVERGENCE_RETRY_INTERVAL_MS = 1_000;
const APPROVAL_CONVERGENCE_MAX_ATTEMPTS = 4;

function isPilotInteractionPhase(phase: Phase): boolean {
  return PILOT_INTERACTION_PHASES.includes(phase);
}

function isArtifactGenerationPhase(phase: Phase): boolean {
  return (
    phase === 'gerando-prd' ||
    phase === 'gerando-techspec' ||
    phase === 'dividindo-tasks'
  );
}

function areChatMessagesEqual(
  previousMessages: ChatMessage[],
  nextMessages: ChatMessage[],
): boolean {
  if (previousMessages === nextMessages) {
    return true;
  }

  if (previousMessages.length !== nextMessages.length) {
    return false;
  }

  return previousMessages.every((message, index) => {
    const nextMessage = nextMessages[index];

    return (
      message.id === nextMessage.id &&
      message.phase === nextMessage.phase &&
      message.role === nextMessage.role &&
      message.content === nextMessage.content &&
      message.timestamp === nextMessage.timestamp
    );
  });
}

function areStringArraysEqual(
  previousValues: string[],
  nextValues: string[],
): boolean {
  return (
    previousValues.length === nextValues.length &&
    previousValues.every((value, index) => value === nextValues[index])
  );
}

function arePhaseConformitySummariesEqual(
  previous: Activity['phaseConformity'],
  next: Activity['phaseConformity'],
): boolean {
  if (previous === next) {
    return true;
  }
  if (!previous || !next) {
    return false;
  }
  return (
    previous.phase === next.phase &&
    previous.phaseAttemptId === next.phaseAttemptId &&
    previous.status === next.status &&
    previous.recoveryAction === next.recoveryAction &&
    areStringArraysEqual(previous.missingReasonCodes, next.missingReasonCodes)
  );
}

function areActivitySnapshotsEqual(
  previous: Activity,
  next: Activity,
): boolean {
  return (
    previous.currentPhase === next.currentPhase &&
    previous.status === next.status &&
    previous.updatedAt === next.updatedAt &&
    arePhaseConformitySummariesEqual(
      previous.phaseConformity,
      next.phaseConformity,
    )
  );
}

function getPilotRecoverySnapshotStorageKey(
  activityId: string,
  phase: Phase,
): string {
  return `${PILOT_RECOVERY_SNAPSHOT_STORAGE_PREFIX}:${activityId}:${phase}`;
}

function isHiddenSystemUserMessage(message: ChatMessage): boolean {
  return (
    message.role === 'user' &&
    (message.content.startsWith('# ') ||
      message.content.startsWith('Fase iniciada automaticamente') ||
      message.content.startsWith('Fase retomada automaticamente'))
  );
}

const PRD_SECTION_HEADERS = [
  'Vis[aã]o Geral',
  'Objetivos',
  'Hist[oó]rias de Usu[aá]rio',
  'Funcionalidades Principais',
  'Experi[eê]ncia do Usu[aá]rio',
  'Restri[cç][oõ]es T[eé]cnicas',
  'Fora de Escopo',
];

const PRD_SECTION_PATTERN = new RegExp(
  `^#{1,3}\\s+(${PRD_SECTION_HEADERS.join('|')})`,
  'i',
);

function extractReadablePrdArtifactContent(
  content: string | undefined | null,
): string {
  if (!content) {
    return '';
  }

  const lines = content.split('\n');

  // Marcador principal: "PRD —"
  const markerIndex = lines.findIndex(line => line.includes('PRD —'));
  if (markerIndex !== -1) {
    return lines.slice(markerIndex).join('\n').trim();
  }

  // Fallback: procura pelo primeiro header de secao especifica do template PRD
  const sectionIndex = lines.findIndex(line => PRD_SECTION_PATTERN.test(line));
  if (sectionIndex !== -1) {
    return lines.slice(sectionIndex).join('\n').trim();
  }

  return content;
}

const TECHSPEC_SECTION_HEADERS = [
  'Resumo Executivo',
  'Arquitetura do Sistema',
  'Design de Implementa[cç][aã]o',
  'Pontos de Integra[cç][aã]o',
  'Abordagem de Testes',
  'Sequenciamento de Desenvolvimento',
];

const TECHSPEC_SECTION_PATTERN = new RegExp(
  `^#{1,3}\\s+(${TECHSPEC_SECTION_HEADERS.join('|')})`,
  'i',
);

function extractReadableTechSpecContent(
  content: string | undefined | null,
): string {
  if (!content) {
    return '';
  }

  const lines = content.split('\n');

  // Marcador principal: "TechSpec —" ou "Tech Spec —"
  const markerIndex = lines.findIndex(
    line => line.includes('TechSpec —') || line.includes('Tech Spec —'),
  );
  if (markerIndex !== -1) {
    return lines.slice(markerIndex).join('\n').trim();
  }

  // Fallback: procura pelo primeiro header de secao especifica do template TechSpec
  const sectionIndex = lines.findIndex(line =>
    TECHSPEC_SECTION_PATTERN.test(line),
  );
  if (sectionIndex !== -1) {
    return lines.slice(sectionIndex).join('\n').trim();
  }

  return content;
}

function formatInteractionTimestamp(
  timestamp: string | null | undefined,
): string | null {
  if (!timestamp) {
    return null;
  }

  return new Date(timestamp).toLocaleString('pt-BR');
}

function getInteractionTerminalReasonLabel(
  reason: string | null | undefined,
): string {
  switch (reason) {
    case 'timeout':
      return 'Tempo limite excedido';
    case 'session_lost':
      return 'Worker local desconectou — conecte o worker e envie "continuar" no chat para retomar.';
    case 'worker_disconnected':
      return 'Worker local desconectado — conecte o worker e envie "continuar" no chat para retomar.';
    case 'restart':
      return 'Fase reiniciada';
    case 'new_message':
      return 'Nova mensagem iniciou outra interação';
    case 'approaches_timeout':
      return 'Tempo para escolher a abordagem expirou — envie "continuar" no chat para retomar.';
    default:
      return 'Interação encerrada';
  }
}

function getPostAnswerRecoveryStageLabel(
  stage: PostAnswerRecoveryStage,
): string | null {
  switch (stage) {
    case 'answer_sent':
      return 'Resposta enviada';
    case 'stream_reattach_attempt':
      return 'Reconectando stream';
    case 'stream_live':
      return 'Stream ativa';
    case 'backend_processing':
      return 'Processando no backend';
    case 'awaiting_new_interaction':
      return 'Nova pergunta recebida';
    case 'completed':
      return 'Concluído';
    case 'failed_timeout':
      return 'Sem progresso';
    case 'idle':
    default:
      return null;
  }
}

function normalizeRecoveredReplayFingerprintContent(
  content: string | undefined | null,
): string {
  return content ? content.replace(/\s+/g, ' ').trim() : '';
}

function mergeToolUseSummaries(
  ...summaries: Array<ToolUseSummary | null | undefined>
): ToolUseSummary | null {
  const tools: string[] = [];
  const toolSet = new Set<string>();
  let totalEvents = 0;

  for (const summary of summaries) {
    if (!summary || summary.totalEvents <= 0) {
      continue;
    }
    totalEvents += summary.totalEvents;
    for (const tool of summary.tools) {
      if (!toolSet.has(tool)) {
        toolSet.add(tool);
        tools.push(tool);
      }
    }
  }

  return totalEvents > 0 ? { tools, totalEvents } : null;
}

export const ActivityDetailPage = () => {
  const classes = useStyles();
  const params = useParams();
  const location = useLocation();
  const rootRoute = useRouteRef(rootRouteRef);
  const activityId = useMemo(
    () => resolveActivityId(params, location.pathname),
    [location.pathname, params],
  );
  const api = useApi(spassuCodeApiRef);
  const catalogApi = useApi(catalogApiRef);
  const {
    hasPermission,
    loading: rbacLoading,
    error: rbacError,
    refresh: refreshProjectRole,
  } = useProjectRole();

  const [activity, setActivity] = useState<Activity>();
  const [pipelineDefinition, setPipelineDefinition] =
    useState<PipelineDefinition>(getDefaultPipelineDefinition());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Metrics and artifacts — extracted to usePhaseDataLoaders hook
  const { metrics, artifacts, loadMetrics, loadArtifacts } =
    usePhaseDataLoaders(activityId, api);
  const [contextFiles, setContextFiles] = useState<Artifact[]>([]);

  const loadContextFiles = useCallback(async () => {
    if (!activityId) return;
    try {
      const files = await api.getContextFiles(activityId);
      setContextFiles(files);
    } catch {
      // silently ignore — context files are non-critical
    }
  }, [activityId, api]);

  // ─── Briefing: Description (saved on "Iniciar PRD") ───
  const [briefingDescription, setBriefingDescription] = useState('');

  // Initialize briefing description from activity
  useEffect(() => {
    if (activity?.description) {
      setBriefingDescription(activity.description);
    }
  }, [activity?.description]);

  const handleContextFileUpload = useCallback(
    async (file: File) => {
      if (!activityId) return;
      const uploaded = await api.uploadContextFile(activityId, file);
      setContextFiles(prev => [...prev, uploaded]);
    },
    [activityId, api],
  );

  const handleContextFileDelete = useCallback(
    async (fileId: string) => {
      if (!activityId) return;
      await api.deleteContextFile(activityId, fileId);
      setContextFiles(prev => prev.filter(f => f.id !== fileId));
    },
    [activityId, api],
  );

  const [finalizedExpanded, setFinalizedExpanded] = useState(true);
  const [selectedPhase, setSelectedPhase] = useState<Phase>('gerando-prd');
  const [viewMode, setViewMode] = useState<ViewMode>('chat');
  const [streamUi, dispatchStreamUi] = useReducer(
    streamUiReducer,
    initialStreamUiState,
  );
  const [interactionUiStatus, setInteractionUiStatus] =
    useState<InteractionUiStatus>('idle');
  const [postAnswerRecovery, dispatchPostAnswerRecovery] = useReducer(
    postAnswerRecoveryReducer,
    initialPostAnswerRecoveryState,
  );
  const [pilotRecoveryResumeRequest, setPilotRecoveryResumeRequest] =
    useState<RecoveryResumeRequest | null>(null);
  const [postAnswerRecoveryElapsedMs, setPostAnswerRecoveryElapsedMs] =
    useState(0);
  const [authoritativeSyncFailureState, setAuthoritativeSyncFailureState] =
    useState<AuthoritativeSyncFailureState | null>(null);
  const [persistedInteractionState, setPersistedInteractionState] =
    useState<InteractionState | null>(null);
  const [chatDraft, setChatDraft] = useState('');
  const [currentTool, setCurrentTool] = useState<string | undefined>();
  const [toolCount, setToolCount] = useState(0);
  const [pendingInteractionModalOpen, setPendingInteractionModalOpen] =
    useState(false);
  const [dismissedInteractionIds, setDismissedInteractionIds] = useState<
    string[]
  >([]);
  const [recoverySnapshotContent, setRecoverySnapshotContent] = useState('');
  const [stableRecoverySnapshotContent, setStableRecoverySnapshotContent] =
    useState('');
  const [isPrdReadingEditing, setIsPrdReadingEditing] = useState(false);
  const [prdReadingDraftContent, setPrdReadingDraftContent] = useState('');
  const [isPrdReadingSaving, setIsPrdReadingSaving] = useState(false);
  const [isTechSpecReadingEditing, setIsTechSpecReadingEditing] =
    useState(false);
  const [techSpecReadingDraftContent, setTechSpecReadingDraftContent] =
    useState('');
  const [isTechSpecReadingSaving, setIsTechSpecReadingSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const initialLoadDoneRef = useRef(false);
  // erro-ui-por-origem: o slot de erro é UM para ~20 origens (carregamento,
  // stream do agente, aprovar fase, salvar PRD…). Guardar a origem junto existe
  // para que `loadActivity` — que roda em background — limpe apenas o erro que
  // ele mesmo escreveu, em vez de apagar o de todo mundo. Era o que fazia a
  // faixa do erro de API aparecer e sumir sozinha (o recovery pedido pelo
  // próprio erro chamava `loadActivity`, que o apagava).
  const [errorState, setErrorState] = useState<ActivityErrorState>();
  const error = errorState?.mensagem;
  /**
   * `setError(msg)` mantém a assinatura antiga de propósito: os ~20 pontos que
   * já escrevem erro não mudam e entram como `'geral'`. Só quem precisa ser
   * distinguido declara a origem.
   */
  const setError = useCallback(
    (mensagem?: string, origem: ActivityErrorOrigin = 'geral') => {
      setErrorState(mensagem === undefined ? undefined : { origem, mensagem });
    },
    [],
  );
  /** Limpa só o que veio da origem dada — nunca o erro alheio. */
  const clearErrorFromOrigin = useCallback((origem: ActivityErrorOrigin) => {
    setErrorState(prev => (prev?.origem === origem ? undefined : prev));
  }, []);
  const [phaseConformityRepairNotice, setPhaseConformityRepairNotice] =
    useState<PhaseConformityRepairNotice & { activityId: string }>();
  const [lockedByOwner, setLockedByOwner] = useState(false);
  const [phaseSyncWarning, setPhaseSyncWarning] = useState<string>();
  const [approving, setApproving] = useState(false);
  const [revalidatingConformity, setRevalidatingConformity] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [projectLabels, setProjectLabels] = useState<Record<string, string>>(
    {},
  );
  const [createdByLabel, setCreatedByLabel] = useState<string>();
  const [workerConnected, setWorkerConnected] = useState(false);
  const [workerStatusResolved, setWorkerStatusResolved] = useState(false);
  const [pendingFrontendAutoStartPhase, setPendingFrontendAutoStartPhase] =
    useState<Phase | null>(null);
  const lastActivityRef = useRef<Activity>();
  const lastReplayCursorActivityIdRef = useRef<string | null>(null);
  const pilotRecoveryResumeTokenRef = useRef(0);
  const pilotRecoveryResumeRequestRef = useRef<RecoveryResumeRequest | null>(
    null,
  );
  const pilotRecoveryResumeSuppressedInteractionIdRef = useRef<string | null>(
    null,
  );
  const pilotRecoverySyncInFlightRef =
    useRef<Promise<RecoverySyncOutcome> | null>(null);
  const queuedPilotRecoveryRequestRef = useRef<RecoverySyncRequest | null>(
    null,
  );
  const selectedPhaseRef = useRef<Phase>('gerando-prd');
  const lastSeenPhaseRef = useRef<Phase | null>(null);
  const isRestartingRef = useRef(false);
  const phaseReconcileRunRef = useRef(0);
  const pilotPhaseSyncKeyRef = useRef<string | null>(null);
  const pilotRecoveryStreamAttemptRef = useRef<string | null>(null);
  const pilotRecoveryEventReceivedRef = useRef(false);
  const lastInterruptTimestampRef = useRef(0);
  const workerRecoverySyncKeyRef = useRef<string | null>(null);
  const workerRecoveryBootstrapKeyRef = useRef<string | null>(null);
  const pendingQuestionsRef = useRef<Question[] | undefined>(undefined);
  const pendingApproachesRef = useRef<
    { question: string; approaches: Approach[] } | undefined
  >(undefined);
  const postAnswerReattachTimerRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const postAnswerReattachInFlightRef = useRef(false);
  const postAnswerReattachAttemptsRef = useRef(0);
  const postAnswerRecoveryActiveRef = useRef(false);
  const postAnswerRecoveryStageRef = useRef<PostAnswerRecoveryStage>('idle');
  const postAnswerInitialMessageCountRef = useRef(0);
  const fallbackPostAnswerToPollingRef = useRef<
    ((initialMessageCount?: number) => void) | null
  >(null);
  const retryPostAnswerPollingRef = useRef<(() => void) | null>(null);
  const recoveryTriggerCooldownRef = useRef<
    Partial<Record<`${ReactiveRecoveryTrigger}:${Phase}`, number>>
  >({});
  const initialHydrationRepollKeyRef = useRef<string | null>(null);
  const persistedInteractionStateIdRef = useRef<string | null>(null);
  const submittedPendingInteractionRef =
    useRef<SubmittedPendingInteraction | null>(null);
  const recoveredReplayCycleKeyRef = useRef<string | null>(null);
  const recoveredReplayFingerprintsRef = useRef<Set<string>>(new Set());
  const handleReconnectRef = useRef<(() => Promise<void>) | null>(null);
  const lastSuccessfulApprovePermissionRef = useRef<Record<string, boolean>>(
    {},
  );
  const workerConnectedRef = useRef(workerConnected);
  const workerStatusResolvedRef = useRef(workerStatusResolved);
  const requestPilotRecoverySyncRef = useRef<
    ((request: RecoverySyncRequest) => Promise<RecoverySyncOutcome>) | null
  >(null);

  // ADR-013: live streaming continuity across phase_auto_transition.
  // Same-SSE pilot-to-pilot transitions must not let the local reset cascade
  // zero out `isStreaming`/interaction status and hide the live chunks on the
  // new phase. The ref guards the two follow-up effects (phase_changed reset
  // and pilot recovery focus sync); the state drives the sync banner.
  const liveTransitionInProgressRef = useRef(false);
  const liveTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const LIVE_TRANSITION_DEAD_MAN_MS = 5_000;
  const [isLiveTransitionPending, setIsLiveTransitionPending] = useState(false);
  const clearLiveTransitionGuard = useCallback(() => {
    if (liveTransitionTimeoutRef.current !== null) {
      clearTimeout(liveTransitionTimeoutRef.current);
      liveTransitionTimeoutRef.current = null;
    }
    if (liveTransitionInProgressRef.current) {
      liveTransitionInProgressRef.current = false;
    }
    setIsLiveTransitionPending(false);
  }, []);
  useEffect(() => {
    return () => {
      if (liveTransitionTimeoutRef.current !== null) {
        clearTimeout(liveTransitionTimeoutRef.current);
        liveTransitionTimeoutRef.current = null;
      }
    };
  }, []);

  const HEARTBEAT_TIMEOUT_MS = 35_000;
  const handlePilotHeartbeatTimeout = useCallback(() => {
    // eslint-disable-next-line no-console
    console.warn('[pilot-stream] heartbeat_timeout', {
      activityId,
      selectedPhase: selectedPhaseRef.current,
      interactionStateId: persistedInteractionStateIdRef.current,
    });
    setInteractionUiStatus('recovering');
    void handleReconnectRef.current?.();
  }, [activityId]);
  const handlePilotStreamChunkFlush = useCallback(
    (content: string) => {
      dispatchStreamUi({ type: 'chat_append', content });
    },
    [dispatchStreamUi],
  );
  const {
    chatStreamAbortRef,
    pilotRecoveryStreamAbortRef,
    isChatStreamActive,
    isRecoveryStreamActive,
    pilotStreamChunkBufferRef,
    pilotStreamChunkFlushTimeoutRef,
    flushPilotStreamChunkBuffer,
    queuePilotStreamChunk,
    abortChatStream,
    abortRecoveryStream,
    runChatStream,
    runRecoveryStream,
  } = usePilotStreamTransport({
    flushDelayMs: PRD_STREAM_BATCH_FLUSH_MS,
    onFlush: handlePilotStreamChunkFlush,
  });
  const {
    recoveryRequestedRef: pilotRecoveryRequestedRef,
    recoveryPollRef: pilotRecoveryPollRef,
    setRecoveryRequested: setPilotRecoveryRequested,
    startHeartbeatWatchdog,
    stopHeartbeatWatchdog,
    onHeartbeat,
    stopRecoveryPolling,
  } = usePilotRecoveryState({
    heartbeatTimeoutMs: HEARTBEAT_TIMEOUT_MS,
    onHeartbeatTimeout: handlePilotHeartbeatTimeout,
  });
  const hasActivePilotStreamTransport = useCallback(
    () =>
      hasAttachedPilotStreamTransport({
        chatStreamAbortController: chatStreamAbortRef.current,
        recoveryStreamAbortController: pilotRecoveryStreamAbortRef.current,
      }),
    [chatStreamAbortRef, pilotRecoveryStreamAbortRef],
  );
  const {
    isPassive: isPilotTabPassive,
    claimStream: claimPilotTabStream,
    forceClaimStream: forcePilotTabStream,
    releaseStream: releasePilotTabStream,
    refreshOwnership: refreshPilotTabOwnership,
  } = usePilotTabOwnership({
    activityId,
    enabled: Boolean(activityId) && isPilotInteractionPhase(selectedPhase),
    onStreamStolen: () => {
      abortChatStream();
      abortRecoveryStream();
      flushPilotStreamChunkBuffer({ discard: true });
      setPilotRecoveryRequested(false);
    },
  });

  const draftStorageKey = useMemo(
    () =>
      activityId
        ? `${CHAT_DRAFT_STORAGE_PREFIX}:${activityId}:${selectedPhase}`
        : null,
    [activityId, selectedPhase],
  );
  const dismissedInteractionStorageKey = useMemo(
    () =>
      activityId
        ? `${DISMISSED_INTERACTION_STORAGE_PREFIX}:${activityId}`
        : null,
    [activityId],
  );
  const recoverySnapshotStorageKey = useMemo(
    () =>
      activityId && isPilotInteractionPhase(selectedPhase)
        ? getPilotRecoverySnapshotStorageKey(activityId, selectedPhase)
        : null,
    [activityId, selectedPhase],
  );

  const resetRecoveredReplayDedup = useCallback(() => {
    recoveredReplayCycleKeyRef.current = null;
    recoveredReplayFingerprintsRef.current.clear();
  }, []);

  const {
    lastSeqIdRef,
    clearCursor: clearPilotReplayCursor,
    onSeqId,
    readSnapshot: readPilotReplayCursor,
  } = usePilotReplayCursor({
    activityId,
    phase: isPilotInteractionPhase(selectedPhase) ? selectedPhase : null,
    interactionStateId: persistedInteractionState?.id,
    isStreamingInteraction: persistedInteractionState?.status === 'streaming',
    onInteractionStateChanged: () => {
      resetRecoveredReplayDedup();
      pilotStreamChunkBufferRef.current = '';
      setRecoverySnapshotContent('');
      setStableRecoverySnapshotContent('');
      if (activityId && isPilotInteractionPhase(selectedPhase)) {
        try {
          sessionStorage.removeItem(
            getPilotRecoverySnapshotStorageKey(activityId, selectedPhase),
          );
        } catch {
          // Ignore sessionStorage errors in restricted environments.
        }
      }
    },
  });

  const logPilotStreamEvent = useCallback(
    (
      event: string,
      details: Record<string, unknown> = {},
      level: 'info' | 'warn' | 'error' = 'info',
    ) => {
      const logPayload = {
        activityId,
        selectedPhase: selectedPhaseRef.current,
        interactionStateId:
          persistedInteractionStateIdRef.current ??
          persistedInteractionState?.id ??
          null,
        lastSeqId: lastSeqIdRef.current,
        replayCursor: readPilotReplayCursor(),
        isPassiveTab: isPilotTabPassive,
        ...details,
      };

      if (level === 'warn') {
        // eslint-disable-next-line no-console
        console.warn(`[pilot-stream] ${event}`, logPayload);
        return;
      }

      if (level === 'error') {
        // eslint-disable-next-line no-console
        console.error(`[pilot-stream] ${event}`, logPayload);
        return;
      }

      // eslint-disable-next-line no-console
      console.info(`[pilot-stream] ${event}`, logPayload);
    },
    [
      activityId,
      isPilotTabPassive,
      persistedInteractionState?.id,
      readPilotReplayCursor,
    ],
  );

  useEffect(() => {
    workerConnectedRef.current = workerConnected;
    workerStatusResolvedRef.current = workerStatusResolved;
  }, [workerConnected, workerStatusResolved]);

  const getWorkerStatusWakeSnapshot =
    useCallback((): WorkerStatusWakeSnapshot => {
      return {
        connected: workerConnectedRef.current,
        resolved: workerStatusResolvedRef.current,
      };
    }, []);

  const shouldIgnoreRecoveredReplayEvent = useCallback(
    (event: StreamEvent): boolean => {
      if (event.type !== 'text' && event.type !== 'tool_use') {
        return false;
      }

      const normalizedContent = normalizeRecoveredReplayFingerprintContent(
        event.content,
      );
      if (!normalizedContent) {
        return false;
      }

      const interactionId = persistedInteractionStateIdRef.current ?? 'none';
      const cycleKey = `${selectedPhaseRef.current}:${interactionId}`;
      if (recoveredReplayCycleKeyRef.current !== cycleKey) {
        recoveredReplayCycleKeyRef.current = cycleKey;
        recoveredReplayFingerprintsRef.current.clear();
      }

      const fingerprint = `${interactionId}:${event.type}:${normalizedContent}`;
      if (recoveredReplayFingerprintsRef.current.has(fingerprint)) {
        return true;
      }

      recoveredReplayFingerprintsRef.current.add(fingerprint);
      return false;
    },
    [],
  );

  // Cleanup: abort active chat stream and watchdog on unmount to prevent
  // background fetch keeping the backend connection alive after SPA navigation.
  useEffect(() => {
    return () => {
      logPilotStreamEvent('stream_abort', { reason: 'unmount' }, 'warn');
      abortChatStream();
      abortRecoveryStream();
      releasePilotTabStream();
      stopHeartbeatWatchdog();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousReplayCursorActivityId =
      lastReplayCursorActivityIdRef.current;
    lastReplayCursorActivityIdRef.current = activityId ?? null;

    lastSeenPhaseRef.current = null;
    setPendingFrontendAutoStartPhase(null);
    setInteractionUiStatus('idle');
    dispatchPostAnswerRecovery({ type: 'reset' });
    setPostAnswerRecoveryElapsedMs(0);
    setPersistedInteractionState(null);
    persistedInteractionStateIdRef.current = null;
    submittedPendingInteractionRef.current = null;
    postAnswerRecoveryStageRef.current = 'idle';
    resetRecoveredReplayDedup();
    setChatDraft('');
    setPendingInteractionModalOpen(false);
    setDismissedInteractionIds([]);
    setPhaseConformityRepairNotice(undefined);
    setRecoverySnapshotContent('');
    setStableRecoverySnapshotContent('');
    setIsPrdReadingEditing(false);
    setPrdReadingDraftContent('');
    setIsPrdReadingSaving(false);
    setIsTechSpecReadingEditing(false);
    setTechSpecReadingDraftContent('');
    setIsTechSpecReadingSaving(false);
    stopRecoveryPolling();
    abortRecoveryStream();
    pilotRecoveryStreamAttemptRef.current = null;
    pilotRecoveryEventReceivedRef.current = false;
    lastSeqIdRef.current = 0;
    pilotPhaseSyncKeyRef.current = null;
    setPilotRecoveryRequested(false);
    postAnswerRecoveryActiveRef.current = false;
    postAnswerReattachInFlightRef.current = false;
    postAnswerReattachAttemptsRef.current = 0;
    if (postAnswerReattachTimerRef.current) {
      clearInterval(postAnswerReattachTimerRef.current);
      postAnswerReattachTimerRef.current = null;
    }
    abortChatStream();
    releasePilotTabStream();
    if (
      previousReplayCursorActivityId &&
      previousReplayCursorActivityId !== activityId
    ) {
      clearPilotReplayCursor();
    }
    pilotStreamChunkBufferRef.current = '';
  }, [
    abortChatStream,
    abortRecoveryStream,
    activityId,
    clearPilotReplayCursor,
    releasePilotTabStream,
    resetRecoveredReplayDedup,
    setPilotRecoveryRequested,
    stopRecoveryPolling,
  ]);

  useEffect(() => {
    loadContextFiles();
  }, [loadContextFiles]);

  useEffect(() => {
    if (!recoverySnapshotStorageKey) {
      setRecoverySnapshotContent('');
      return;
    }

    try {
      setRecoverySnapshotContent(
        sessionStorage.getItem(recoverySnapshotStorageKey) ?? '',
      );
    } catch {
      setRecoverySnapshotContent('');
    }
  }, [recoverySnapshotStorageKey]);

  const clearPilotRecoverySnapshot = useCallback(
    (phase: Phase = selectedPhaseRef.current) => {
      if (!activityId || !isPilotInteractionPhase(phase)) {
        return;
      }

      try {
        sessionStorage.removeItem(
          getPilotRecoverySnapshotStorageKey(activityId, phase),
        );
      } catch {
        // Ignore sessionStorage errors in restricted environments.
      }

      if (phase === selectedPhaseRef.current) {
        setRecoverySnapshotContent('');
      }
    },
    [activityId],
  );

  const persistPilotRecoverySnapshot = useCallback(
    (phase: Phase, content: string) => {
      if (!activityId || !isPilotInteractionPhase(phase)) {
        return;
      }

      const normalized = normalizeRecoverySnapshotContent(content);
      try {
        if (normalized) {
          sessionStorage.setItem(
            getPilotRecoverySnapshotStorageKey(activityId, phase),
            normalized,
          );
        } else {
          sessionStorage.removeItem(
            getPilotRecoverySnapshotStorageKey(activityId, phase),
          );
        }
      } catch {
        // Ignore sessionStorage errors in restricted environments.
      }

      if (phase === selectedPhaseRef.current) {
        setRecoverySnapshotContent(normalized);
      }
    },
    [activityId],
  );

  useEffect(() => {
    if (!PILOT_INTERACTION_PHASES.includes(selectedPhase)) {
      setStableRecoverySnapshotContent('');
      return;
    }

    const normalizedSnapshot = normalizeRecoverySnapshotContent(
      recoverySnapshotContent,
    );
    const shouldFreezeRecoveryBaseline =
      interactionUiStatus === 'recovering' ||
      interactionUiStatus === 'streaming' ||
      persistedInteractionState?.status === 'streaming' ||
      persistedInteractionState?.status === 'awaiting_user' ||
      pilotRecoveryRequestedRef.current ||
      Boolean(pilotRecoveryStreamAbortRef.current);

    if (!normalizedSnapshot) {
      if (!shouldFreezeRecoveryBaseline) {
        setStableRecoverySnapshotContent('');
      }
      return;
    }

    setStableRecoverySnapshotContent(previous => {
      if (!previous) {
        return normalizedSnapshot;
      }

      if (
        normalizedSnapshot === previous ||
        normalizedSnapshot.includes(previous)
      ) {
        return normalizedSnapshot;
      }

      if (previous.includes(normalizedSnapshot)) {
        return previous;
      }

      const previousSanitized = sanitizeArtifactDraftContent(previous);
      const nextSanitized = sanitizeArtifactDraftContent(normalizedSnapshot);
      if (
        previousSanitized &&
        nextSanitized &&
        previousSanitized.includes(nextSanitized)
      ) {
        return previous;
      }
      if (
        previousSanitized &&
        nextSanitized &&
        nextSanitized.includes(previousSanitized)
      ) {
        return normalizedSnapshot;
      }

      return shouldFreezeRecoveryBaseline
        ? previous
        : mergeRecoveryStreamingContent(previous, normalizedSnapshot);
    });
  }, [
    interactionUiStatus,
    persistedInteractionState?.status,
    recoverySnapshotContent,
    selectedPhase,
  ]);

  useEffect(() => {
    return () => {
      stopRecoveryPolling();
      abortRecoveryStream();
      pilotRecoveryStreamAttemptRef.current = null;
      pilotRecoveryEventReceivedRef.current = false;
      abortChatStream();
      if (pilotStreamChunkFlushTimeoutRef.current) {
        clearTimeout(pilotStreamChunkFlushTimeoutRef.current);
        pilotStreamChunkFlushTimeoutRef.current = null;
      }
      pilotStreamChunkBufferRef.current = '';
      releasePilotTabStream();
    };
  }, [
    abortChatStream,
    abortRecoveryStream,
    releasePilotTabStream,
    stopRecoveryPolling,
  ]);

  useEffect(() => {
    let cancelled = false;
    const checkWorker = async () => {
      try {
        const status = await api.getWorkerStatus();
        if (!cancelled) {
          setWorkerConnected(status.connected);
          setWorkerStatusResolved(true);
        }
      } catch {
        if (!cancelled) {
          setWorkerConnected(false);
          setWorkerStatusResolved(true);
        }
      }
    };
    checkWorker();
    const interval = setInterval(checkWorker, 15_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api]);

  // Load activity details
  // Only show loading skeleton on initial load; subsequent refreshes update silently
  // to avoid flashing the skeleton and hiding UI elements (e.g. approve button).
  // Skips state updates when data hasn't changed to prevent unnecessary re-renders.
  const loadActivity = useCallback(async () => {
    if (!activityId) return undefined;
    if (!initialLoadDoneRef.current) {
      setLoading(true);
    }
    // Só o erro DESTE carregamento: `loadActivity` roda em background (refresh
    // periódico, recovery) e antes limpava o slot inteiro, apagando o erro do
    // agente que outra parte da tela acabara de escrever (erro-ui-por-origem).
    clearErrorFromOrigin('carregamento');
    try {
      const result = await api.getActivity(activityId);
      setLockedByOwner(false);
      setActivity(prev => {
        if (prev && areActivitySnapshotsEqual(prev, result)) {
          return prev;
        }
        return result;
      });
      setSelectedPhase(prev =>
        prev === result.currentPhase ? prev : result.currentPhase,
      );
      setPhaseSyncWarning(prev => (prev === undefined ? prev : undefined));
      return result;
    } catch (e) {
      const code = (e as { code?: string } | undefined)?.code;
      if (code === 'ACTIVITY_LOCKED_BY_OWNER') {
        setLockedByOwner(true);
      } else {
        setError(
          `Erro ao carregar atividade: ${
            e instanceof Error ? e.message : String(e)
          }`,
          'carregamento',
        );
      }
      return undefined;
    } finally {
      setLoading(prev => (prev ? false : prev));
      initialLoadDoneRef.current = true;
    }
  }, [activityId, api, clearErrorFromOrigin, setError]);

  const loadPipelineDefinition = useCallback(async () => {
    try {
      const definition = await api.getPipelineDefinition();
      setPipelineDefinition(definition);
    } catch {
      // Keep default definition on transient errors
    }
  }, [api]);

  const loadProjectLabels = useCallback(async () => {
    try {
      const projects = await api.listProjects();
      const nextLabels = Object.fromEntries(
        projects.map(project => [project.id, project.name]),
      );
      setProjectLabels(nextLabels);
    } catch {
      // Keep fallback labels when project lookup fails.
    }
  }, [api]);

  // Load messages for selected phase
  const loadMessages = useCallback(
    async (phase: Phase) => {
      if (!activityId) return;
      try {
        const result = await api.getMessages(activityId, phase);
        setMessages(prevMessages =>
          areChatMessagesEqual(prevMessages, result) ? prevMessages : result,
        );
      } catch {
        // Messages may not exist yet; silently ignore
        setMessages(prevMessages =>
          prevMessages.length === 0 ? prevMessages : [],
        );
      }
    },
    [activityId, api],
  );

  // loadMetrics and loadArtifacts are now provided by usePhaseDataLoaders

  const loadExecutionState = useCallback(async () => {
    if (!activityId) return undefined;
    try {
      const result = await api.getTasks(activityId);
      dispatchStreamUi({
        type: 'tasks_loaded',
        tasks: result.tasks,
        executionState: result.executionState,
      });
      return result;
    } catch {
      return undefined;
    }
  }, [activityId, api]);

  useEffect(() => {
    if (!draftStorageKey) {
      setChatDraft('');
      return;
    }

    try {
      setChatDraft(sessionStorage.getItem(draftStorageKey) ?? '');
    } catch {
      setChatDraft('');
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!dismissedInteractionStorageKey) {
      setDismissedInteractionIds([]);
      return;
    }

    try {
      const stored = sessionStorage.getItem(dismissedInteractionStorageKey);
      setDismissedInteractionIds(stored ? JSON.parse(stored) : []);
    } catch {
      setDismissedInteractionIds([]);
    }
  }, [dismissedInteractionStorageKey]);

  useEffect(() => {
    if (!dismissedInteractionStorageKey) {
      return;
    }

    try {
      sessionStorage.setItem(
        dismissedInteractionStorageKey,
        JSON.stringify(dismissedInteractionIds),
      );
    } catch {
      // Ignore sessionStorage errors in restricted environments.
    }
  }, [dismissedInteractionIds, dismissedInteractionStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) {
      return;
    }

    try {
      if (chatDraft.trim().length === 0) {
        sessionStorage.removeItem(draftStorageKey);
      } else {
        sessionStorage.setItem(draftStorageKey, chatDraft);
      }
    } catch {
      // Ignore sessionStorage errors in restricted environments.
    }
  }, [chatDraft, draftStorageKey]);

  const clearCurrentDraft = useCallback(() => {
    setChatDraft('');
    if (!draftStorageKey) {
      return;
    }

    try {
      sessionStorage.removeItem(draftStorageKey);
    } catch {
      // Ignore sessionStorage errors in restricted environments.
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!activityId || !isPilotInteractionPhase(selectedPhase)) {
      return;
    }

    const snapshotContent = mergeRecoveryStreamingContent(
      stableRecoverySnapshotContent || recoverySnapshotContent,
      streamUi.streamingContent,
    );

    if (snapshotContent) {
      persistPilotRecoverySnapshot(selectedPhase, snapshotContent);
      return;
    }

    if (
      interactionUiStatus === 'completed' ||
      interactionUiStatus === 'expired'
    ) {
      clearPilotRecoverySnapshot(selectedPhase);
    }
  }, [
    activityId,
    clearPilotRecoverySnapshot,
    interactionUiStatus,
    persistPilotRecoverySnapshot,
    recoverySnapshotContent,
    stableRecoverySnapshotContent,
    selectedPhase,
    streamUi.streamingContent,
  ]);

  const hasPilotRecoveryEvidence = useCallback(
    () =>
      Boolean(stableRecoverySnapshotContent) ||
      Boolean(recoverySnapshotContent) ||
      persistedInteractionState?.status === 'streaming' ||
      Boolean(pilotRecoveryStreamAbortRef.current) ||
      pilotRecoveryRequestedRef.current,
    [
      persistedInteractionState?.status,
      recoverySnapshotContent,
      stableRecoverySnapshotContent,
    ],
  );

  const hasPersistedAssistantOutputForPhase = useCallback(
    (phase: Phase) =>
      messages.some(
        message =>
          message.phase === phase &&
          message.role === 'assistant' &&
          !message.id.startsWith('temp-assistant-') &&
          message.content.trim().length > 0,
      ),
    [messages],
  );

  const resetAuthoritativeSyncFailureState = useCallback(() => {
    setAuthoritativeSyncFailureState(current => (current ? null : current));
  }, []);

  const shouldSuppressReactiveRecoveryTrigger = useCallback(
    (trigger: ReactiveRecoveryTrigger, phase: Phase): boolean => {
      const key = `${trigger}:${phase}` as const;
      const now = Date.now();
      const lastRunAt = recoveryTriggerCooldownRef.current[key];

      if (
        lastRunAt !== undefined &&
        now - lastRunAt < POST_ANSWER_REACTIVE_TRIGGER_COOLDOWN_MS
      ) {
        return true;
      }

      recoveryTriggerCooldownRef.current[key] = now;
      return false;
    },
    [],
  );

  const clearSubmittedPendingInteraction = useCallback((phase?: Phase) => {
    if (
      !submittedPendingInteractionRef.current ||
      (phase !== undefined &&
        submittedPendingInteractionRef.current.phase !== phase)
    ) {
      return;
    }

    submittedPendingInteractionRef.current = null;
  }, []);

  const getPendingInteractionSignature = useCallback(
    (input: {
      type: SubmittedPendingInteractionType;
      interaction?: InteractionState | null;
      legacyQuestions?: Question[] | null;
      legacyApproaches?: { question: string; approaches: Approach[] } | null;
    }): string | null => {
      if (input.type === 'question') {
        const questions =
          input.interaction?.type === 'question'
            ? input.interaction.questions
            : input.legacyQuestions;
        if (!questions?.length) {
          return null;
        }
        return questions.map(question => question.question).join('||');
      }

      const approachQuestion =
        input.interaction?.type === 'approach'
          ? input.interaction.approaches?.question
          : input.legacyApproaches?.question;

      return approachQuestion ?? null;
    },
    [],
  );

  const isSubmittedPendingInteractionStale = useCallback(
    (
      phase: Phase,
      input: {
        interaction?: InteractionState | null;
        legacyQuestions?: Question[] | null;
        legacyApproaches?: { question: string; approaches: Approach[] } | null;
      },
    ): boolean => {
      const submitted = submittedPendingInteractionRef.current;
      if (!submitted || submitted.phase !== phase) {
        return false;
      }

      let type: SubmittedPendingInteractionType | null = null;
      let interactionId: string | null = null;

      if (input.interaction?.status === 'awaiting_user') {
        if (
          input.interaction.type !== 'question' &&
          input.interaction.type !== 'approach'
        ) {
          return false;
        }
        type = input.interaction.type;
        interactionId = input.interaction.id;
      } else if (input.legacyQuestions?.length) {
        type = 'question';
      } else if (input.legacyApproaches) {
        type = 'approach';
      }

      if (!type || submitted.type !== type) {
        return false;
      }

      if (submitted.interactionId && interactionId) {
        return submitted.interactionId === interactionId;
      }

      const signature = getPendingInteractionSignature({
        type,
        interaction: input.interaction,
        legacyQuestions: input.legacyQuestions,
        legacyApproaches: input.legacyApproaches,
      });

      return Boolean(signature && submitted.signature === signature);
    },
    [getPendingInteractionSignature],
  );

  const rememberSubmittedPendingInteraction = useCallback(
    (type: SubmittedPendingInteractionType, phase: Phase) => {
      const interaction =
        persistedInteractionState?.status === 'awaiting_user' &&
        persistedInteractionState.type === type
          ? persistedInteractionState
          : null;

      const signature = getPendingInteractionSignature({
        type,
        interaction,
        legacyQuestions:
          type === 'question' ? streamUi.pendingQuestions ?? null : null,
        legacyApproaches:
          type === 'approach' ? streamUi.pendingApproaches ?? null : null,
      });

      submittedPendingInteractionRef.current = {
        phase,
        type,
        interactionId: interaction?.id ?? null,
        signature,
      };
    },
    [
      getPendingInteractionSignature,
      persistedInteractionState,
      streamUi.pendingApproaches,
      streamUi.pendingQuestions,
    ],
  );

  const syncPilotInteractionState = useCallback(
    async (
      phase: Phase,
      options: {
        markRecovering?: boolean;
        recovered?: boolean;
        throwOnError?: boolean;
      } = {},
    ): Promise<InteractionState | null> => {
      if (!activityId || !isPilotInteractionPhase(phase)) {
        setPersistedInteractionState(null);
        setInteractionUiStatus(streamUi.isStreaming ? 'streaming' : 'idle');
        return null;
      }

      const shouldShowRecoveryDuringSync =
        Boolean(options.markRecovering) && hasPilotRecoveryEvidence();

      if (shouldShowRecoveryDuringSync) {
        setInteractionUiStatus('recovering');
      }

      const clearPendingInteractionUi = () => {
        if (pendingQuestionsRef.current?.length) {
          dispatchStreamUi({ type: 'chat_clear_questions' });
        }
        if (pendingApproachesRef.current) {
          dispatchStreamUi({ type: 'chat_clear_approaches' });
        }
      };

      const applyAwaitingInteraction = (
        state: InteractionState | null,
        fallbackType?: 'question' | 'approach',
      ) => {
        if (state?.type === 'question' && state.questions?.length) {
          dispatchStreamUi({
            type: 'chat_set_questions',
            questions: state.questions,
            content: '',
          });
          if (pendingApproachesRef.current) {
            dispatchStreamUi({ type: 'chat_clear_approaches' });
          }
        }

        if (state?.type === 'approach' && state.approaches) {
          dispatchStreamUi({
            type: 'chat_set_approaches',
            approaches: state.approaches,
          });
          if (pendingQuestionsRef.current?.length) {
            dispatchStreamUi({ type: 'chat_clear_questions' });
          }
        }

        if (fallbackType === 'question' && pendingApproachesRef.current) {
          dispatchStreamUi({ type: 'chat_clear_approaches' });
        }
        if (
          fallbackType === 'approach' &&
          pendingQuestionsRef.current?.length
        ) {
          dispatchStreamUi({ type: 'chat_clear_questions' });
        }

        setInteractionUiStatus(
          options.recovered ? 'resumable' : 'awaiting_user',
        );
      };

      try {
        const interaction = await api.getInteractionState(activityId, phase);
        if (interaction) {
          resetAuthoritativeSyncFailureState();
          if (interaction.recoverySnapshotContent) {
            const mergedRecoverySnapshot = mergeRecoveryStreamingContent(
              stableRecoverySnapshotContent || recoverySnapshotContent,
              interaction.recoverySnapshotContent,
            );
            if (mergedRecoverySnapshot) {
              persistPilotRecoverySnapshot(phase, mergedRecoverySnapshot);
            }
          }
          const isStaleSubmittedAwaitingInteraction =
            interaction.status === 'awaiting_user' &&
            isSubmittedPendingInteractionStale(phase, { interaction });

          if (isStaleSubmittedAwaitingInteraction) {
            setPersistedInteractionState(current =>
              current?.id === interaction.id ? null : current,
            );
          } else {
            setPersistedInteractionState(interaction);
          }

          if (interaction.status === 'streaming') {
            clearSubmittedPendingInteraction(phase);
            const isRecoveredStreamStillReattaching =
              !pilotRecoveryEventReceivedRef.current &&
              Boolean(options.markRecovering);
            const shouldScheduleInitialHydrationRepoll =
              Boolean(options.recovered) &&
              interaction.type === 'session' &&
              !hasPersistedAssistantOutputForPhase(phase) &&
              !pendingQuestionsRef.current?.length &&
              !pendingApproachesRef.current &&
              !pilotRecoveryEventReceivedRef.current;

            if (shouldScheduleInitialHydrationRepoll) {
              const repollKey = `${activityId}:${phase}:${interaction.id}`;
              if (initialHydrationRepollKeyRef.current !== repollKey) {
                initialHydrationRepollKeyRef.current = repollKey;
                window.setTimeout(() => {
                  if (
                    initialHydrationRepollKeyRef.current === repollKey &&
                    selectedPhaseRef.current === phase
                  ) {
                    void requestPilotRecoverySyncRef.current?.({
                      trigger: 'focus',
                      phase,
                      skipChatReconnect: true,
                    });
                  }
                }, INITIAL_HYDRATION_REPOLL_DELAY_MS);
              }
            } else {
              initialHydrationRepollKeyRef.current = null;
            }
            pilotRecoveryRequestedRef.current =
              isRecoveredStreamStillReattaching;
            clearPendingInteractionUi();
            setInteractionUiStatus(
              isRecoveredStreamStillReattaching ? 'recovering' : 'streaming',
            );
            return interaction;
          }

          if (interaction.status === 'awaiting_user') {
            initialHydrationRepollKeyRef.current = null;
            pilotRecoveryRequestedRef.current = false;
            if (isStaleSubmittedAwaitingInteraction) {
              clearPendingInteractionUi();
              setInteractionUiStatus('recovering');
              return interaction;
            }
            clearSubmittedPendingInteraction(phase);
            applyAwaitingInteraction(interaction);
            return interaction;
          }

          initialHydrationRepollKeyRef.current = null;
          pilotRecoveryRequestedRef.current = false;
          clearSubmittedPendingInteraction(phase);
          clearPendingInteractionUi();
          if (interaction.status === 'expired') {
            setInteractionUiStatus('expired');
          } else if (interaction.status === 'completed') {
            setInteractionUiStatus('completed');
          } else if (interaction.status === 'canceled') {
            setInteractionUiStatus('idle');
          } else {
            setInteractionUiStatus('idle');
          }
          return interaction;
        }

        const [legacyQuestions, legacyApproaches] = await Promise.all([
          api.getPendingQuestion(activityId, phase),
          api.getPendingApproach(activityId, phase),
        ]);

        resetAuthoritativeSyncFailureState();
        setPersistedInteractionState(null);
        initialHydrationRepollKeyRef.current = null;

        if (legacyQuestions?.length) {
          if (
            isSubmittedPendingInteractionStale(phase, {
              legacyQuestions,
            })
          ) {
            clearPendingInteractionUi();
            setInteractionUiStatus('recovering');
            return null;
          }
          pilotRecoveryRequestedRef.current = false;
          dispatchStreamUi({
            type: 'chat_set_questions',
            questions: legacyQuestions,
            content: '',
          });
          if (pendingApproachesRef.current) {
            dispatchStreamUi({ type: 'chat_clear_approaches' });
          }
          clearSubmittedPendingInteraction(phase);
          applyAwaitingInteraction(null, 'question');
          return null;
        }

        if (legacyApproaches) {
          if (
            isSubmittedPendingInteractionStale(phase, {
              legacyApproaches,
            })
          ) {
            clearPendingInteractionUi();
            setInteractionUiStatus('recovering');
            return null;
          }
          pilotRecoveryRequestedRef.current = false;
          dispatchStreamUi({
            type: 'chat_set_approaches',
            approaches: legacyApproaches,
          });
          if (pendingQuestionsRef.current?.length) {
            dispatchStreamUi({ type: 'chat_clear_questions' });
          }
          clearSubmittedPendingInteraction(phase);
          applyAwaitingInteraction(null, 'approach');
          return null;
        }

        pilotRecoveryRequestedRef.current = false;
        clearSubmittedPendingInteraction(phase);
        clearPendingInteractionUi();
        setInteractionUiStatus(
          streamUi.phaseCompleted
            ? 'completed'
            : streamUi.isStreaming
            ? 'streaming'
            : 'idle',
        );
        return null;
      } catch (syncError) {
        if (options.throwOnError) {
          throw syncError;
        }
        return null;
      }
    },
    [
      activityId,
      api,
      hasPilotRecoveryEvidence,
      hasPersistedAssistantOutputForPhase,
      persistPilotRecoverySnapshot,
      recoverySnapshotContent,
      resetAuthoritativeSyncFailureState,
      clearSubmittedPendingInteraction,
      isSubmittedPendingInteractionStale,
      stableRecoverySnapshotContent,
      streamUi.isStreaming,
      streamUi.phaseCompleted,
    ],
  );

  const syncPilotInteractionStateRef = useRef(syncPilotInteractionState);

  useEffect(() => {
    syncPilotInteractionStateRef.current = syncPilotInteractionState;
  }, [syncPilotInteractionState]);

  const refreshApprovalVisibility = useCallback(
    (reason: string, phase: Phase = selectedPhaseRef.current) => {
      logPilotStreamEvent('approval_state_refresh', {
        refreshReason: reason,
        phase,
      });
      refreshProjectRole();
    },
    [logPilotStreamEvent, refreshProjectRole],
  );

  const queuePilotRecoveryResume = useCallback((interactionStateId: string) => {
    pilotRecoveryResumeTokenRef.current += 1;
    const nextRequest = {
      interactionStateId,
      token: pilotRecoveryResumeTokenRef.current,
    };
    pilotRecoveryResumeRequestRef.current = nextRequest;
    setPilotRecoveryResumeRequest(nextRequest);
  }, []);

  const clearPilotRecoveryResume = useCallback(() => {
    pilotRecoveryResumeRequestRef.current = null;
    setPilotRecoveryResumeRequest(current => (current ? null : current));
  }, []);

  const settlePostAnswerRecoveryAfterRebuild = useCallback(
    (interaction: InteractionState | null) => {
      if (!postAnswerRecoveryActiveRef.current) {
        return;
      }

      if (interaction?.status === 'streaming') {
        clearSubmittedPendingInteraction(selectedPhaseRef.current);
        return;
      }

      if (
        interaction?.status === 'awaiting_user' &&
        isSubmittedPendingInteractionStale(selectedPhaseRef.current, {
          interaction,
        })
      ) {
        fallbackPostAnswerToPollingRef.current?.(
          postAnswerInitialMessageCountRef.current,
        );
        return;
      }

      if (postAnswerReattachTimerRef.current) {
        clearInterval(postAnswerReattachTimerRef.current);
        postAnswerReattachTimerRef.current = null;
      }

      postAnswerReattachInFlightRef.current = false;
      postAnswerRecoveryActiveRef.current = false;

      const settleAction = getPostAnswerRecoverySettleAction(
        interaction?.status ?? null,
      );
      const nowMs = Date.now();

      switch (settleAction) {
        case 'awaiting_new_interaction':
          clearSubmittedPendingInteraction(selectedPhaseRef.current);
          dispatchPostAnswerRecovery({
            type: 'awaiting_new_interaction',
            nowMs,
          });
          break;
        case 'failed_timeout':
          clearSubmittedPendingInteraction(selectedPhaseRef.current);
          dispatchPostAnswerRecovery({ type: 'failed_timeout', nowMs });
          break;
        case 'completed':
        default:
          clearSubmittedPendingInteraction(selectedPhaseRef.current);
          dispatchPostAnswerRecovery({ type: 'completed', nowMs });
          break;
      }
    },
    [clearSubmittedPendingInteraction, isSubmittedPendingInteractionStale],
  );

  const performPilotRecoveryRebuild = useCallback(
    async (
      phaseToSync: Phase,
      reason: RecoveryRebuildReason,
      options: {
        interaction?: InteractionState | null;
        settlePostAnswer?: boolean;
      } = {},
    ): Promise<RecoverySyncOutcome> => {
      logPilotStreamEvent(
        'stream_rebuild_fallback',
        { reason, phase: phaseToSync },
        'warn',
      );

      clearPilotRecoveryResume();
      abortRecoveryStream();
      pilotRecoveryStreamAttemptRef.current = null;
      pilotRecoveryEventReceivedRef.current = false;
      flushPilotStreamChunkBuffer({ discard: true });
      dispatchStreamUi({ type: 'chat_stop' });

      if (reason === 'cursor_mismatch') {
        clearPilotReplayCursor();
        resetRecoveredReplayDedup();
      }

      await Promise.all([
        loadMessages(phaseToSync),
        loadMetrics(),
        loadArtifacts(),
        loadExecutionState(),
      ]);

      const interaction =
        options.interaction ??
        (await syncPilotInteractionStateRef.current(phaseToSync, {
          recovered: true,
        }));

      if (options.settlePostAnswer) {
        settlePostAnswerRecoveryAfterRebuild(interaction);
      }

      if (interaction?.status === 'streaming') {
        setPersistedInteractionState(null);
        persistedInteractionStateIdRef.current = null;
        setInteractionUiStatus('idle');
      }

      setPilotRecoveryRequested(false);
      if (!chatStreamAbortRef.current) {
        releasePilotTabStream();
      }

      return 'rebuild';
    },
    [
      abortRecoveryStream,
      chatStreamAbortRef,
      clearPilotRecoveryResume,
      clearPilotReplayCursor,
      dispatchStreamUi,
      flushPilotStreamChunkBuffer,
      loadArtifacts,
      loadExecutionState,
      loadMessages,
      loadMetrics,
      logPilotStreamEvent,
      releasePilotTabStream,
      resetRecoveredReplayDedup,
      setPilotRecoveryRequested,
      settlePostAnswerRecoveryAfterRebuild,
    ],
  );

  const executePilotRecoverySync = useCallback(
    async (request: RecoverySyncRequest): Promise<RecoverySyncOutcome> => {
      if (!activityId) {
        clearPilotRecoveryResume();
        return 'noop';
      }

      const requestedPhase = request.phase ?? selectedPhaseRef.current;
      const workerStatusAtWake =
        request.workerStatusAtWake ?? getWorkerStatusWakeSnapshot();
      let retryAttempts = 0;

      if (!request.skipChatReconnect) {
        abortChatStream();
        flushPilotStreamChunkBuffer({ discard: true });
        dispatchStreamUi({ type: 'chat_reconnect' });
      }

      if (request.trigger !== 'stream_terminal') {
        setPilotRecoveryRequested(true);
        setInteractionUiStatus('recovering');
      }

      const updatedActivity = await loadActivity();
      const phaseToSync = updatedActivity?.currentPhase ?? requestedPhase;
      if (phaseToSync !== selectedPhaseRef.current) {
        setSelectedPhase(phaseToSync);
      }

      refreshApprovalVisibility(`recovery:${request.trigger}`, phaseToSync);

      if (!isPilotInteractionPhase(phaseToSync)) {
        clearPilotRecoveryResume();
        await Promise.all([
          loadMessages(phaseToSync),
          loadMetrics(),
          loadArtifacts(),
          loadExecutionState(),
        ]);
        setPilotRecoveryRequested(false);
        setInteractionUiStatus(streamUi.isStreaming ? 'streaming' : 'idle');
        return 'noop';
      }

      await Promise.all([loadMetrics(), loadArtifacts(), loadExecutionState()]);

      let interaction: InteractionState | null = null;
      try {
        interaction = await syncPilotInteractionStateRef.current(phaseToSync, {
          markRecovering: request.trigger !== 'stream_terminal',
          recovered: true,
          throwOnError: true,
        });
      } catch (syncError) {
        clearPilotRecoveryResume();
        logPilotStreamEvent(
          'stream_sync_failed',
          {
            trigger: request.trigger,
            error:
              syncError instanceof Error
                ? syncError.message
                : String(syncError),
            retryAttempts,
            workerStatusAtWake,
          },
          'warn',
        );
        if (
          (request.trigger === 'answer_submitted' ||
            request.trigger === 'reattach_failed') &&
          postAnswerRecoveryActiveRef.current
        ) {
          setAuthoritativeSyncFailureState({
            kind: 'transient',
            attempt: 1,
          });
          fallbackPostAnswerToPollingRef.current?.();
        }
        return 'noop';
      }

      const shouldSettlePostAnswer =
        request.trigger === 'answer_submitted' ||
        request.trigger === 'reattach_failed';
      const isPostAnswerReattachProbe =
        request.trigger === 'answer_submitted' &&
        postAnswerRecoveryActiveRef.current;
      const isStaleSubmittedAwaitingInteraction =
        interaction?.status === 'awaiting_user' &&
        isSubmittedPendingInteractionStale(phaseToSync, {
          interaction,
        });
      const shouldFallbackPostAnswerToPolling =
        request.trigger === 'reattach_failed' &&
        request.conflict?.reason === 'interaction_mismatch' &&
        postAnswerRecoveryActiveRef.current;

      if (isPostAnswerReattachProbe && isStaleSubmittedAwaitingInteraction) {
        setPhaseSyncWarning(undefined);
        setPilotRecoveryRequested(true);
        setInteractionUiStatus('recovering');
        fallbackPostAnswerToPollingRef.current?.();
        return 'noop';
      }

      if (
        request.conflict?.reason === 'no_inflight_stream' &&
        interaction?.id
      ) {
        pilotRecoveryResumeSuppressedInteractionIdRef.current = interaction.id;
      }

      if (
        interaction?.id &&
        pilotRecoveryResumeSuppressedInteractionIdRef.current === interaction.id
      ) {
        const recoveryFallbackContent = mergeRecoveryStreamingContent(
          stableRecoverySnapshotContent || recoverySnapshotContent,
          streamUi.streamingContent,
        );

        clearPilotRecoveryResume();
        abortRecoveryStream();
        pilotRecoveryStreamAttemptRef.current = null;
        pilotRecoveryEventReceivedRef.current = false;
        flushPilotStreamChunkBuffer({ discard: true });
        dispatchStreamUi({ type: 'chat_stop' });
        if (recoveryFallbackContent) {
          dispatchStreamUi({
            type: 'chat_append',
            content: recoveryFallbackContent,
          });
        }
        setPersistedInteractionState(null);
        persistedInteractionStateIdRef.current = null;
        setPhaseSyncWarning(undefined);
        setPilotRecoveryRequested(false);
        setInteractionUiStatus('idle');
        if (shouldSettlePostAnswer) {
          settlePostAnswerRecoveryAfterRebuild(null);
        }
        if (!chatStreamAbortRef.current) {
          releasePilotTabStream();
        }
        return 'noop';
      }

      if (shouldFallbackPostAnswerToPolling) {
        if (
          interaction?.status === 'awaiting_user' ||
          interaction?.status === 'completed' ||
          interaction?.status === 'expired' ||
          interaction?.status === 'canceled'
        ) {
          settlePostAnswerRecoveryAfterRebuild(interaction);
          return 'noop';
        }

        clearPilotRecoveryResume();
        abortRecoveryStream();
        pilotRecoveryStreamAttemptRef.current = null;
        pilotRecoveryEventReceivedRef.current = false;
        flushPilotStreamChunkBuffer({ discard: true });
        dispatchStreamUi({ type: 'chat_stop' });
        setPhaseSyncWarning(undefined);
        setPilotRecoveryRequested(true);
        setInteractionUiStatus('recovering');
        if (!chatStreamAbortRef.current) {
          releasePilotTabStream();
        }
        fallbackPostAnswerToPollingRef.current?.();
        return 'noop';
      }

      if (request.conflict) {
        return performPilotRecoveryRebuild(phaseToSync, 'stream_conflict', {
          interaction,
          settlePostAnswer: shouldSettlePostAnswer,
        });
      }

      const directlyResumableInteraction =
        interaction &&
        (interaction.status === 'streaming' ||
          interaction.status === 'awaiting_user') &&
        interaction.canResume &&
        interaction.resumeMode === 'direct' &&
        !interaction.terminalReason
          ? interaction
          : null;

      if (!directlyResumableInteraction) {
        if (
          isPostAnswerReattachProbe &&
          (!interaction || interaction.status === 'streaming')
        ) {
          setPhaseSyncWarning(undefined);
          setPilotRecoveryRequested(true);
          setInteractionUiStatus('recovering');
          return 'noop';
        }

        return performPilotRecoveryRebuild(phaseToSync, 'terminal_state', {
          interaction,
          settlePostAnswer: shouldSettlePostAnswer,
        });
      }

      const replayCursor = readPilotReplayCursor();
      if (
        replayCursor &&
        replayCursor.interactionStateId !== directlyResumableInteraction.id
      ) {
        return performPilotRecoveryRebuild(phaseToSync, 'cursor_mismatch', {
          interaction,
          settlePostAnswer: shouldSettlePostAnswer,
        });
      }

      if (!workerConnectedRef.current) {
        if (!workerStatusResolvedRef.current) {
          for (const waitMs of PILOT_WAKE_WORKER_STATUS_RETRY_DELAYS_MS) {
            retryAttempts += 1;
            await delay(waitMs);
            if (workerConnectedRef.current || workerStatusResolvedRef.current) {
              break;
            }
          }
        }

        if (!workerConnectedRef.current) {
          if (retryAttempts > 0) {
            logPilotStreamEvent(
              'stream_sync_failed',
              {
                trigger: request.trigger,
                error:
                  'Worker status did not resolve within the wake retry budget.',
                retryAttempts,
                workerStatusAtWake,
              },
              'warn',
            );
          }

          if (isPostAnswerReattachProbe) {
            setPhaseSyncWarning(undefined);
            setPilotRecoveryRequested(true);
            setInteractionUiStatus('recovering');
            fallbackPostAnswerToPollingRef.current?.();
            return 'noop';
          }

          return performPilotRecoveryRebuild(phaseToSync, 'stream_conflict', {
            interaction,
            settlePostAnswer: shouldSettlePostAnswer,
          });
        }
      }

      const currentResumeRequest = pilotRecoveryResumeRequestRef.current;
      if (
        currentResumeRequest?.interactionStateId ===
          directlyResumableInteraction.id ||
        pilotRecoveryStreamAbortRef.current
      ) {
        setPhaseSyncWarning(undefined);
        setPilotRecoveryRequested(true);
        setInteractionUiStatus('recovering');
        return 'resume';
      }

      queuePilotRecoveryResume(directlyResumableInteraction.id);
      setPhaseSyncWarning(undefined);
      setPilotRecoveryRequested(true);
      setInteractionUiStatus('recovering');
      return 'resume';
    },
    [
      abortChatStream,
      abortRecoveryStream,
      activityId,
      clearPilotRecoveryResume,
      dispatchStreamUi,
      flushPilotStreamChunkBuffer,
      loadActivity,
      loadArtifacts,
      loadExecutionState,
      loadMessages,
      loadMetrics,
      logPilotStreamEvent,
      isSubmittedPendingInteractionStale,
      performPilotRecoveryRebuild,
      queuePilotRecoveryResume,
      readPilotReplayCursor,
      pilotRecoveryStreamAbortRef,
      recoverySnapshotContent,
      refreshApprovalVisibility,
      releasePilotTabStream,
      setPilotRecoveryRequested,
      stableRecoverySnapshotContent,
      settlePostAnswerRecoveryAfterRebuild,
      streamUi.isStreaming,
      streamUi.streamingContent,
      getWorkerStatusWakeSnapshot,
    ],
  );

  const requestPilotRecoverySync = useCallback(
    (
      trigger: RecoveryTrigger,
      options: Omit<RecoverySyncRequest, 'trigger'> = {},
    ): Promise<RecoverySyncOutcome> => {
      const request: RecoverySyncRequest = { trigger, ...options };

      if (pilotRecoverySyncInFlightRef.current) {
        queuedPilotRecoveryRequestRef.current = mergeRecoverySyncRequest(
          queuedPilotRecoveryRequestRef.current,
          request,
        );
        return pilotRecoverySyncInFlightRef.current;
      }

      const promise = executePilotRecoverySync(request).finally(() => {
        pilotRecoverySyncInFlightRef.current = null;
        const queuedRequest = queuedPilotRecoveryRequestRef.current;
        queuedPilotRecoveryRequestRef.current = null;
        if (queuedRequest) {
          void requestPilotRecoverySyncRef.current?.(queuedRequest);
        }
      });

      pilotRecoverySyncInFlightRef.current = promise;
      return promise;
    },
    [executePilotRecoverySync],
  );

  useEffect(() => {
    requestPilotRecoverySyncRef.current = async request => {
      const { trigger, ...options } = request;
      return requestPilotRecoverySync(trigger, options);
    };
  }, [requestPilotRecoverySync]);

  useEffect(() => {
    lastActivityRef.current = activity;
  }, [activity]);

  useEffect(() => {
    setPhaseConformityRepairNotice(previous => {
      if (!previous) return previous;
      return isPhaseConformityRepairNoticeCurrent(
        previous,
        activityId,
        activity?.currentPhase,
        selectedPhase,
      )
        ? previous
        : undefined;
    });
  }, [activity?.currentPhase, activityId, selectedPhase]);

  useEffect(() => {
    selectedPhaseRef.current = selectedPhase;
    resetRecoveredReplayDedup();
    if (!isPilotInteractionPhase(selectedPhase)) {
      pilotRecoveryResumeSuppressedInteractionIdRef.current = null;
    }
  }, [resetRecoveredReplayDedup, selectedPhase]);

  useEffect(() => {
    const previousInteractionStateId = persistedInteractionStateIdRef.current;
    persistedInteractionStateIdRef.current =
      persistedInteractionState?.id ?? null;
    resetRecoveredReplayDedup();
    if (
      persistedInteractionState?.id &&
      pilotRecoveryResumeSuppressedInteractionIdRef.current &&
      pilotRecoveryResumeSuppressedInteractionIdRef.current !==
        persistedInteractionState.id
    ) {
      pilotRecoveryResumeSuppressedInteractionIdRef.current = null;
    }
    if (
      previousInteractionStateId &&
      persistedInteractionState?.id &&
      previousInteractionStateId !== persistedInteractionState.id
    ) {
      pilotStreamChunkBufferRef.current = '';
      logPilotStreamEvent('stream_identity_changed', {
        previousInteractionStateId,
        nextInteractionStateId: persistedInteractionState.id,
      });
    }
  }, [
    logPilotStreamEvent,
    persistedInteractionState?.id,
    pilotStreamChunkBufferRef,
    resetRecoveredReplayDedup,
  ]);

  useEffect(() => {
    pendingQuestionsRef.current = streamUi.pendingQuestions;
    pendingApproachesRef.current = streamUi.pendingApproaches;
  }, [streamUi.pendingApproaches, streamUi.pendingQuestions]);

  const canReclaimPilotOwnership = useCallback(() => {
    if (!activity || !activityId) {
      return false;
    }

    const phaseToCheck = selectedPhaseRef.current;
    if (
      phaseToCheck !== activity.currentPhase ||
      !isPilotInteractionPhase(phaseToCheck)
    ) {
      return false;
    }

    const postAnswerSettled =
      postAnswerRecoveryStageRef.current === 'idle' ||
      postAnswerRecoveryStageRef.current === 'awaiting_new_interaction' ||
      postAnswerRecoveryStageRef.current === 'completed' ||
      postAnswerRecoveryStageRef.current === 'failed_timeout';
    if (!postAnswerSettled) {
      return false;
    }

    const hasAuthoritativeSettledOutput =
      hasPersistedAssistantOutputForPhase(phaseToCheck) &&
      !streamUi.isStreaming &&
      !pendingQuestionsRef.current?.length &&
      !pendingApproachesRef.current &&
      !pilotRecoveryStreamAbortRef.current;
    const hasAwaitingInteraction =
      interactionUiStatus === 'awaiting_user' ||
      interactionUiStatus === 'resumable' ||
      persistedInteractionState?.status === 'awaiting_user' ||
      Boolean(pendingQuestionsRef.current?.length) ||
      Boolean(pendingApproachesRef.current);
    const hasTerminalInteraction =
      persistedInteractionState?.status === 'completed' ||
      persistedInteractionState?.status === 'expired' ||
      persistedInteractionState?.status === 'canceled';
    const hasAuthoritativeStreaming =
      persistedInteractionState?.status === 'streaming' ||
      streamUi.isStreaming ||
      (interactionUiStatus === 'streaming' &&
        (Boolean(pilotRecoveryStreamAbortRef.current) ||
          pilotRecoveryRequestedRef.current));
    const hasPendingRecoveryWork =
      interactionUiStatus === 'recovering' &&
      !hasAwaitingInteraction &&
      !hasAuthoritativeSettledOutput &&
      !hasTerminalInteraction;

    return (
      !hasAuthoritativeStreaming &&
      !hasPendingRecoveryWork &&
      (hasAwaitingInteraction ||
        hasAuthoritativeSettledOutput ||
        hasTerminalInteraction)
    );
  }, [
    activity,
    activityId,
    hasPersistedAssistantOutputForPhase,
    interactionUiStatus,
    persistedInteractionState?.status,
    streamUi.isStreaming,
  ]);

  const ensurePilotStreamOwnership = useCallback(
    (streamKind: 'live' | 'recovery'): boolean => {
      if (
        !activity ||
        selectedPhaseRef.current !== activity.currentPhase ||
        !isPilotInteractionPhase(selectedPhaseRef.current)
      ) {
        return true;
      }

      if (claimPilotTabStream()) {
        setPhaseSyncWarning(undefined);
        return true;
      }

      if (!canReclaimPilotOwnership()) {
        setPhaseSyncWarning(PASSIVE_STREAM_WARNING);
        logPilotStreamEvent('stream_open_blocked', {
          streamKind,
          reason: 'ownership_claim_failed',
        });
        return false;
      }

      refreshPilotTabOwnership();
      forcePilotTabStream();
      setPhaseSyncWarning(undefined);
      logPilotStreamEvent('stream_ownership_reclaimed', {
        streamKind,
        reason: 'authoritative_state_ready',
      });
      return true;
    },
    [
      activity,
      canReclaimPilotOwnership,
      claimPilotTabStream,
      forcePilotTabStream,
      logPilotStreamEvent,
      refreshPilotTabOwnership,
    ],
  );

  const currentPendingInteractionId = useMemo(() => {
    if (
      persistedInteractionState?.status === 'awaiting_user' &&
      selectedPhase === activity?.currentPhase &&
      isPilotInteractionPhase(selectedPhase)
    ) {
      return persistedInteractionState.id;
    }

    if (streamUi.pendingQuestions?.length) {
      return `legacy:${selectedPhase}:question:${streamUi.pendingQuestions[0].question}`;
    }

    if (streamUi.pendingApproaches?.question) {
      return `legacy:${selectedPhase}:approach:${streamUi.pendingApproaches.question}`;
    }

    return null;
  }, [
    activity?.currentPhase,
    persistedInteractionState,
    selectedPhase,
    streamUi.pendingApproaches,
    streamUi.pendingQuestions,
  ]);

  const pendingInteractionType = useMemo(() => {
    if (streamUi.pendingQuestions?.length) {
      return 'question' as const;
    }

    if (streamUi.pendingApproaches) {
      return 'approach' as const;
    }

    if (persistedInteractionState?.status === 'awaiting_user') {
      return persistedInteractionState.type;
    }

    return null;
  }, [
    persistedInteractionState,
    streamUi.pendingApproaches,
    streamUi.pendingQuestions,
  ]);

  const markInteractionDismissed = useCallback(
    (interactionId: string | null) => {
      if (!interactionId) {
        return;
      }

      setDismissedInteractionIds(prev =>
        prev.includes(interactionId) ? prev : [...prev, interactionId],
      );
    },
    [],
  );

  const reopenPendingInteraction = useCallback(() => {
    setPendingInteractionModalOpen(true);
  }, []);

  const dismissPendingInteractionModal = useCallback(() => {
    markInteractionDismissed(currentPendingInteractionId);
    setPendingInteractionModalOpen(false);
  }, [currentPendingInteractionId, markInteractionDismissed]);

  useEffect(() => {
    if (!currentPendingInteractionId || !pendingInteractionType) {
      setPendingInteractionModalOpen(false);
      return;
    }

    if (!dismissedInteractionIds.includes(currentPendingInteractionId)) {
      setPendingInteractionModalOpen(true);
    }
  }, [
    currentPendingInteractionId,
    dismissedInteractionIds,
    pendingInteractionType,
  ]);

  // Reset phase-specific completion flags when navigating between phases.
  // Without this, phaseCompleted/everStartedStreaming from a previous phase
  // (e.g. QA's chat_complete) would leak into the next phase (e.g. Pronto),
  // causing it to start in reading mode instead of chat mode.
  const prevPhaseRef = useRef(selectedPhase);
  useEffect(() => {
    if (prevPhaseRef.current !== selectedPhase) {
      prevPhaseRef.current = selectedPhase;
      // ADR-013: if the phase change came from a live phase_auto_transition,
      // the handler already kept the stream alive for the new phase. Skip
      // this reset once so we do not re-zero isStreaming mid-SSE.
      if (liveTransitionInProgressRef.current) {
        return;
      }
      dispatchStreamUi({ type: 'phase_changed' });
    }
  }, [selectedPhase]);

  useEffect(() => {
    if (!activityId || !activity) {
      return;
    }

    if (
      selectedPhase === activity.currentPhase &&
      isPilotInteractionPhase(selectedPhase)
    ) {
      // ADR-013: during a live phase_auto_transition the same SSE session
      // continues feeding the new phase. Skip the focus sync until the first
      // real event of the new phase arrives so we do not rebase the live
      // interaction state into 'recovering' by accident.
      if (liveTransitionInProgressRef.current) {
        return;
      }
      const pilotSyncKey = `${activityId}:${selectedPhase}:${activity.currentPhase}`;
      if (pilotPhaseSyncKeyRef.current === pilotSyncKey) {
        return;
      }

      pilotPhaseSyncKeyRef.current = pilotSyncKey;
      if (shouldSuppressReactiveRecoveryTrigger('remount', selectedPhase)) {
        return;
      }
      void requestPilotRecoverySync('focus', {
        phase: selectedPhase,
        skipChatReconnect: true,
      });
      return;
    }

    // ADR-013: during a live phase_auto_transition window we must not rebase
    // interaction status to 'idle'; the handler already set it to 'streaming'
    // and the live chunks keep flowing on the same SSE.
    if (liveTransitionInProgressRef.current) {
      return;
    }

    pilotPhaseSyncKeyRef.current = null;
    pilotRecoveryRequestedRef.current = false;
    setPersistedInteractionState(null);
    setInteractionUiStatus(streamUi.isStreaming ? 'streaming' : 'idle');
  }, [
    activity?.currentPhase,
    activityId,
    requestPilotRecoverySync,
    selectedPhase,
    shouldSuppressReactiveRecoveryTrigger,
    streamUi.isStreaming,
  ]);

  useEffect(() => {
    if (
      !workerStatusResolved ||
      !activityId ||
      !activity ||
      activity.status !== 'active' ||
      selectedPhase !== activity.currentPhase ||
      !isPilotInteractionPhase(selectedPhase)
    ) {
      workerRecoverySyncKeyRef.current = null;
      return;
    }

    const syncKey = `${activityId}:${selectedPhase}:${
      workerConnected ? 'connected' : 'disconnected'
    }`;
    const bootstrapKey = `${activityId}:${selectedPhase}`;
    if (workerRecoveryBootstrapKeyRef.current !== bootstrapKey) {
      workerRecoveryBootstrapKeyRef.current = bootstrapKey;
      workerRecoverySyncKeyRef.current = syncKey;
      return;
    }
    if (workerRecoverySyncKeyRef.current === syncKey) {
      return;
    }

    workerRecoverySyncKeyRef.current = syncKey;
    if (
      shouldSuppressReactiveRecoveryTrigger(
        workerConnected ? 'online' : 'focus',
        selectedPhase,
      )
    ) {
      return;
    }
    void requestPilotRecoverySync(workerConnected ? 'online' : 'focus', {
      phase: selectedPhase,
      skipChatReconnect: true,
    });
  }, [
    activity,
    activityId,
    requestPilotRecoverySync,
    selectedPhase,
    shouldSuppressReactiveRecoveryTrigger,
    workerConnected,
    workerStatusResolved,
  ]);

  useEffect(() => {
    if (!activity) {
      setViewMode(prev => (prev === 'chat' ? prev : 'chat'));
      return;
    }
    const phases = pipelineDefinition.phases.map(p => p.id);
    const selIdx = phases.indexOf(selectedPhase);
    const curIdx = phases.indexOf(activity.currentPhase);
    const isApprovedPhase =
      activity.status === 'completed' ||
      (selIdx !== -1 && curIdx !== -1 && selIdx < curIdx);
    // QA defaults to reading when execution finished (content is read-only)
    const isQaCompleted =
      selectedPhase === 'qa' &&
      activity.currentPhase === 'qa' &&
      streamUi.executionState !== 'running';
    // Pronto switches to reading mode ONLY after confirmed completion (phase_complete/done
    // received) or on page reload with persisted messages (never streamed in this session).
    // During SSE drops (everStartedStreaming=true but phaseCompleted=false), stays in chat
    // mode so the approve button stays hidden until the agent truly finishes.
    const currentPhaseMessages_ = messages.filter(
      m => m.phase === selectedPhase,
    );
    const isProntoCompleted =
      selectedPhase === 'pronto' &&
      activity.currentPhase === 'pronto' &&
      !streamUi.isStreaming &&
      (streamUi.phaseCompleted ||
        (!streamUi.everStartedStreaming && currentPhaseMessages_.length > 0));
    const newMode =
      isApprovedPhase || isQaCompleted || isProntoCompleted
        ? 'reading'
        : 'chat';
    setViewMode(prev => (prev === newMode ? prev : newMode));
  }, [
    selectedPhase,
    activity?.currentPhase,
    activity?.status,
    pipelineDefinition.phases,
    streamUi.executionState,
    streamUi.isStreaming,
    streamUi.phaseCompleted,
    streamUi.everStartedStreaming,
    messages,
  ]);

  const phaseIndexById = useMemo(() => {
    return pipelineDefinition.phases.reduce<Map<Phase, number>>(
      (acc, phase, index) => {
        acc.set(phase.id, index);
        return acc;
      },
      new Map<Phase, number>(),
    );
  }, [pipelineDefinition.phases]);

  const getPhaseIndex = useCallback(
    (phase: Phase): number => phaseIndexById.get(phase) ?? -1,
    [phaseIndexById],
  );

  const shouldIgnoreOutOfOrderTransition = useCallback(
    (fromPhase: Phase, nextPhase: Phase): boolean => {
      const fromIndex = getPhaseIndex(fromPhase);
      const nextIndex = getPhaseIndex(nextPhase);

      if (fromIndex === -1 || nextIndex === -1) {
        return false;
      }

      return nextIndex < fromIndex;
    },
    [getPhaseIndex],
  );

  // Initial load
  useEffect(() => {
    const init = async () => {
      await Promise.all([loadPipelineDefinition(), loadProjectLabels()]);
      const act = await loadActivity();
      if (act) {
        await Promise.all([
          loadMessages(act.currentPhase),
          loadMetrics(),
          loadArtifacts(),
        ]);
        if (activityId && isPilotInteractionPhase(act.currentPhase)) {
          pilotPhaseSyncKeyRef.current = `${activityId}:${act.currentPhase}:${act.currentPhase}`;
        }
        await requestPilotRecoverySyncRef.current?.({
          trigger: 'focus',
          phase: act.currentPhase,
          skipChatReconnect: true,
        });
      }
    };
    init();
  }, [
    activityId,
    api,
    loadActivity,
    loadMessages,
    loadMetrics,
    loadArtifacts,
    loadPipelineDefinition,
    loadProjectLabels,
  ]);

  // Delayed re-sync: the backend may still be persisting messages/artifacts
  // when the initial load runs (race condition on server after SSE drop).
  // A secondary load after 3s catches these late-persisted records.
  useEffect(() => {
    if (!activityId || !activity || loading) return undefined;
    const timer = setTimeout(async () => {
      const activePhase = selectedPhaseRef.current;
      const currentActivityPhase =
        lastActivityRef.current?.currentPhase ?? activity.currentPhase;
      const shouldHoldPilotMessageReload =
        isPilotInteractionPhase(activePhase) &&
        activePhase === currentActivityPhase &&
        (pilotRecoveryRequestedRef.current ||
          Boolean(pilotRecoveryStreamAbortRef.current) ||
          Boolean(persistedInteractionStateIdRef.current));

      if (shouldHoldPilotMessageReload) {
        return;
      }

      try {
        await Promise.all([
          loadMessages(activity.currentPhase),
          loadArtifacts(),
        ]);
      } catch {
        // silently ignore retry failures
      }
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  useEffect(() => {
    if (!activity) {
      setCreatedByLabel(undefined);
      return undefined;
    }

    if (activity.userId === 'anonymous') {
      setCreatedByLabel(formatUserEntityRef(activity.userId));
      return undefined;
    }

    let cancelled = false;

    const loadCreatedBy = async () => {
      try {
        const response = await catalogApi.getEntitiesByRefs({
          entityRefs: [activity.userId],
          fields: [
            'metadata.name',
            'metadata.title',
            'spec.profile.displayName',
          ],
        });

        if (cancelled) {
          return;
        }

        const label = getCatalogEntityDisplayName(response.items[0]);
        setCreatedByLabel(label ?? formatUserEntityRef(activity.userId));
      } catch {
        if (!cancelled) {
          setCreatedByLabel(formatUserEntityRef(activity.userId));
        }
      }
    };

    void loadCreatedBy();
    return () => {
      cancelled = true;
    };
  }, [activity, catalogApi]);

  // Poll while activity is preparing (clone in progress)
  useEffect(() => {
    if (activity?.status !== 'preparing') return undefined;

    const pollInterval = setInterval(async () => {
      const updated = await loadActivity();
      if (updated && updated.status !== 'preparing') {
        // Clone finished, load all data
        await Promise.all([
          loadMessages(updated.currentPhase),
          loadMetrics(),
          loadArtifacts(),
        ]);
        if (activityId && isPilotInteractionPhase(updated.currentPhase)) {
          pilotPhaseSyncKeyRef.current = `${activityId}:${updated.currentPhase}:${updated.currentPhase}`;
        }
        await requestPilotRecoverySync('focus', {
          phase: updated.currentPhase,
          skipChatReconnect: true,
        });
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [
    activity?.status,
    activityId,
    loadActivity,
    loadMessages,
    loadMetrics,
    loadArtifacts,
    requestPilotRecoverySync,
  ]);

  // Load messages when selected phase changes
  const handlePhaseClick = useCallback(
    async (phase: Phase) => {
      setSelectedPhase(phase);
      // Clear previous phase messages immediately to avoid stale content flash
      setMessages([]);
      await Promise.all([loadMessages(phase), loadMetrics()]);
    },
    [loadMessages, loadMetrics],
  );

  const reconcilePhaseWithBackend = useCallback(
    async (expectedPhase: Phase) => {
      if (!activityId) return;

      const runId = ++phaseReconcileRunRef.current;
      const retryDelays = [0, 600, 1500, 3000];

      for (const waitMs of retryDelays) {
        if (waitMs > 0) {
          await delay(waitMs);
        }

        if (runId !== phaseReconcileRunRef.current) {
          return;
        }

        try {
          const updatedActivity = await api.getActivity(activityId);
          if (runId !== phaseReconcileRunRef.current) {
            return;
          }

          const backendPhase = updatedActivity.currentPhase;
          const backendIsBehind = shouldIgnoreOutOfOrderTransition(
            expectedPhase,
            backendPhase,
          );

          if (backendIsBehind) {
            continue;
          }

          setActivity(prev => {
            if (
              prev &&
              prev.currentPhase === updatedActivity.currentPhase &&
              prev.status === updatedActivity.status &&
              prev.updatedAt === updatedActivity.updatedAt
            ) {
              return prev;
            }
            return updatedActivity;
          });
          setSelectedPhase(backendPhase);
          setPhaseSyncWarning(undefined);
          await Promise.all([
            loadMessages(backendPhase),
            loadMetrics(),
            loadArtifacts(),
          ]);
          return;
        } catch {
          // Retry on transient failures
        }
      }

      if (runId !== phaseReconcileRunRef.current) {
        return;
      }

      setPhaseSyncWarning(
        `Não foi possível confirmar no backend a transição para ${expectedPhase}. A tela será mantida no estado atual e atualizada na próxima verificação.`,
      );
    },
    [
      activityId,
      api,
      loadArtifacts,
      loadExecutionState,
      loadMessages,
      loadMetrics,
      shouldIgnoreOutOfOrderTransition,
    ],
  );

  // Sincroniza estado após atividade completada automaticamente
  const syncAfterActivityComplete = useCallback(async () => {
    try {
      const updatedActivity = await loadActivity();
      if (updatedActivity) {
        setActivity(updatedActivity);
        await Promise.all([loadMetrics(), loadArtifacts()]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erro ao sincronizar após atividade completa:', err);
    }
  }, [loadActivity, loadMetrics, loadArtifacts]);

  const handleSharedSSEEvent = useCallback(
    (event: StreamEvent) => {
      switch (event.type) {
        case 'phase_complete':
          // IA terminou a fase (ex: PR criado) - parar streaming e habilitar botao de aprovacao.
          // Use chat_complete (not chat_stop) to preserve streamingContent in the UI
          // until the handleSendMessage finally block persists it as a permanent message.
          clearLiveTransitionGuard();
          dispatchStreamUi({ type: 'chat_complete' });
          pilotRecoveryRequestedRef.current = false;
          setInteractionUiStatus('completed');
          setPersistedInteractionState(null);
          setPendingInteractionModalOpen(false);
          setPhaseConformityRepairNotice(undefined);
          refreshApprovalVisibility('stream:phase_complete');
          loadActivity();
          loadArtifacts();
          loadExecutionState();
          break;
        case 'phase_auto_transition':
          setPhaseConformityRepairNotice(undefined);
          if (event.nextPhase) {
            const nextPhase = event.nextPhase;
            const nextPhaseDefinition = pipelineDefinition.phases.find(
              phase => phase.id === nextPhase,
            );
            const localPhase =
              lastActivityRef.current?.currentPhase ?? selectedPhaseRef.current;

            if (shouldIgnoreOutOfOrderTransition(localPhase, nextPhase)) {
              break;
            }

            setPhaseSyncWarning(undefined);

            if (nextPhase !== 'qa') {
              dispatchStreamUi({
                type: 'execution_state',
                executionState: 'idle',
              });
              dispatchStreamUi({ type: 'task_stream_stop' });
              // Use phase_changed (not chat_complete) to reset phaseCompleted and
              // everStartedStreaming. Without this, QA's chat_complete flags leak
              // into Pronto, causing it to start in reading mode instead of chat.
              dispatchStreamUi({ type: 'phase_changed' });
            }
            setPersistedInteractionState(null);
            pilotRecoveryRequestedRef.current = false;
            // ADR-013: the worker keeps streaming the new phase on the SAME SSE
            // session. For pilot→pilot transitions, re-activate the stream UI
            // and arm a single-shot guard that blinds the cascading resets
            // (selectedPhase effect + focus sync) until the first real live
            // event of the new phase, phase_complete/done/error, or a dead-man
            // timeout.
            const nextIsPilotInteraction = isPilotInteractionPhase(nextPhase);
            if (nextIsPilotInteraction) {
              liveTransitionInProgressRef.current = true;
              setIsLiveTransitionPending(true);
              if (liveTransitionTimeoutRef.current !== null) {
                clearTimeout(liveTransitionTimeoutRef.current);
              }
              liveTransitionTimeoutRef.current = setTimeout(() => {
                liveTransitionTimeoutRef.current = null;
                liveTransitionInProgressRef.current = false;
                setIsLiveTransitionPending(false);
              }, LIVE_TRANSITION_DEAD_MAN_MS);
              dispatchStreamUi({ type: 'chat_resume_stream' });
              setInteractionUiStatus('streaming');
            } else {
              setInteractionUiStatus('idle');
            }
            setPendingInteractionModalOpen(false);

            if (
              nextPhaseDefinition?.autoStart &&
              nextPhaseDefinition.autoStartPolicy === 'frontend'
            ) {
              setPendingFrontendAutoStartPhase(nextPhase);
            }

            setActivity(prev => {
              if (!prev) return prev;
              if (
                shouldIgnoreOutOfOrderTransition(prev.currentPhase, nextPhase)
              ) {
                return prev;
              }
              if (prev.currentPhase === nextPhase) {
                return prev;
              }
              return { ...prev, currentPhase: nextPhase };
            });

            setSelectedPhase(prev => {
              if (shouldIgnoreOutOfOrderTransition(prev, nextPhase)) {
                return prev;
              }
              return nextPhase;
            });

            setMessages([]);
            void Promise.all([
              loadMessages(nextPhase),
              loadMetrics(),
              loadArtifacts(),
              loadExecutionState(),
            ]);
            void reconcilePhaseWithBackend(nextPhase);
          }
          break;
        case 'activity_complete':
          setPhaseConformityRepairNotice(undefined);
          refreshApprovalVisibility('stream:activity_complete');
          syncAfterActivityComplete();
          break;
        case 'done':
          // No-op: tratado individualmente no finally de cada funcao de streaming
          clearLiveTransitionGuard();
          setPhaseConformityRepairNotice(undefined);
          break;
        case 'phase_conformity_repair_required':
          if (activityId) {
            setPhaseConformityRepairNotice({
              activityId,
              ...buildPhaseConformityRepairNotice(
                event.phase,
                lastActivityRef.current?.currentPhase ??
                  selectedPhaseRef.current,
                event.missingConcepts,
              ),
            });
          }
          break;
        case 'phase_conformity_repair_unavailable':
          if (activityId) {
            setPhaseConformityRepairNotice({
              activityId,
              phase:
                lastActivityRef.current?.currentPhase ??
                selectedPhaseRef.current,
              content:
                'A retomada automática não pôde continuar. Use a ação de recuperação disponível para esta fase.',
            });
          }
          void loadActivity();
          break;
        case 'error':
          clearLiveTransitionGuard();
          setPhaseConformityRepairNotice(undefined);
          setError(`Erro do agente: ${event.content}`);
          pilotRecoveryRequestedRef.current = true;
          setInteractionUiStatus('recovering');
          break;
        case 'rate_limit':
          dispatchStreamUi({
            type: 'chat_rate_limit',
            content: event.content,
            resetTime: event.resetTime,
            timezone: event.timezone,
          });
          break;
        case 'model_fallback':
          dispatchStreamUi({
            type: 'chat_model_fallback',
            content: event.content,
          });
          break;
        default:
          break;
      }
    },
    [
      clearLiveTransitionGuard,
      activityId,
      loadActivity,
      loadArtifacts,
      loadExecutionState,
      loadMessages,
      loadMetrics,
      pipelineDefinition.phases,
      refreshApprovalVisibility,
      reconcilePhaseWithBackend,
      shouldIgnoreOutOfOrderTransition,
      syncAfterActivityComplete,
    ],
  );

  const handleRecoveredPilotStreamEvent = useCallback(
    (event: StreamEvent) => {
      stopHeartbeatWatchdog();
      pilotRecoveryEventReceivedRef.current = true;
      if (shouldIgnoreRecoveredReplayEvent(event)) {
        return;
      }
      switch (event.type) {
        case 'text':
          resetAuthoritativeSyncFailureState();
          setInteractionUiStatus('streaming');
          if (postAnswerRecoveryActiveRef.current) {
            dispatchPostAnswerRecovery({
              type: 'stream_live',
              nowMs: Date.now(),
            });
          }
          queuePilotStreamChunk(event.content);
          Promise.resolve().then(() => {
            if (pilotRecoveryStreamAbortRef.current) {
              flushPilotStreamChunkBuffer();
            }
          });
          break;
        case 'tool_use':
          resetAuthoritativeSyncFailureState();
          setInteractionUiStatus('streaming');
          if (postAnswerRecoveryActiveRef.current) {
            dispatchPostAnswerRecovery({
              type: 'stream_live',
              nowMs: Date.now(),
            });
          }
          queuePilotStreamChunk(`\n[${event.content}]\n`);
          Promise.resolve().then(() => {
            if (pilotRecoveryStreamAbortRef.current) {
              flushPilotStreamChunkBuffer();
            }
          });
          setCurrentTool(event.content);
          setToolCount(prev => prev + 1);
          break;
        case 'ask_user_question':
          resetAuthoritativeSyncFailureState();
          flushPilotStreamChunkBuffer();
          setInteractionUiStatus('awaiting_user');
          if (postAnswerRecoveryActiveRef.current) {
            dispatchPostAnswerRecovery({
              type: 'awaiting_new_interaction',
              nowMs: Date.now(),
            });
          }
          setPendingInteractionModalOpen(true);
          if (event.questions) {
            dispatchStreamUi({
              type: 'chat_set_questions',
              questions: event.questions.questions,
              content: `\n\n${event.content}\n\n`,
            });
            void syncPilotInteractionState(selectedPhaseRef.current, {
              recovered: false,
            });
          } else {
            queuePilotStreamChunk(`\n\n${event.content}\n\n`, {
              flushNow: true,
            });
          }
          break;
        case 'approaches':
          resetAuthoritativeSyncFailureState();
          flushPilotStreamChunkBuffer();
          if (event.approaches) {
            setInteractionUiStatus('awaiting_user');
            if (postAnswerRecoveryActiveRef.current) {
              dispatchPostAnswerRecovery({
                type: 'awaiting_new_interaction',
                nowMs: Date.now(),
              });
            }
            setPendingInteractionModalOpen(true);
            dispatchStreamUi({
              type: 'chat_set_approaches',
              approaches: event.approaches,
              content: event.content ? `\n\n${event.content}\n\n` : '',
            });
            void syncPilotInteractionState(selectedPhaseRef.current, {
              recovered: false,
            });
          }
          break;
        case 'metrics':
          break;
        case 'rate_limit':
          flushPilotStreamChunkBuffer();
          dispatchStreamUi({
            type: 'chat_rate_limit',
            content: event.content,
            resetTime: event.resetTime,
            timezone: event.timezone,
          });
          break;
        default:
          flushPilotStreamChunkBuffer();
          handleSharedSSEEvent(event);
          break;
      }
    },
    [
      flushPilotStreamChunkBuffer,
      handleSharedSSEEvent,
      queuePilotStreamChunk,
      resetAuthoritativeSyncFailureState,
      shouldIgnoreRecoveredReplayEvent,
      stopHeartbeatWatchdog,
      syncPilotInteractionState,
    ],
  );

  const handleRecoveredPilotStreamEventRef = useRef(
    handleRecoveredPilotStreamEvent,
  );

  useEffect(() => {
    handleRecoveredPilotStreamEventRef.current =
      handleRecoveredPilotStreamEvent;
  }, [handleRecoveredPilotStreamEvent]);

  // Send message via SSE
  const [availableSkills, setAvailableSkills] = useState<
    import('../../api/types').SkillSummary[]
  >([]);

  const loadAvailableSkills = useCallback(async () => {
    if (!activityId) return;
    try {
      const result = await api.getAvailableSkills(activityId);
      setAvailableSkills((result.skills as any[]) ?? []);
    } catch {
      // Skills not available — silent
    }
  }, [activityId, api]);

  const handleSendMessage = useCallback(
    async (
      text: string,
      options: {
        optimisticUserMessage?: boolean;
        bypassStreamingGuards?: boolean;
        skillId?: string;
      } = {},
    ) => {
      const isQaExecutionRunning =
        activity?.currentPhase === 'qa' &&
        selectedPhase === 'qa' &&
        streamUi.executionState === 'running';
      if (!activityId || streamUi.isTaskStreaming || isQaExecutionRunning) {
        return;
      }

      // Interrupt current streaming session if active (with 2s cooldown)
      if (streamUi.isStreaming && !options.bypassStreamingGuards) {
        const INTERRUPT_COOLDOWN_MS = 2000;
        const now = Date.now();
        if (now - lastInterruptTimestampRef.current < INTERRUPT_COOLDOWN_MS) {
          return;
        }
        lastInterruptTimestampRef.current = now;
        // Persist partial response as assistant message before aborting
        if (streamUi.streamingContent) {
          setMessages(prev => [
            ...prev,
            {
              id: `temp-interrupted-${Date.now()}`,
              activityId,
              phase: selectedPhase,
              role: 'assistant' as const,
              content: streamUi.streamingContent,
              timestamp: new Date().toISOString(),
            },
          ]);
        }
        chatStreamAbortRef.current?.abort();
        dispatchStreamUi({ type: 'chat_interrupt' });
      }
      const shouldAddOptimisticUserMessage =
        options.optimisticUserMessage ?? true;
      const hadPendingInteraction =
        isPilotInteractionPhase(selectedPhase) &&
        (Boolean(streamUi.pendingQuestions?.length) ||
          Boolean(streamUi.pendingApproaches));
      const requiresPilotOwnership =
        isPilotInteractionPhase(selectedPhase) &&
        selectedPhase === activity?.currentPhase;

      if (requiresPilotOwnership && !ensurePilotStreamOwnership('live')) {
        return;
      }

      setPhaseSyncWarning(undefined);

      if (shouldAddOptimisticUserMessage) {
        const userMsg: ChatMessage = {
          id: `temp-${Date.now()}`,
          activityId,
          phase: selectedPhase,
          role: 'user',
          content: text,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);
      }
      clearCurrentDraft();
      if (hadPendingInteraction) {
        dispatchStreamUi({ type: 'chat_clear_questions' });
        dispatchStreamUi({ type: 'chat_clear_approaches' });
        setPendingInteractionModalOpen(false);
      }
      resetRecoveredReplayDedup();
      dispatchStreamUi({ type: 'chat_start' });
      setPilotRecoveryRequested(false);
      setInteractionUiStatus('streaming');
      setPersistedInteractionState(null);
      setCurrentTool(undefined);
      setToolCount(0);
      logPilotStreamEvent('stream_open', {
        streamKind: 'live',
        skillId: options.skillId ?? null,
      });

      let fullResponse = '';
      let receivedDone = false;
      try {
        await runChatStream(async streamController => {
          startHeartbeatWatchdog();
          try {
            await api.sendMessage(
              activityId,
              text,
              (event: StreamEvent) => {
                onHeartbeat();
                // ADR-013: the first real live event of a new phase after
                // phase_auto_transition must consume the transition guard, so
                // the deferred effects resume their normal behavior.
                if (
                  liveTransitionInProgressRef.current &&
                  (event.type === 'text' ||
                    event.type === 'tool_use' ||
                    event.type === 'ask_user_question' ||
                    event.type === 'approaches')
                ) {
                  clearLiveTransitionGuard();
                }
                switch (event.type) {
                  case 'text':
                    fullResponse += event.content;
                    queuePilotStreamChunk(event.content);
                    setCurrentTool(undefined);
                    break;
                  case 'tool_use':
                    fullResponse += `\n[${event.content}]\n`;
                    queuePilotStreamChunk(`\n[${event.content}]\n`);
                    setCurrentTool(event.content);
                    setToolCount(prev => prev + 1);
                    break;
                  case 'ask_user_question':
                    if (event.questions) {
                      const questionCount =
                        event.questions.questions?.length ?? 0;
                      fullResponse += `\n\n---\n_${questionCount} pergunta(s) de clarificação enviada(s) ao usuário._\n---\n\n`;
                    } else {
                      fullResponse += `\n\n${event.content}\n\n`;
                    }
                    flushPilotStreamChunkBuffer();
                    setInteractionUiStatus('awaiting_user');
                    setPendingInteractionModalOpen(true);
                    if (event.questions) {
                      dispatchStreamUi({
                        type: 'chat_set_questions',
                        questions: event.questions.questions,
                        content: `\n\n${event.content}\n\n`,
                      });
                      void syncPilotInteractionState(selectedPhaseRef.current, {
                        recovered: false,
                      });
                    } else {
                      queuePilotStreamChunk(`\n\n${event.content}\n\n`, {
                        flushNow: true,
                      });
                    }
                    break;
                  case 'approaches':
                    {
                      const approachCount =
                        event.approaches?.approaches?.length ?? 0;
                      fullResponse += `\n\n---\n_${approachCount} abordagem(ns) apresentada(s) ao usuário._\n---\n\n`;
                    }
                    flushPilotStreamChunkBuffer();
                    if (event.approaches) {
                      setInteractionUiStatus('awaiting_user');
                      setPendingInteractionModalOpen(true);
                      dispatchStreamUi({
                        type: 'chat_set_approaches',
                        approaches: event.approaches,
                        content: `\n\n${event.content}\n\n`,
                      });
                      void syncPilotInteractionState(selectedPhaseRef.current, {
                        recovered: false,
                      });
                    }
                    break;
                  case 'metrics':
                    break;
                  case 'done':
                    flushPilotStreamChunkBuffer();
                    setPhaseConformityRepairNotice(undefined);
                    receivedDone = true;
                    logPilotStreamEvent('stream_done', {
                      streamKind: 'live',
                    });
                    break;
                  case 'rate_limit':
                    flushPilotStreamChunkBuffer();
                    dispatchStreamUi({
                      type: 'chat_rate_limit',
                      content: event.content,
                      resetTime: event.resetTime,
                      timezone: event.timezone,
                    });
                    break;
                  default:
                    flushPilotStreamChunkBuffer();
                    handleSharedSSEEvent(event);
                    break;
                }
              },
              streamController.signal,
              { onHeartbeat, onSeqId, skillId: options.skillId },
            );
          } catch (e) {
            if (streamController.signal.aborted) {
              logPilotStreamEvent(
                'stream_abort',
                { streamKind: 'live', reason: 'signal_aborted' },
                'warn',
              );
              flushPilotStreamChunkBuffer({ discard: true });
              return;
            }

            logPilotStreamEvent(
              'stream_error',
              {
                streamKind: 'live',
                error: e instanceof Error ? e.message : String(e),
              },
              'error',
            );
            flushPilotStreamChunkBuffer();
            releasePilotTabStream();
            if (hadPendingInteraction) {
              await syncPilotInteractionState(selectedPhaseRef.current, {
                recovered: true,
              });
            }
            if (!isRestartingRef.current) {
              setError(
                `Erro ao enviar mensagem: ${
                  e instanceof Error ? e.message : String(e)
                }`,
              );
            }
          } finally {
            stopHeartbeatWatchdog();
            if (streamController.signal.aborted) {
              flushPilotStreamChunkBuffer({ discard: true });
              if (
                chatStreamAbortRef.current === null &&
                !pilotRecoveryStreamAbortRef.current
              ) {
                releasePilotTabStream();
              }
              return;
            }

            flushPilotStreamChunkBuffer();
            if (!isRestartingRef.current) {
              if (fullResponse && selectedPhaseRef.current === selectedPhase) {
                // ADR-009: drop any previous temp-assistant-* placeholder for
                // this exact phase before appending a new one, so reconnect
                // rounds do not stack multiple drafts ("step 1 duplicated"
                // symptom). The next loadMessages refetch will replace the
                // whole list with persisted rows, so this scoping keeps the
                // UI consistent while the refetch is inflight.
                setMessages(prev => [
                  ...prev.filter(
                    message =>
                      !(
                        message.phase === selectedPhase &&
                        message.id.startsWith('temp-assistant-')
                      ),
                  ),
                  {
                    id: `temp-assistant-${Date.now()}`,
                    activityId,
                    phase: selectedPhase,
                    role: 'assistant',
                    content: fullResponse,
                    timestamp: new Date().toISOString(),
                  },
                ]);
              }

              const isArtifactPhaseLocal =
                selectedPhase === 'gerando-prd' ||
                selectedPhase === 'gerando-techspec';
              const isConfirmedCompletion =
                receivedDone ||
                (isArtifactPhaseLocal && fullResponse.length > 0);
              setPilotRecoveryRequested(!isConfirmedCompletion);
              setInteractionUiStatus(
                isConfirmedCompletion ? 'completed' : 'recovering',
              );
              dispatchStreamUi({
                type: isConfirmedCompletion ? 'chat_complete' : 'chat_stop',
              });
              await loadMetrics();
              await loadArtifacts();
              await loadExecutionState();
              const updatedActivity = await loadActivity();
              if (
                updatedActivity &&
                activity &&
                updatedActivity.currentPhase !== activity.currentPhase
              ) {
                setSelectedPhase(updatedActivity.currentPhase);
                void Promise.all([
                  loadMessages(updatedActivity.currentPhase),
                  loadArtifacts(),
                ]);
              } else {
                await loadMessages(selectedPhase);
              }
              if (
                isConfirmedCompletion &&
                isPilotInteractionPhase(selectedPhase)
              ) {
                setRecoverySnapshotContent('');
                setStableRecoverySnapshotContent('');
                await syncPilotInteractionState(selectedPhase);
                releasePilotTabStream();
              } else {
                logPilotStreamEvent(
                  'stream_recovery_requested',
                  {
                    streamKind: 'live',
                    reason: 'stream_closed_without_done',
                  },
                  'warn',
                );
              }
            }
          }
        });
      } catch (e) {
        logPilotStreamEvent(
          'stream_transport_error',
          {
            streamKind: 'live',
            error: e instanceof Error ? e.message : String(e),
          },
          'error',
        );
      }
    },
    [
      activity?.currentPhase,
      activityId,
      clearCurrentDraft,
      clearLiveTransitionGuard,
      ensurePilotStreamOwnership,
      flushPilotStreamChunkBuffer,
      handleSharedSSEEvent,
      loadActivity,
      loadArtifacts,
      loadExecutionState,
      loadMessages,
      loadMetrics,
      logPilotStreamEvent,
      onHeartbeat,
      onSeqId,
      queuePilotStreamChunk,
      selectedPhase,
      releasePilotTabStream,
      runChatStream,
      setPilotRecoveryRequested,
      startHeartbeatWatchdog,
      stopHeartbeatWatchdog,
      syncPilotInteractionState,
      api,
      resetRecoveredReplayDedup,
      streamUi.executionState,
      streamUi.pendingApproaches,
      streamUi.pendingQuestions,
      streamUi.isStreaming,
      streamUi.isTaskStreaming,
    ],
  );

  const stopPostAnswerReattachLoop = useCallback(
    (options: { resetAttempts?: boolean } = {}) => {
      if (postAnswerReattachTimerRef.current) {
        clearInterval(postAnswerReattachTimerRef.current);
        postAnswerReattachTimerRef.current = null;
      }
      if (options.resetAttempts) {
        postAnswerReattachAttemptsRef.current = 0;
      }
      postAnswerReattachInFlightRef.current = false;
    },
    [],
  );

  const stopPostAnswerRecoveryAutomation = useCallback(() => {
    if (answerPollRef.current) {
      clearTimeout(answerPollRef.current);
      answerPollRef.current = null;
    }
    retryPostAnswerPollingRef.current = null;
    stopPostAnswerReattachLoop({ resetAttempts: true });
    postAnswerReattachInFlightRef.current = false;
    postAnswerRecoveryActiveRef.current = false;
    postAnswerInitialMessageCountRef.current = 0;
    resetAuthoritativeSyncFailureState();
  }, [resetAuthoritativeSyncFailureState, stopPostAnswerReattachLoop]);

  const prepareSummaryResume = useCallback(() => {
    if (answerPollRef.current) {
      clearTimeout(answerPollRef.current);
      answerPollRef.current = null;
    }

    if (pilotRecoveryPollRef.current) {
      clearInterval(pilotRecoveryPollRef.current);
      pilotRecoveryPollRef.current = null;
    }

    if (pilotRecoveryStreamAbortRef.current) {
      pilotRecoveryStreamAbortRef.current.abort();
      pilotRecoveryStreamAbortRef.current = null;
    }
    pilotRecoveryStreamAttemptRef.current = null;
    pilotRecoveryEventReceivedRef.current = false;

    if (chatStreamAbortRef.current) {
      chatStreamAbortRef.current.abort();
      chatStreamAbortRef.current = null;
    }

    stopPostAnswerRecoveryAutomation();
    dispatchPostAnswerRecovery({ type: 'reset' });
    setPostAnswerRecoveryElapsedMs(0);

    flushPilotStreamChunkBuffer({ discard: true });

    dispatchStreamUi({ type: 'chat_stop' });
    pilotRecoveryRequestedRef.current = true;
    setInteractionUiStatus('recovering');
    setPersistedInteractionState(null);
    resetRecoveredReplayDedup();
  }, [
    flushPilotStreamChunkBuffer,
    resetRecoveredReplayDedup,
    stopPostAnswerRecoveryAutomation,
  ]);

  useEffect(() => {
    postAnswerRecoveryStageRef.current = postAnswerRecovery.stage;
  }, [postAnswerRecovery.stage]);

  useEffect(() => {
    if (postAnswerRecovery.stage === 'idle') {
      setPostAnswerRecoveryElapsedMs(0);
      return;
    }

    const updateElapsed = () => {
      if (!postAnswerRecovery.startedAtMs) {
        setPostAnswerRecoveryElapsedMs(0);
        return;
      }
      setPostAnswerRecoveryElapsedMs(
        Math.max(0, Date.now() - postAnswerRecovery.startedAtMs),
      );
    };

    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [postAnswerRecovery.stage, postAnswerRecovery.startedAtMs]);

  useEffect(() => {
    if (
      !postAnswerRecoveryActiveRef.current ||
      postAnswerRecovery.stage === 'idle' ||
      postAnswerRecovery.stage === 'awaiting_new_interaction' ||
      postAnswerRecovery.stage === 'completed' ||
      postAnswerRecovery.stage === 'failed_timeout'
    ) {
      return;
    }

    const progressAnchor =
      postAnswerRecovery.lastProgressAtMs ?? postAnswerRecovery.startedAtMs;
    if (!progressAnchor) {
      return;
    }

    const remainingMs =
      POST_ANSWER_RECOVERY_TIMEOUT_MS - (Date.now() - progressAnchor);

    if (remainingMs <= 0) {
      dispatchPostAnswerRecovery({
        type: 'failed_timeout',
        nowMs: Date.now(),
      });
      return;
    }

    const timeout = setTimeout(() => {
      dispatchPostAnswerRecovery({
        type: 'failed_timeout',
        nowMs: Date.now(),
      });
    }, remainingMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    postAnswerRecovery.lastProgressAtMs,
    postAnswerRecovery.stage,
    postAnswerRecovery.startedAtMs,
  ]);

  useEffect(() => {
    if (postAnswerRecovery.stage === 'stream_live') {
      stopPostAnswerReattachLoop();
      return;
    }

    if (
      postAnswerRecovery.stage === 'idle' ||
      postAnswerRecovery.stage === 'answer_sent' ||
      postAnswerRecovery.stage === 'stream_reattach_attempt' ||
      postAnswerRecovery.stage === 'backend_processing'
    ) {
      return;
    }

    stopPostAnswerRecoveryAutomation();
  }, [
    postAnswerRecovery.stage,
    stopPostAnswerReattachLoop,
    stopPostAnswerRecoveryAutomation,
  ]);

  useEffect(() => {
    if (
      postAnswerRecovery.stage !== 'completed' &&
      postAnswerRecovery.stage !== 'awaiting_new_interaction'
    ) {
      return;
    }

    const clearTerminalBanner = setTimeout(() => {
      dispatchPostAnswerRecovery({ type: 'reset' });
      setPostAnswerRecoveryElapsedMs(0);
    }, 1500);

    return () => {
      clearTimeout(clearTerminalBanner);
    };
  }, [postAnswerRecovery.stage]);

  // Answer pending question from AskUserQuestion
  // When SSE is still active, the answer flows through the existing stream.
  // When SSE has dropped (isStreaming === false), we first try to reattach the
  // live recovery stream and only fall back to polling when that is not
  // possible or the retry budget is exhausted.
  const answerPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup answer polling on unmount or phase change
  useEffect(() => {
    return () => {
      if (answerPollRef.current) {
        clearTimeout(answerPollRef.current);
        answerPollRef.current = null;
      }
    };
  }, [selectedPhase]);

  const startInteractionRecoveryPolling = useCallback(
    (initialMessageCount: number) => {
      if (!activityId) {
        return;
      }

      if (answerPollRef.current) {
        clearTimeout(answerPollRef.current);
        answerPollRef.current = null;
      }

      let pollCount = 0;
      let lastObservedMessageCount = initialMessageCount;
      let pollingStopped = false;
      let consecutiveSyncFailures = 0;
      let lastArtifactLiveRetryAtMs = 0;

      const stopPolling = ({
        clearManualRetry = true,
      }: {
        clearManualRetry?: boolean;
      } = {}) => {
        pollingStopped = true;
        if (answerPollRef.current) {
          clearTimeout(answerPollRef.current);
          answerPollRef.current = null;
        }
        if (clearManualRetry) {
          retryPostAnswerPollingRef.current = null;
        }
      };

      const getNextPollDelayMs = () =>
        Date.now() - (postAnswerRecovery.startedAtMs ?? Date.now()) <
        POST_ANSWER_FAST_POLL_WINDOW_MS
          ? POST_ANSWER_FAST_POLL_INTERVAL_MS
          : POST_ANSWER_SLOW_POLL_INTERVAL_MS;

      const scheduleNextPoll = (delayMs = getNextPollDelayMs()) => {
        if (pollingStopped || !postAnswerRecoveryActiveRef.current) {
          return;
        }

        answerPollRef.current = setTimeout(() => {
          void pollOnce();
        }, delayMs);
      };

      const handleSyncFailure = () => {
        consecutiveSyncFailures += 1;
        const backoffDelayMs =
          POST_ANSWER_SYNC_FAILURE_BACKOFF_MS[consecutiveSyncFailures - 1];

        if (backoffDelayMs !== undefined) {
          setAuthoritativeSyncFailureState({
            kind: 'transient',
            attempt: consecutiveSyncFailures,
          });
          scheduleNextPoll(backoffDelayMs);
          return false;
        }

        setAuthoritativeSyncFailureState({
          kind: 'persistent',
          attempt: consecutiveSyncFailures,
        });
        stopPolling({ clearManualRetry: false });
        return true;
      };

      const maybeRetryArtifactLiveRecovery = async (
        phaseToSync: Phase,
        interaction: InteractionState | null,
      ) => {
        if (
          interaction?.status !== 'streaming' ||
          !isArtifactGenerationPhase(phaseToSync) ||
          !interaction.canResume ||
          interaction.resumeMode !== 'direct' ||
          interaction.terminalReason
        ) {
          return;
        }

        const nowMs = Date.now();
        dispatchPostAnswerRecovery({
          type: 'progress',
          nowMs,
        });

        if (
          hasActivePilotStreamTransport() ||
          postAnswerReattachInFlightRef.current ||
          nowMs - lastArtifactLiveRetryAtMs <
            POST_ANSWER_ARTIFACT_LIVE_RETRY_INTERVAL_MS
        ) {
          return;
        }

        lastArtifactLiveRetryAtMs = nowMs;
        dispatchPostAnswerRecovery({
          type: 'stream_reattach_attempt',
          nowMs,
        });
        queuePilotRecoveryResume(interaction.id);
      };

      retryPostAnswerPollingRef.current = () => {
        consecutiveSyncFailures = 0;
        pollingStopped = false;
        resetAuthoritativeSyncFailureState();
        dispatchPostAnswerRecovery({
          type: 'backend_processing',
          nowMs: Date.now(),
        });
        void pollOnce();
      };

      const pollOnce = async () => {
        pollCount++;
        if (pollCount > POST_ANSWER_POLL_MAX_ATTEMPTS) {
          resetAuthoritativeSyncFailureState();
          stopPolling();
          dispatchPostAnswerRecovery({
            type: 'failed_timeout',
            nowMs: Date.now(),
          });
          return;
        }

        let shouldScheduleNextPoll = true;

        try {
          const interaction = await syncPilotInteractionState(
            selectedPhaseRef.current,
            {
              markRecovering: true,
              recovered: true,
              throwOnError: true,
            },
          );
          consecutiveSyncFailures = 0;
          resetAuthoritativeSyncFailureState();
          const isStaleSubmittedAwaitingInteraction =
            interaction?.status === 'awaiting_user' &&
            isSubmittedPendingInteractionStale(selectedPhaseRef.current, {
              interaction,
            });

          if (interaction?.status === 'expired') {
            stopPolling();
            shouldScheduleNextPoll = false;
            dispatchPostAnswerRecovery({
              type: 'failed_timeout',
              nowMs: Date.now(),
            });
            return;
          }

          if (
            interaction?.status === 'awaiting_user' &&
            !isStaleSubmittedAwaitingInteraction
          ) {
            stopPolling();
            shouldScheduleNextPoll = false;
            dispatchPostAnswerRecovery({
              type: 'awaiting_new_interaction',
              nowMs: Date.now(),
            });
            return;
          }

          const updatedMessages = await api.getMessages(
            activityId,
            selectedPhaseRef.current,
          );

          if (updatedMessages.length > lastObservedMessageCount) {
            lastObservedMessageCount = updatedMessages.length;
            setMessages(previousMessages =>
              areChatMessagesEqual(previousMessages, updatedMessages)
                ? previousMessages
                : updatedMessages,
            );
          }

          if (
            interaction?.status === 'completed' ||
            interaction?.status === 'canceled'
          ) {
            stopPolling();
            shouldScheduleNextPoll = false;
            dispatchPostAnswerRecovery({
              type: 'completed',
              nowMs: Date.now(),
            });
            return;
          }

          if (interaction?.status === 'streaming') {
            dispatchPostAnswerRecovery({
              type: 'backend_processing',
              nowMs: Date.now(),
            });
            await maybeRetryArtifactLiveRecovery(
              selectedPhaseRef.current,
              interaction,
            );
          } else if (isStaleSubmittedAwaitingInteraction) {
            dispatchPostAnswerRecovery({
              type: 'backend_processing',
              nowMs: Date.now(),
            });
          }

          const updatedActivity = await loadActivity();
          if (!updatedActivity) {
            return;
          }

          if (updatedActivity.currentPhase !== selectedPhaseRef.current) {
            stopPolling();
            shouldScheduleNextPoll = false;
            dispatchPostAnswerRecovery({
              type: 'completed',
              nowMs: Date.now(),
            });
            await Promise.all([loadMetrics(), loadArtifacts()]);
            setSelectedPhase(updatedActivity.currentPhase);
            await loadMessages(updatedActivity.currentPhase);
          }
        } catch {
          shouldScheduleNextPoll = false;
          handleSyncFailure();
        } finally {
          if (shouldScheduleNextPoll) {
            scheduleNextPoll();
          }
        }
      };

      scheduleNextPoll();
    },
    [
      activityId,
      api,
      loadActivity,
      loadArtifacts,
      loadMessages,
      loadMetrics,
      postAnswerRecovery.startedAtMs,
      resetAuthoritativeSyncFailureState,
      syncPilotInteractionState,
      isSubmittedPendingInteractionStale,
      dispatchPostAnswerRecovery,
      hasActivePilotStreamTransport,
      queuePilotRecoveryResume,
    ],
  );

  const fallbackToPostAnswerPolling = useCallback(
    (initialMessageCount?: number) => {
      if (!postAnswerRecoveryActiveRef.current) {
        return;
      }

      stopPostAnswerReattachLoop();
      dispatchPostAnswerRecovery({
        type: 'backend_processing',
        nowMs: Date.now(),
      });
      startInteractionRecoveryPolling(
        initialMessageCount ?? postAnswerInitialMessageCountRef.current,
      );
    },
    [
      dispatchPostAnswerRecovery,
      startInteractionRecoveryPolling,
      stopPostAnswerReattachLoop,
    ],
  );

  useEffect(() => {
    fallbackPostAnswerToPollingRef.current = fallbackToPostAnswerPolling;
    return () => {
      fallbackPostAnswerToPollingRef.current = null;
    };
  }, [fallbackToPostAnswerPolling]);

  const startPostAnswerRecoveryFlow = useCallback(
    (initialMessageCount: number) => {
      if (!activityId) {
        return;
      }

      stopPostAnswerRecoveryAutomation();
      postAnswerInitialMessageCountRef.current = initialMessageCount;
      postAnswerRecoveryActiveRef.current = true;
      postAnswerReattachAttemptsRef.current = 0;
      resetAuthoritativeSyncFailureState();
      resetRecoveredReplayDedup();
      dispatchPostAnswerRecovery({
        type: 'start',
        nowMs: Date.now(),
      });
      setPostAnswerRecoveryElapsedMs(0);

      const attemptReattach = async () => {
        if (!postAnswerRecoveryActiveRef.current) {
          return;
        }
        if (postAnswerReattachInFlightRef.current) {
          return;
        }
        if (pilotRecoveryStreamAbortRef.current) {
          return;
        }
        if (postAnswerRecoveryStageRef.current === 'stream_live') {
          return;
        }
        if (
          postAnswerReattachAttemptsRef.current >=
          POST_ANSWER_REATTACH_MAX_RETRIES
        ) {
          fallbackToPostAnswerPolling(initialMessageCount);
          return;
        }

        const isFirstAttempt = postAnswerReattachAttemptsRef.current === 0;
        postAnswerReattachInFlightRef.current = true;
        postAnswerReattachAttemptsRef.current += 1;
        dispatchPostAnswerRecovery({
          type: 'stream_reattach_attempt',
          nowMs: Date.now(),
        });
        try {
          const recoveryOutcome = await requestPilotRecoverySync(
            'answer_submitted',
            {
              phase: selectedPhaseRef.current,
              skipChatReconnect: !isFirstAttempt,
            },
          );
          if (!postAnswerRecoveryActiveRef.current) {
            return;
          }

          if (recoveryOutcome === 'rebuild') {
            fallbackToPostAnswerPolling(initialMessageCount);
          }
        } catch {
          if (!postAnswerRecoveryActiveRef.current) {
            return;
          }
        } finally {
          postAnswerReattachInFlightRef.current = false;
        }
      };

      void attemptReattach();

      postAnswerReattachTimerRef.current = setInterval(() => {
        void attemptReattach();
      }, POST_ANSWER_REATTACH_RETRY_INTERVAL_MS);
    },
    [
      activityId,
      fallbackToPostAnswerPolling,
      resetRecoveredReplayDedup,
      resetAuthoritativeSyncFailureState,
      stopPostAnswerRecoveryAutomation,
      requestPilotRecoverySync,
    ],
  );

  const handleAnswerQuestion = useCallback(
    async (answers: Record<string, string>) => {
      if (!activityId) return;
      const hasLivePilotTransport = hasActivePilotStreamTransport();
      try {
        const result = await api.answerQuestion(activityId, answers);
        if (result.resumeMode === 'summary_required') {
          if (!result.resumeMessage) {
            throw new Error(
              'Resumo de retomada indisponível para esta interação.',
            );
          }
          setMessages(prev => [
            ...prev,
            {
              id: `system-summary-required-${Date.now()}`,
              activityId,
              phase: selectedPhase,
              role: 'system' as const,
              content:
                'Sessão anterior expirou. Retomando PRD com resumo do checkpoint...',
              timestamp: new Date().toISOString(),
            },
          ]);
          prepareSummaryResume();
          await handleSendMessage(result.resumeMessage, {
            optimisticUserMessage: false,
            bypassStreamingGuards: true,
          });
          return;
        }

        rememberSubmittedPendingInteraction(
          'question',
          selectedPhaseRef.current,
        );
        dispatchStreamUi({ type: 'chat_clear_questions' });
        setPersistedInteractionState(current =>
          current?.status === 'awaiting_user' && current.type === 'question'
            ? null
            : current,
        );
        setPendingInteractionModalOpen(false);
        pilotRecoveryRequestedRef.current = !hasLivePilotTransport;
        setInteractionUiStatus(
          hasLivePilotTransport ? 'streaming' : 'recovering',
        );
        if (hasLivePilotTransport) {
          stopPostAnswerRecoveryAutomation();
          dispatchPostAnswerRecovery({ type: 'reset' });
          setPostAnswerRecoveryElapsedMs(0);
        } else {
          startPostAnswerRecoveryFlow(messages.length);
        }

        // SSE still active — continuation arrives through the stream, nothing else needed.
        if (hasLivePilotTransport) return;
      } catch (e) {
        stopPostAnswerRecoveryAutomation();
        dispatchPostAnswerRecovery({ type: 'reset' });
        setPostAnswerRecoveryElapsedMs(0);
        await syncPilotInteractionState(selectedPhaseRef.current, {
          recovered: true,
        });
        setError(
          `Erro ao enviar resposta: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
      }
    },
    [
      activityId,
      api,
      hasActivePilotStreamTransport,
      messages.length,
      rememberSubmittedPendingInteraction,
      startPostAnswerRecoveryFlow,
      prepareSummaryResume,
      handleSendMessage,
      stopPostAnswerRecoveryAutomation,
      syncPilotInteractionState,
    ],
  );

  // Select approach from ApproachViewer (reuses answer_question endpoint)
  const handleSelectApproach = useCallback(
    async (approachTitle: string) => {
      if (!activityId) return;
      const hasLivePilotTransport = hasActivePilotStreamTransport();
      try {
        const result = await api.answerQuestion(activityId, {
          approach: approachTitle,
        });
        if (result.resumeMode === 'summary_required') {
          if (!result.resumeMessage) {
            throw new Error(
              'Resumo de retomada indisponível para esta interação.',
            );
          }
          setMessages(prev => [
            ...prev,
            {
              id: `system-summary-required-${Date.now()}`,
              activityId,
              phase: selectedPhase,
              role: 'system' as const,
              content:
                'Sessão anterior expirou. Retomando PRD com resumo do checkpoint...',
              timestamp: new Date().toISOString(),
            },
          ]);
          prepareSummaryResume();
          await handleSendMessage(result.resumeMessage, {
            optimisticUserMessage: false,
            bypassStreamingGuards: true,
          });
          return;
        }

        rememberSubmittedPendingInteraction(
          'approach',
          selectedPhaseRef.current,
        );
        dispatchStreamUi({ type: 'chat_clear_approaches' });
        setPersistedInteractionState(current =>
          current?.status === 'awaiting_user' && current.type === 'approach'
            ? null
            : current,
        );
        setPendingInteractionModalOpen(false);
        pilotRecoveryRequestedRef.current = !hasLivePilotTransport;
        setInteractionUiStatus(
          hasLivePilotTransport ? 'streaming' : 'recovering',
        );
        if (hasLivePilotTransport) {
          stopPostAnswerRecoveryAutomation();
          dispatchPostAnswerRecovery({ type: 'reset' });
          setPostAnswerRecoveryElapsedMs(0);
        } else {
          startPostAnswerRecoveryFlow(messages.length);
        }
      } catch (e) {
        stopPostAnswerRecoveryAutomation();
        dispatchPostAnswerRecovery({ type: 'reset' });
        setPostAnswerRecoveryElapsedMs(0);
        await syncPilotInteractionState(selectedPhaseRef.current, {
          recovered: true,
        });
        setError(
          `Erro ao selecionar abordagem: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
      }
    },
    [
      activityId,
      api,
      hasActivePilotStreamTransport,
      messages.length,
      rememberSubmittedPendingInteraction,
      startPostAnswerRecoveryFlow,
      prepareSummaryResume,
      handleSendMessage,
      stopPostAnswerRecoveryAutomation,
      syncPilotInteractionState,
    ],
  );

  // Approve current phase
  const handleApprovePhase = useCallback(async () => {
    if (!activityId || approving) return;
    setApproving(true);
    setError(undefined);
    try {
      const previousPhase = selectedPhaseRef.current;
      const updated = await api.approvePhase(activityId);
      clearLiveTransitionGuard();
      abortChatStream();
      abortRecoveryStream();
      flushPilotStreamChunkBuffer({ discard: true });
      releasePilotTabStream();
      dispatchStreamUi({ type: 'chat_interrupt' });
      dispatchStreamUi({ type: 'phase_changed' });
      dispatchPostAnswerRecovery({ type: 'reset' });
      setPostAnswerRecoveryElapsedMs(0);
      pilotRecoveryRequestedRef.current = false;
      postAnswerRecoveryActiveRef.current = false;
      postAnswerReattachInFlightRef.current = false;
      postAnswerReattachAttemptsRef.current = 0;
      if (postAnswerReattachTimerRef.current) {
        clearInterval(postAnswerReattachTimerRef.current);
        postAnswerReattachTimerRef.current = null;
      }
      pilotRecoveryStreamAttemptRef.current = null;
      pilotRecoveryEventReceivedRef.current = false;
      pilotPhaseSyncKeyRef.current = null;
      resetRecoveredReplayDedup();
      setPersistedInteractionState(null);
      persistedInteractionStateIdRef.current = null;
      submittedPendingInteractionRef.current = null;
      setPilotRecoveryRequested(false);
      setPendingInteractionModalOpen(false);
      setInteractionUiStatus('idle');
      setRecoverySnapshotContent('');
      setStableRecoverySnapshotContent('');
      clearPilotRecoverySnapshot(previousPhase);
      clearPilotReplayCursor();
      setCurrentTool(undefined);
      setToolCount(0);
      setActivity(updated);
      setSelectedPhase(updated.currentPhase);
      // Clear old phase messages immediately to avoid showing stale content
      // while the new phase's messages load from the backend.
      setMessages([]);
      await Promise.all([
        loadMessages(updated.currentPhase),
        loadMetrics(),
        loadArtifacts(),
      ]);
    } catch (e) {
      setError(
        `Erro ao aprovar fase: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setApproving(false);
    }
  }, [
    abortChatStream,
    abortRecoveryStream,
    activityId,
    api,
    approving,
    clearLiveTransitionGuard,
    clearPilotRecoverySnapshot,
    clearPilotReplayCursor,
    flushPilotStreamChunkBuffer,
    loadArtifacts,
    loadMessages,
    loadMetrics,
    releasePilotTabStream,
    resetRecoveredReplayDedup,
    setPilotRecoveryRequested,
  ]);

  const handlePhaseConformityRecovery = useCallback(async () => {
    const summary = activity?.phaseConformity;
    const recoveryView = getPhaseConformityRecoveryView(summary);
    if (
      !activityId ||
      !summary ||
      !recoveryView?.action ||
      revalidatingConformity
    ) {
      return;
    }
    setRevalidatingConformity(true);
    setError(undefined);
    try {
      const result = await api.revalidatePhaseConformity(
        activityId,
        summary.phaseAttemptId,
      );
      const refreshed = await api.getActivity(activityId);
      setActivity({
        ...refreshed,
        phaseConformity: refreshed.phaseConformity ?? result.phaseConformity,
      });
      setSelectedPhase(refreshed.currentPhase);
      setPhaseConformityRepairNotice(undefined);
    } catch (caughtError) {
      setError(
        `${recoveryView.action.errorPrefix}: ${
          caughtError instanceof Error
            ? caughtError.message
            : String(caughtError)
        }`,
      );
    } finally {
      setRevalidatingConformity(false);
    }
  }, [activity?.phaseConformity, activityId, api, revalidatingConformity]);

  const handleStartPhase = useCallback(async () => {
    if (!activity) return;

    // Save description before starting (replaces auto-save)
    if (
      selectedPhase === 'gerando-prd' &&
      activityId &&
      briefingDescription.trim()
    ) {
      try {
        await api.updateDescription(activityId, briefingDescription.trim());
        setActivity(prev =>
          prev ? { ...prev, description: briefingDescription.trim() } : prev,
        );
      } catch {
        // If save fails, continue with local value — activity.description
        // will be used by buildInitialMessage from the updated state above
      }
    }

    const phaseDefinition = pipelineDefinition.phases.find(
      phase => phase.id === selectedPhase,
    );
    if (
      phaseDefinition?.autoStart &&
      phaseDefinition.autoStartPolicy === 'frontend'
    ) {
      handleSendMessage(buildAutoStartMessage(phaseDefinition.skill), {
        optimisticUserMessage: false,
      });
      return;
    }
    // Use activity with latest description (updated by setActivity above)
    const updatedActivity = {
      ...activity,
      description: briefingDescription.trim() || activity.description,
    };
    const initialMessage = buildInitialMessage(
      updatedActivity,
      artifacts,
      selectedPhase,
    );
    // Hide the context message from the chat UI — it includes previous phase artifacts
    // (PRD, TechSpec) as context for the AI, not content the user needs to see.
    handleSendMessage(initialMessage, { optimisticUserMessage: false });
  }, [
    activity,
    activityId,
    api,
    artifacts,
    briefingDescription,
    handleSendMessage,
    pipelineDefinition.phases,
    selectedPhase,
  ]);

  // Reiniciar fase: chama backend para limpar mensagens e artefatos, depois limpa estado local.
  // Usado quando a fase gerando-prd/gerando-techspec não gerou artefato.
  const handleRestartPhase = useCallback(async () => {
    if (!activity || !activityId) return;
    const phaseToRestart = selectedPhase;
    setError(undefined);
    setRestarting(true);
    isRestartingRef.current = true;

    abortChatStream();
    abortRecoveryStream();
    stopHeartbeatWatchdog();
    stopPostAnswerRecoveryAutomation();
    flushPilotStreamChunkBuffer({ discard: true });
    releasePilotTabStream();
    clearLiveTransitionGuard();
    clearPilotReplayCursor();
    resetRecoveredReplayDedup();
    dispatchStreamUi({ type: 'chat_interrupt' });
    dispatchStreamUi({ type: 'phase_changed' });
    dispatchPostAnswerRecovery({ type: 'reset' });
    setPostAnswerRecoveryElapsedMs(0);
    pilotRecoveryRequestedRef.current = false;
    postAnswerRecoveryActiveRef.current = false;
    postAnswerReattachInFlightRef.current = false;
    postAnswerReattachAttemptsRef.current = 0;
    if (postAnswerReattachTimerRef.current) {
      clearInterval(postAnswerReattachTimerRef.current);
      postAnswerReattachTimerRef.current = null;
    }
    pilotRecoveryStreamAttemptRef.current = null;
    pilotRecoveryEventReceivedRef.current = false;
    pilotPhaseSyncKeyRef.current = null;
    submittedPendingInteractionRef.current = null;
    setMessages([]);
    setPersistedInteractionState(null);
    persistedInteractionStateIdRef.current = null;
    setPilotRecoveryRequested(false);
    setPendingInteractionModalOpen(false);
    setInteractionUiStatus('idle');
    setRecoverySnapshotContent('');
    setStableRecoverySnapshotContent('');
    clearCurrentDraft();
    clearPilotRecoverySnapshot(phaseToRestart);

    try {
      // 1. Chamar backend para limpar mensagens e artefatos da fase (+ abort worker)
      await api.restartPhase(activityId, phaseToRestart);
      // 2. Recarregar dados limpos antes de iniciar o novo run
      const updatedActivity = await loadActivity();
      await loadMessages(phaseToRestart);
      await loadArtifacts();

      const phaseDefinition = pipelineDefinition.phases.find(
        phase => phase.id === phaseToRestart,
      );
      const restartActivity =
        updatedActivity && updatedActivity.currentPhase === phaseToRestart
          ? updatedActivity
          : activity;
      const restartMessage =
        phaseDefinition?.autoStart &&
        phaseDefinition.autoStartPolicy === 'frontend'
          ? buildAutoStartMessage(phaseDefinition.skill)
          : buildInitialMessage(restartActivity, artifacts, phaseToRestart);

      isRestartingRef.current = false;
      setRestarting(false);
      await handleSendMessage(restartMessage, {
        optimisticUserMessage: false,
        bypassStreamingGuards: true,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setRestarting(false);
      isRestartingRef.current = false;
    }
  }, [
    activity,
    activityId,
    api,
    abortChatStream,
    abortRecoveryStream,
    artifacts,
    clearLiveTransitionGuard,
    clearCurrentDraft,
    clearPilotReplayCursor,
    clearPilotRecoverySnapshot,
    flushPilotStreamChunkBuffer,
    handleSendMessage,
    loadActivity,
    loadArtifacts,
    loadMessages,
    pipelineDefinition.phases,
    releasePilotTabStream,
    resetRecoveredReplayDedup,
    selectedPhase,
    setPilotRecoveryRequested,
    stopHeartbeatWatchdog,
    stopPostAnswerRecoveryAutomation,
  ]);

  const handleResumeFromCheckpoint = useCallback(async () => {
    if (!activityId) return;
    setError(undefined);
    await handleSendMessage('continuar', {
      bypassStreamingGuards: true,
    });
  }, [activityId, handleSendMessage]);

  const [retryingClone, setRetryingClone] = useState(false);
  const handleRetryClone = useCallback(async () => {
    if (!activityId || retryingClone) return;
    setRetryingClone(true);
    setError(undefined);
    try {
      await api.retryClone(activityId);
      const refreshed = await api.getActivity(activityId);
      setActivity(refreshed);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Falha ao reclonar repositório: ${msg}`);
    } finally {
      setRetryingClone(false);
    }
  }, [activityId, api, retryingClone]);

  // Reconectar após queda de SSE usando o coordinator unificado.
  const handleReconnect = useCallback(async () => {
    logPilotStreamEvent('stream_reconnect_requested', {
      streamKind: 'recovery',
      reason: 'manual_or_lifecycle',
    });
    await requestPilotRecoverySync('focus', {
      phase: selectedPhaseRef.current,
    });
  }, [logPilotStreamEvent, requestPilotRecoverySync]);

  useEffect(() => {
    handleReconnectRef.current = handleReconnect;
  }, [handleReconnect]);

  const handleRecoveryStatusAction = useCallback(() => {
    if (authoritativeSyncFailureState?.kind === 'persistent') {
      retryPostAnswerPollingRef.current?.();
      return;
    }

    void handleReconnect();
  }, [authoritativeSyncFailureState?.kind, handleReconnect]);

  useEffect(() => {
    if (!activityId || !activity) {
      return undefined;
    }

    const handleFocus = () => {
      if (
        document.visibilityState !== 'visible' ||
        activity.status !== 'active' ||
        !isPilotInteractionPhase(selectedPhaseRef.current)
      ) {
        return;
      }

      const workerStatusAtWake = getWorkerStatusWakeSnapshot();
      logPilotStreamEvent('stream_visibility_sync', {
        reason: 'window_focus',
        retryAttempts: 0,
        workerStatusAtWake,
      });

      if (
        shouldSuppressReactiveRecoveryTrigger('focus', selectedPhaseRef.current)
      ) {
        return;
      }

      void requestPilotRecoverySync('focus', {
        phase: selectedPhaseRef.current,
        workerStatusAtWake,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const workerStatusAtWake = getWorkerStatusWakeSnapshot();
        logPilotStreamEvent('stream_visibility_sync', {
          reason: 'visibilitychange_visible',
          retryAttempts: 0,
          workerStatusAtWake,
        });
        if (
          activity.status === 'active' &&
          isPilotInteractionPhase(selectedPhaseRef.current)
        ) {
          if (
            shouldSuppressReactiveRecoveryTrigger(
              'visibility_change',
              selectedPhaseRef.current,
            )
          ) {
            return;
          }
          void requestPilotRecoverySync('focus', {
            phase: selectedPhaseRef.current,
            workerStatusAtWake,
          });
        }
      }
    };

    const handleOnline = () => {
      const workerStatusAtWake = getWorkerStatusWakeSnapshot();
      logPilotStreamEvent('stream_visibility_sync', {
        reason: 'network_online',
        retryAttempts: 0,
        workerStatusAtWake,
      });
      if (
        activity.status === 'active' &&
        selectedPhaseRef.current === activity.currentPhase &&
        isPilotInteractionPhase(activity.currentPhase)
      ) {
        if (
          shouldSuppressReactiveRecoveryTrigger(
            'online',
            selectedPhaseRef.current,
          )
        ) {
          return;
        }
        void requestPilotRecoverySync('online', {
          phase: selectedPhaseRef.current,
        });
      }
    };

    const handleOffline = () => {
      if (isPilotInteractionPhase(selectedPhaseRef.current)) {
        setPilotRecoveryRequested(true);
        setInteractionUiStatus('recovering');
        logPilotStreamEvent(
          'stream_recovery_requested',
          {
            streamKind: 'recovery',
            reason: 'network_offline',
          },
          'warn',
        );
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [
    activity,
    activityId,
    getWorkerStatusWakeSnapshot,
    handleReconnect,
    logPilotStreamEvent,
    requestPilotRecoverySync,
    setPilotRecoveryRequested,
    shouldSuppressReactiveRecoveryTrigger,
  ]);

  useEffect(() => {
    if (
      !activityId ||
      !activity ||
      !isPilotTabPassive ||
      canReclaimPilotOwnership() ||
      activity.status !== 'active' ||
      selectedPhase !== activity.currentPhase ||
      !isPilotInteractionPhase(selectedPhase)
    ) {
      return undefined;
    }

    let cancelled = false;
    const syncPassiveTab = async () => {
      try {
        const updatedActivity = await loadActivity();
        if (cancelled) {
          return;
        }

        const phaseToSync =
          updatedActivity?.currentPhase ?? selectedPhaseRef.current;
        if (phaseToSync !== selectedPhaseRef.current) {
          setSelectedPhase(phaseToSync);
        }

        await Promise.all([
          loadMessages(phaseToSync),
          loadArtifacts(),
          loadMetrics(),
          syncPilotInteractionState(phaseToSync, {
            recovered: true,
          }),
        ]);

        refreshPilotTabOwnership();
      } catch {
        // Passive sync is best-effort.
      }
    };

    void syncPassiveTab();
    const interval = setInterval(() => {
      void syncPassiveTab();
    }, PILOT_RECOVERY_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    activity,
    activityId,
    canReclaimPilotOwnership,
    isPilotTabPassive,
    loadActivity,
    loadArtifacts,
    loadMessages,
    loadMetrics,
    refreshPilotTabOwnership,
    selectedPhase,
    syncPilotInteractionState,
  ]);

  useEffect(() => {
    const hasSettledPilotOutput =
      isPilotInteractionPhase(selectedPhase) &&
      hasPersistedAssistantOutputForPhase(selectedPhase) &&
      !streamUi.isStreaming &&
      !streamUi.pendingQuestions?.length &&
      !streamUi.pendingApproaches &&
      !pilotRecoveryStreamAbortRef.current;
    const shouldPollRecoveringPilot =
      !hasSettledPilotOutput &&
      interactionUiStatus === 'recovering' &&
      hasPilotRecoveryEvidence();
    const pollingRecoveredPilotStream =
      !hasSettledPilotOutput &&
      persistedInteractionState?.status === 'streaming' &&
      Boolean(pilotRecoveryStreamAbortRef.current) &&
      !pilotRecoveryEventReceivedRef.current &&
      (interactionUiStatus === 'recovering' ||
        interactionUiStatus === 'streaming') &&
      !streamUi.pendingQuestions?.length &&
      !streamUi.pendingApproaches;

    if (
      !activityId ||
      !activity ||
      selectedPhase !== activity.currentPhase ||
      !isPilotInteractionPhase(selectedPhase) ||
      (!shouldPollRecoveringPilot && !pollingRecoveredPilotStream)
    ) {
      if (pilotRecoveryPollRef.current) {
        clearInterval(pilotRecoveryPollRef.current);
        pilotRecoveryPollRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let pollCount = 0;

    const stopPolling = () => {
      if (pilotRecoveryPollRef.current) {
        clearInterval(pilotRecoveryPollRef.current);
        pilotRecoveryPollRef.current = null;
      }
    };

    const syncRecoveringPrd = async () => {
      if (cancelled) {
        return;
      }

      pollCount += 1;

      try {
        const updatedActivity = await loadActivity();
        if (cancelled) {
          return;
        }

        const phaseToSync =
          updatedActivity?.currentPhase ?? selectedPhaseRef.current;
        if (phaseToSync !== selectedPhaseRef.current) {
          setSelectedPhase(phaseToSync);
        }

        const interaction = await syncPilotInteractionState(phaseToSync, {
          markRecovering:
            shouldPollRecoveringPilot || !pilotRecoveryEventReceivedRef.current,
          recovered: true,
        });
        if (cancelled) {
          return;
        }

        const shouldHoldMessageSync =
          PILOT_INTERACTION_PHASES.includes(phaseToSync) &&
          (interaction?.status === 'streaming' ||
            interactionUiStatus === 'recovering' ||
            interactionUiStatus === 'streaming' ||
            shouldPollRecoveringPilot ||
            pollingRecoveredPilotStream);

        await Promise.all([
          shouldHoldMessageSync ? Promise.resolve() : loadMessages(phaseToSync),
          loadMetrics(),
          loadArtifacts(),
        ]);
        if (cancelled) {
          return;
        }

        if (
          phaseToSync !== selectedPhaseRef.current ||
          interaction?.status === 'awaiting_user' ||
          interaction?.status === 'completed' ||
          interaction?.status === 'expired' ||
          interaction?.status === 'canceled' ||
          pollCount >= PILOT_RECOVERY_POLL_MAX_ATTEMPTS
        ) {
          stopPolling();
        }
      } catch {
        if (pollCount >= PILOT_RECOVERY_POLL_MAX_ATTEMPTS) {
          stopPolling();
        }
      }
    };

    void syncRecoveringPrd();
    pilotRecoveryPollRef.current = setInterval(() => {
      void syncRecoveringPrd();
    }, PILOT_RECOVERY_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [
    activity,
    activityId,
    hasPilotRecoveryEvidence,
    hasPersistedAssistantOutputForPhase,
    interactionUiStatus,
    loadActivity,
    loadArtifacts,
    loadMessages,
    loadMetrics,
    persistedInteractionState?.status,
    selectedPhase,
    streamUi.pendingApproaches,
    streamUi.pendingQuestions,
    syncPilotInteractionState,
  ]);

  useEffect(() => {
    const resumeRequest =
      pilotRecoveryResumeRequest &&
      pilotRecoveryResumeRequest.interactionStateId ===
        persistedInteractionState?.id
        ? pilotRecoveryResumeRequest
        : null;
    const directlyResumablePersistedInteraction =
      persistedInteractionState &&
      (persistedInteractionState.status === 'streaming' ||
        persistedInteractionState.status === 'awaiting_user') &&
      persistedInteractionState.canResume &&
      persistedInteractionState.resumeMode === 'direct' &&
      !persistedInteractionState.terminalReason
        ? persistedInteractionState
        : null;
    const reattachAttemptKey =
      activityId && resumeRequest
        ? `${activityId}:${resumeRequest.interactionStateId}:${resumeRequest.token}`
        : null;
    const shouldReattachPilotStream =
      Boolean(activityId) &&
      activity?.status === 'active' &&
      selectedPhase === activity.currentPhase &&
      isPilotInteractionPhase(selectedPhase) &&
      workerConnected &&
      Boolean(directlyResumablePersistedInteraction) &&
      Boolean(resumeRequest);

    if (!shouldReattachPilotStream) {
      abortRecoveryStream();
      if (pilotRecoveryResumeRequest && !resumeRequest) {
        clearPilotRecoveryResume();
      }
      if (!directlyResumablePersistedInteraction) {
        pilotRecoveryStreamAttemptRef.current = null;
        pilotRecoveryEventReceivedRef.current = false;
        clearPilotRecoveryResume();
        if (!chatStreamAbortRef.current) {
          releasePilotTabStream();
        }
      }
      return undefined;
    }

    if (
      pilotRecoveryStreamAbortRef.current ||
      (reattachAttemptKey &&
        pilotRecoveryStreamAttemptRef.current === reattachAttemptKey)
    ) {
      return undefined;
    }

    if (!ensurePilotStreamOwnership('recovery')) {
      return undefined;
    }

    abortChatStream();
    setPhaseSyncWarning(undefined);
    pilotRecoveryStreamAttemptRef.current = reattachAttemptKey;
    pilotRecoveryEventReceivedRef.current = false;
    flushPilotStreamChunkBuffer({ discard: true });
    dispatchStreamUi({ type: 'chat_stop' });
    dispatchStreamUi({ type: 'chat_resume_stream' });
    setInteractionUiStatus('streaming');
    logPilotStreamEvent('stream_reattach_attempt', {
      streamKind: 'recovery',
      interactionStateId: resumeRequest?.interactionStateId ?? null,
      resumeToken: resumeRequest?.token ?? null,
      lastEventId: lastSeqIdRef.current || null,
    });
    void (async () => {
      try {
        await runRecoveryStream(async controller => {
          let recoveryTrigger: RecoveryTrigger = 'stream_terminal';
          let recoveryConflict: ResumeStreamConflict | undefined;
          let shouldRequestRecoverySync = false;

          startHeartbeatWatchdog();
          try {
            await api.resumeActivityStream(
              activityId!,
              (event: StreamEvent) => {
                onHeartbeat();
                handleRecoveredPilotStreamEventRef.current(event);
              },
              controller.signal,
              {
                onHeartbeat,
                onSeqId,
                lastEventId: lastSeqIdRef.current || undefined,
              },
            );
            logPilotStreamEvent('stream_reattach_success', {
              streamKind: 'recovery',
              interactionStateId: resumeRequest?.interactionStateId ?? null,
              resumeToken: resumeRequest?.token ?? null,
              lastEventId: lastSeqIdRef.current || null,
            });
          } catch (resumeError) {
            if (!controller.signal.aborted) {
              recoveryTrigger = 'reattach_failed';
              shouldRequestRecoverySync = true;
              recoveryConflict = isResumeActivityStreamError(resumeError)
                ? resumeError.conflict
                : {
                    error:
                      resumeError instanceof Error
                        ? resumeError.message
                        : String(resumeError),
                    retryable: true,
                  };
              logPilotStreamEvent(
                'stream_reattach_failed',
                {
                  streamKind: 'recovery',
                  interactionStateId: resumeRequest?.interactionStateId ?? null,
                  resumeToken: resumeRequest?.token ?? null,
                  error:
                    resumeError instanceof Error
                      ? resumeError.message
                      : String(resumeError),
                  reason: recoveryConflict?.reason ?? null,
                  retryable: recoveryConflict?.retryable ?? null,
                },
                'warn',
              );
            }
          } finally {
            stopHeartbeatWatchdog();

            if (controller.signal.aborted) {
              flushPilotStreamChunkBuffer({ discard: true });
              if (
                !chatStreamAbortRef.current &&
                pilotRecoveryStreamAbortRef.current === null
              ) {
                releasePilotTabStream();
              }
              return;
            }

            flushPilotStreamChunkBuffer();
            dispatchStreamUi({ type: 'chat_stop' });
            clearPilotRecoveryResume();
            pilotRecoveryStreamAttemptRef.current = null;

            if (!shouldRequestRecoverySync) {
              shouldRequestRecoverySync = true;
            }

            try {
              await requestPilotRecoverySyncRef.current?.({
                trigger: recoveryTrigger,
                phase: selectedPhaseRef.current,
                conflict: recoveryConflict,
                skipChatReconnect: true,
              });
            } catch (syncError) {
              logPilotStreamEvent(
                'stream_sync_failed',
                {
                  trigger: recoveryTrigger,
                  error:
                    syncError instanceof Error
                      ? syncError.message
                      : String(syncError),
                },
                'warn',
              );
            }
          }
        });
      } catch (transportError) {
        logPilotStreamEvent(
          'stream_transport_error',
          {
            streamKind: 'recovery',
            error:
              transportError instanceof Error
                ? transportError.message
                : String(transportError),
          },
          'error',
        );
      }
    })();

    return () => {
      abortRecoveryStream();
      flushPilotStreamChunkBuffer({ discard: true });
    };
  }, [
    activity?.currentPhase,
    activity?.status,
    activityId,
    api,
    abortChatStream,
    abortRecoveryStream,
    clearPilotRecoveryResume,
    ensurePilotStreamOwnership,
    flushPilotStreamChunkBuffer,
    logPilotStreamEvent,
    onHeartbeat,
    onSeqId,
    persistedInteractionState?.canResume,
    persistedInteractionState?.id,
    persistedInteractionState?.resumeMode,
    persistedInteractionState?.status,
    persistedInteractionState?.terminalReason,
    pilotRecoveryResumeRequest,
    releasePilotTabStream,
    runRecoveryStream,
    selectedPhase,
    startHeartbeatWatchdog,
    stopHeartbeatWatchdog,
    workerConnected,
  ]);

  // Track phase changes and queue deterministic frontend auto-start phases.
  useEffect(() => {
    if (!activity) return;
    if (lastSeenPhaseRef.current === activity.currentPhase) return;
    lastSeenPhaseRef.current = activity.currentPhase;

    const phaseDefinition = pipelineDefinition.phases.find(
      phase => phase.id === activity.currentPhase,
    );

    if (
      activity.status === 'active' &&
      phaseDefinition?.autoStart &&
      phaseDefinition.autoStartPolicy === 'frontend'
    ) {
      setPendingFrontendAutoStartPhase(activity.currentPhase);
    }
  }, [activity, pipelineDefinition.phases]);

  // Execute deterministic auto-start only when the phase is stable and ready.
  useEffect(() => {
    if (!pendingFrontendAutoStartPhase || !activityId || !activity) return;
    if (activity.status !== 'active') {
      setPendingFrontendAutoStartPhase(null);
      return;
    }
    if (selectedPhase !== activity.currentPhase) return;
    if (pendingFrontendAutoStartPhase !== activity.currentPhase) return;
    if (streamUi.isStreaming || streamUi.isTaskStreaming) return;
    if (
      (streamUi.pendingQuestions?.length ?? 0) > 0 ||
      streamUi.pendingApproaches
    )
      return;

    const phaseDefinition = pipelineDefinition.phases.find(
      phase => phase.id === pendingFrontendAutoStartPhase,
    );
    if (
      !phaseDefinition?.autoStart ||
      phaseDefinition.autoStartPolicy !== 'frontend'
    ) {
      setPendingFrontendAutoStartPhase(null);
      return;
    }

    let cancelled = false;
    const ensureProntoPhaseIsBackendConfirmed = async (): Promise<boolean> => {
      if (pendingFrontendAutoStartPhase !== 'pronto') {
        return true;
      }

      let latestBackendActivity: Activity | undefined;

      for (const waitMs of PRONTO_PHASE_SYNC_RETRY_DELAYS_MS) {
        if (waitMs > 0) {
          await delay(waitMs);
        }

        if (cancelled) return false;

        try {
          const backendActivity = await api.getActivity(activityId);
          if (cancelled) return false;

          latestBackendActivity = backendActivity;

          if (backendActivity.currentPhase === 'pronto') {
            setActivity(prev => {
              if (
                prev &&
                prev.currentPhase === backendActivity.currentPhase &&
                prev.status === backendActivity.status &&
                prev.updatedAt === backendActivity.updatedAt
              ) {
                return prev;
              }
              return backendActivity;
            });
            setSelectedPhase('pronto');
            setPhaseSyncWarning(undefined);
            return true;
          }
        } catch {
          // Retry on transient backend fetch errors.
        }
      }

      if (cancelled) return false;

      if (latestBackendActivity) {
        setActivity(prev => {
          if (
            prev &&
            prev.currentPhase === latestBackendActivity.currentPhase &&
            prev.status === latestBackendActivity.status &&
            prev.updatedAt === latestBackendActivity.updatedAt
          ) {
            return prev;
          }
          return latestBackendActivity;
        });
        setSelectedPhase(latestBackendActivity.currentPhase);
        setPhaseSyncWarning(
          'A fase local estava em Pronto, mas o backend ainda não confirmou a transição. O estado foi atualizado a partir do backend.',
        );
        await Promise.all([
          loadMessages(latestBackendActivity.currentPhase),
          loadMetrics(),
          loadArtifacts(),
        ]);
      }

      setError(
        'Não foi possível iniciar automaticamente a fase Pronto neste momento. Aguarde a atualização do estado e tente novamente.',
      );
      return false;
    };

    const startFrontendPhase = async () => {
      try {
        const prontoPhaseConfirmed =
          await ensureProntoPhaseIsBackendConfirmed();
        // When cancelled (effect re-run due to dep change), preserve pending state
        // so the next effect invocation can retry the auto-start.
        if (cancelled) return;
        if (!prontoPhaseConfirmed) {
          setPendingFrontendAutoStartPhase(null);
          return;
        }

        const phaseMessages = await api.getMessages(
          activityId,
          pendingFrontendAutoStartPhase,
        );
        if (cancelled) return;
        setMessages(phaseMessages);

        // Messages already exist: avoid duplicate auto-start and keep manual flow.
        if (phaseMessages.length > 0) {
          setPendingFrontendAutoStartPhase(null);
          return;
        }

        setPendingFrontendAutoStartPhase(null);
        await handleSendMessage(buildAutoStartMessage(phaseDefinition.skill), {
          optimisticUserMessage: false,
        });
      } catch {
        if (cancelled) return;
        // On transient failures, clear pending state and keep manual fallback.
        setPendingFrontendAutoStartPhase(null);
      }
    };

    void startFrontendPhase();

    return () => {
      cancelled = true;
    };
  }, [
    activity,
    activityId,
    api,
    handleSendMessage,
    loadArtifacts,
    loadMessages,
    loadMetrics,
    pendingFrontendAutoStartPhase,
    pipelineDefinition.phases,
    selectedPhase,
    streamUi.pendingApproaches,
    streamUi.isStreaming,
    streamUi.isTaskStreaming,
    streamUi.pendingQuestions,
  ]);

  // Load persisted task plan before approval and during execution/QA.
  useEffect(() => {
    if (!activityId) return;
    if (
      selectedPhase !== 'dividindo-tasks' &&
      selectedPhase !== 'executando' &&
      selectedPhase !== 'qa'
    )
      return;
    void loadExecutionState();
  }, [activityId, loadExecutionState, selectedPhase]);

  // Recover QA auto-execution status on refresh (snapshot + short polling)
  useEffect(() => {
    if (!activityId || !activity) return undefined;
    if (selectedPhase !== 'qa' || activity.currentPhase !== 'qa')
      return undefined;
    if (streamUi.executionState !== 'running') return undefined;
    if (streamUi.isTaskStreaming) return undefined;

    let cancelled = false;
    const pollInterval = setInterval(async () => {
      if (cancelled) return;
      try {
        const updatedActivity = await api.getActivity(activityId);
        if (cancelled) return;

        setActivity(prev => {
          if (!prev) return updatedActivity;
          if (
            prev.currentPhase === updatedActivity.currentPhase &&
            prev.status === updatedActivity.status &&
            prev.updatedAt === updatedActivity.updatedAt
          ) {
            return prev;
          }
          return updatedActivity;
        });

        if (updatedActivity.currentPhase !== 'qa') {
          clearInterval(pollInterval);
          setSelectedPhase(updatedActivity.currentPhase);
          await Promise.all([
            loadMessages(updatedActivity.currentPhase),
            loadMetrics(),
            loadArtifacts(),
          ]);
          return;
        }

        await Promise.all([loadMessages('qa'), loadMetrics(), loadArtifacts()]);

        const execution = await loadExecutionState();
        if (execution?.executionState !== 'running') {
          clearInterval(pollInterval);
        }
      } catch {
        // Keep polling on transient failures
      }
    }, 2500);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [
    activity,
    activityId,
    api,
    loadArtifacts,
    loadExecutionState,
    loadMessages,
    loadMetrics,
    selectedPhase,
    streamUi.executionState,
    streamUi.isTaskStreaming,
  ]);

  // Fallback: detect stuck "Executando" phase when all tasks completed but
  // SSE transition events were lost. Polls backend to check real phase.
  useEffect(() => {
    if (!activityId || !activity) return undefined;
    if (selectedPhase !== 'executando') return undefined;
    if (activity.currentPhase !== 'executando') return undefined;
    if (streamUi.tasks.length === 0) return undefined;

    const allCompleted =
      streamUi.tasks.length > 0 &&
      streamUi.tasks.every(t => t.status === 'completed');
    if (!allCompleted) return undefined;

    let cancelled = false;
    // Wait a short grace period for SSE events to arrive, then poll
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const updatedActivity = await api.getActivity(activityId);
        if (cancelled) return;

        if (updatedActivity.currentPhase !== 'executando') {
          // Backend already transitioned — sync frontend
          // eslint-disable-next-line no-console
          console.warn(
            `[fallback-sync] Backend phase is "${updatedActivity.currentPhase}" but frontend stuck at "executando". Syncing...`,
          );
          dispatchStreamUi({ type: 'task_stream_stop' });
          dispatchStreamUi({ type: 'chat_stop' });
          setActivity(updatedActivity);
          setSelectedPhase(updatedActivity.currentPhase);
          setMessages([]);
          await Promise.all([
            loadExecutionState(),
            loadMessages(updatedActivity.currentPhase),
            loadMetrics(),
            loadArtifacts(),
          ]);
        }
      } catch {
        // Ignore transient errors; SSE may still deliver the event
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activity,
    activityId,
    api,
    loadArtifacts,
    loadExecutionState,
    loadMessages,
    loadMetrics,
    selectedPhase,
    streamUi.tasks,
  ]);

  // Poll task statuses when SSE stream dropped but worker may still be running.
  // This catches the case where the SSE connection closes (timeout, network)
  // while the worker continues executing tasks in the background.
  useEffect(() => {
    if (!activityId || !activity) return undefined;
    if (selectedPhase !== 'executando') return undefined;
    if (activity.currentPhase !== 'executando') return undefined;
    // Only poll when SSE is NOT active
    if (streamUi.isTaskStreaming) return undefined;
    // Only poll when there are incomplete tasks
    const hasIncomplete = streamUi.tasks.some(
      t => t.status === 'running' || t.status === 'pending',
    );
    if (streamUi.tasks.length === 0 || !hasIncomplete) return undefined;

    let cancelled = false;
    const pollInterval = setInterval(async () => {
      if (cancelled) return;
      try {
        const result = await api.getTasks(activityId);
        if (cancelled) return;

        dispatchStreamUi({
          type: 'tasks_loaded',
          tasks: result.tasks,
          executionState: result.executionState,
        });

        // Check if phase changed (e.g. execution completed while disconnected)
        const updatedActivity = await api.getActivity(activityId);
        if (cancelled) return;

        if (updatedActivity.currentPhase !== 'executando') {
          clearInterval(pollInterval);
          setActivity(updatedActivity);
          setSelectedPhase(updatedActivity.currentPhase);
          setMessages([]);
          await Promise.all([
            loadMessages(updatedActivity.currentPhase),
            loadMetrics(),
            loadArtifacts(),
          ]);
        }
      } catch {
        // Ignore transient errors; retry on next interval
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [
    activity,
    activityId,
    api,
    loadArtifacts,
    loadMessages,
    loadMetrics,
    selectedPhase,
    streamUi.isTaskStreaming,
    streamUi.tasks,
  ]);

  // Recovery polling: when frontend is idle (no streaming) but activity is still
  // active, periodically check backend for phase changes AND new messages the
  // SSE may have missed (e.g. worker continued after a network disconnect,
  // or the user answered a question after SSE dropped).
  const recoveryMessageCountRef = useRef(0);
  useEffect(() => {
    if (!activityId || !activity) return undefined;
    if (activity.status !== 'active') return undefined;
    if (
      isPilotInteractionPhase(selectedPhase) &&
      hasPersistedAssistantOutputForPhase(selectedPhase) &&
      !streamUi.isStreaming &&
      !streamUi.pendingQuestions?.length &&
      !streamUi.pendingApproaches &&
      !pilotRecoveryStreamAbortRef.current
    ) {
      return undefined;
    }
    // Only poll when nothing is streaming
    if (streamUi.isStreaming || streamUi.isTaskStreaming) return undefined;

    // Track current message count to detect new messages from backend
    recoveryMessageCountRef.current = messages.filter(
      message => message.phase === selectedPhaseRef.current,
    ).length;

    let cancelled = false;
    const pollInterval = setInterval(async () => {
      if (cancelled) return;
      try {
        const updated = await api.getActivity(activityId);
        if (cancelled) return;

        // Phase or status changed — full sync
        if (
          updated.currentPhase !== activity.currentPhase ||
          updated.status !== activity.status
        ) {
          setActivity(updated);
          setSelectedPhase(updated.currentPhase);
          await Promise.all([
            loadMessages(updated.currentPhase),
            loadMetrics(),
            loadArtifacts(),
            loadExecutionState(),
          ]);
          return;
        }

        if (!areActivitySnapshotsEqual(activity, updated)) {
          setActivity(updated);
        }

        let pilotInteraction: InteractionState | null = null;
        if (isPilotInteractionPhase(selectedPhaseRef.current)) {
          pilotInteraction = await syncPilotInteractionState(
            selectedPhaseRef.current,
            {
              recovered: true,
            },
          );
          if (cancelled) return;
          if (pilotInteraction?.status === 'expired') {
            return;
          }
        }

        const isPilotRecoveryActive =
          isPilotInteractionPhase(selectedPhaseRef.current) &&
          (pilotInteraction?.status === 'streaming' ||
            interactionUiStatus === 'recovering' ||
            interactionUiStatus === 'streaming' ||
            persistedInteractionState?.status === 'streaming' ||
            Boolean(pilotRecoveryStreamAbortRef.current) ||
            pilotRecoveryRequestedRef.current);
        if (isPilotRecoveryActive) {
          return;
        }

        // Phase unchanged — check for new messages (covers SSE-dropped scenarios
        // where worker persisted responses the frontend never received).
        const currentPhaseMessages = await api.getMessages(
          activityId,
          selectedPhaseRef.current,
        );
        if (cancelled) return;
        let hasMessagesChanged = false;
        setMessages(previousMessages => {
          if (areChatMessagesEqual(previousMessages, currentPhaseMessages)) {
            return previousMessages;
          }
          hasMessagesChanged = true;
          return currentPhaseMessages;
        });
        recoveryMessageCountRef.current = currentPhaseMessages.length;
        if (hasMessagesChanged) {
          // Clear stale streaming content since persisted messages are now loaded
          if (streamUi.streamingContent) {
            dispatchStreamUi({ type: 'chat_stop' });
          }
          // Reload both metrics AND artifacts: the worker may have called
          // register_artifact during the dropped SSE session (e.g. PRD/TechSpec done),
          // so artifacts must be refreshed to unlock the approve button.
          await Promise.all([loadMetrics(), loadArtifacts()]);
        }
      } catch {
        // Ignore transient errors
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [
    activity,
    activityId,
    api,
    hasPersistedAssistantOutputForPhase,
    loadArtifacts,
    loadExecutionState,
    loadMessages,
    loadMetrics,
    messages.length,
    selectedPhase,
    streamUi.pendingApproaches,
    streamUi.pendingQuestions,
    streamUi.isStreaming,
    streamUi.isTaskStreaming,
    streamUi.streamingContent,
    interactionUiStatus,
    persistedInteractionState?.status,
    syncPilotInteractionState,
  ]);

  // Avoid carrying stale streaming text into unrelated phases.
  // Skip for QA (polling-based) and Pronto (phase_complete via chat_complete).
  // These phases manage their own streaming lifecycle; cleanup happens in their
  // respective finally blocks.
  // Also skip when answer polling is active — the partial AI response must stay
  // visible until the full persisted response arrives from the backend.
  useEffect(() => {
    // Artifact/review phases use chat_complete to preserve streamingContent as
    // fallback display content while the backend persists the final assistant
    // message. Tasks also needs this gap covered: task plan registration can
    // unlock backend state before the final tasks.md text is visible.
    if (
      selectedPhase === 'qa' ||
      selectedPhase === 'pronto' ||
      selectedPhase === 'gerando-prd' ||
      selectedPhase === 'gerando-techspec' ||
      selectedPhase === 'dividindo-tasks'
    )
      return;
    if (streamUi.isStreaming) return;
    if (!streamUi.streamingContent) return;
    if (answerPollRef.current) return;
    dispatchStreamUi({ type: 'chat_stop' });
  }, [selectedPhase, streamUi.isStreaming, streamUi.streamingContent]);

  // Sincroniza estado após conclusão de execução (transição automática para QA)
  const syncAfterExecutionComplete = useCallback(async () => {
    dispatchStreamUi({ type: 'task_stream_stop' });
    dispatchStreamUi({ type: 'chat_stop' });

    try {
      const updatedActivity = await loadActivity();
      if (updatedActivity) {
        setSelectedPhase(updatedActivity.currentPhase);
        await loadExecutionState();
        await Promise.all([
          loadMessages(updatedActivity.currentPhase),
          loadMetrics(),
          loadArtifacts(),
        ]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erro ao sincronizar após execution_complete:', err);
    }
  }, [
    loadActivity,
    loadArtifacts,
    loadExecutionState,
    loadMessages,
    loadMetrics,
  ]);

  // Process task SSE events
  const processTaskSSEEvent = useCallback(
    (event: StreamEvent) => {
      switch (event.type) {
        case 'tasks_discovered':
          if (event.tasks) {
            dispatchStreamUi({ type: 'tasks_discovered', tasks: event.tasks });
          }
          break;
        case 'task_started':
          if (event.taskNumber) {
            if (
              event.parallelSlot !== undefined &&
              event.totalSlots !== undefined
            ) {
              dispatchStreamUi({
                type: 'task_parallel_start',
                taskNumber: event.taskNumber,
                taskTitle: event.taskTitle,
                slot: event.parallelSlot,
                totalSlots: event.totalSlots,
              });
            } else {
              dispatchStreamUi({
                type: 'task_started',
                taskNumber: event.taskNumber,
                taskTitle: event.taskTitle,
              });
            }
          }
          break;
        case 'task_progress':
          if (event.taskNumber === 'qa') {
            dispatchStreamUi({
              type: 'qa_progress',
              content: event.content,
              taskChunkType: event.taskChunkType,
            });
            break;
          }
          if (event.parallelSlot !== undefined && event.taskNumber) {
            dispatchStreamUi({
              type: 'task_parallel_progress',
              taskNumber: event.taskNumber,
              content: event.content,
              taskChunkType: event.taskChunkType,
            });
          } else {
            dispatchStreamUi({
              type: 'task_progress',
              content: event.content,
              taskChunkType: event.taskChunkType,
            });
          }
          break;
        case 'task_completed':
          if (event.taskNumber) {
            if (event.parallelSlot !== undefined) {
              dispatchStreamUi({
                type: 'task_parallel_complete',
                taskNumber: event.taskNumber,
              });
            } else {
              dispatchStreamUi({
                type: 'task_completed',
                taskNumber: event.taskNumber,
              });
            }
            void loadMetrics();
          }
          break;
        case 'task_integrated':
          if (event.taskNumber) {
            dispatchStreamUi({
              type: 'task_integrated',
              taskNumber: event.taskNumber,
              slot: event.parallelSlot,
              totalSlots: event.totalSlots,
              worktreePath: event.worktreePath,
            });
          }
          break;
        case 'task_error':
          if (event.parallelSlot !== undefined && event.taskNumber) {
            dispatchStreamUi({
              type: 'task_parallel_error',
              taskNumber: event.taskNumber,
              errorMessage: event.errorMessage ?? event.content,
            });
          } else {
            dispatchStreamUi({
              type: 'task_error',
              taskNumber: event.taskNumber,
              errorMessage: event.errorMessage ?? event.content,
            });
          }
          if (event.taskNumber === 'qa') {
            dispatchStreamUi({ type: 'chat_complete' });
            dispatchStreamUi({ type: 'task_stream_stop' });
          }
          setError(event.content);
          void loadMetrics();
          break;
        case 'review_started':
          dispatchStreamUi({ type: 'review_loading' });
          break;
        case 'review_result':
          if (event.review) {
            dispatchStreamUi({ type: 'review_result', result: event.review });
          } else {
            try {
              const parsed = JSON.parse(event.content);
              dispatchStreamUi({ type: 'review_result', result: parsed });
            } catch {
              // skip malformed review result
            }
          }
          break;
        case 'execution_complete':
          // Sincroniza estado com backend (padrão AI-First)
          syncAfterExecutionComplete();
          break;
        case 'execution_paused':
          dispatchStreamUi({
            type: 'execution_state',
            executionState: 'paused',
          });
          dispatchStreamUi({ type: 'task_stream_stop' });
          break;
        case 'phase_auto_transition':
          dispatchStreamUi({ type: 'task_stream_stop' });
          // Use phase_changed instead of chat_complete to reset flags cleanly.
          // handleSharedSSEEvent will also dispatch phase_changed for the new phase.
          dispatchStreamUi({ type: 'phase_changed' });
          handleSharedSSEEvent(event);
          break;
        case 'done':
          // Reload metrics to capture QA costs persisted by backend just before
          // closing the stream (persistQaAutoStartResults runs before done event).
          void loadMetrics();
          break;
        default:
          handleSharedSSEEvent(event);
          return;
      }
    },
    [loadMetrics, syncAfterExecutionComplete, handleSharedSSEEvent],
  );

  const handleStartExecution = useCallback(async () => {
    if (!activityId) return;
    dispatchStreamUi({ type: 'task_stream_start' });
    dispatchStreamUi({ type: 'execution_state', executionState: 'running' });
    try {
      await api.startExecution(activityId, processTaskSSEEvent);
    } catch (e) {
      setError(
        `Erro ao iniciar execução: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
      dispatchStreamUi({ type: 'execution_state', executionState: 'idle' });
    } finally {
      dispatchStreamUi({ type: 'task_stream_stop' });
      // Reload metrics, tasks, and activity after stream ends to catch
      // updates persisted by the backend just before the SSE closed.
      await loadMetrics();
      const executionResult = await loadExecutionState();
      const updated = await loadActivity();
      if (updated && updated.currentPhase !== selectedPhaseRef.current) {
        setSelectedPhase(updated.currentPhase);
        await Promise.all([
          loadMessages(updated.currentPhase),
          loadArtifacts(),
          loadExecutionState(),
        ]);
      } else if (executionResult) {
        // SSE ended but phase didn't change — sync execution state from backend
        // so the UI reflects idle/running/paused correctly (e.g. after SSE timeout).
        dispatchStreamUi({
          type: 'execution_state',
          executionState: executionResult.executionState,
        });
      }
    }
  }, [
    activityId,
    api,
    processTaskSSEEvent,
    loadMetrics,
    loadActivity,
    loadMessages,
    loadArtifacts,
    loadExecutionState,
  ]);

  const handlePause = useCallback(async () => {
    if (!activityId) return;
    try {
      await api.pauseExecution(activityId);
      // Don't set paused optimistically -- trust backend state.
      // The SSE stream will emit execution_paused event, or we poll.
      const result = await loadExecutionState();
      if (result) {
        dispatchStreamUi({
          type: 'execution_state',
          executionState: result.executionState,
        });
      }
    } catch (e) {
      setError(
        `Erro ao pausar execução: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    }
  }, [activityId, api, loadExecutionState]);

  const handleResume = useCallback(async () => {
    if (!activityId) return;
    dispatchStreamUi({ type: 'task_stream_start' });
    dispatchStreamUi({ type: 'execution_state', executionState: 'running' });
    try {
      await api.resumeExecution(activityId, processTaskSSEEvent);
    } catch (e) {
      setError(
        `Erro ao retomar execução: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
      dispatchStreamUi({ type: 'execution_state', executionState: 'idle' });
    } finally {
      dispatchStreamUi({ type: 'task_stream_stop' });
    }
  }, [activityId, api, processTaskSSEEvent]);

  const handleRetry = useCallback(
    async (taskNumber: string) => {
      if (!activityId) return;
      dispatchStreamUi({ type: 'task_stream_start' });
      dispatchStreamUi({ type: 'execution_state', executionState: 'running' });
      try {
        await api.retryTask(activityId, taskNumber, processTaskSSEEvent);
      } catch (e) {
        setError(
          `Erro ao retentar tarefa: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
        dispatchStreamUi({ type: 'execution_state', executionState: 'idle' });
      } finally {
        dispatchStreamUi({ type: 'task_stream_stop' });
      }
    },
    [activityId, api, processTaskSSEEvent],
  );

  useEffect(() => {
    if (!activityId || !isPilotInteractionPhase(selectedPhase)) {
      return;
    }

    const hasPersistedAssistantOutput = messages.some(
      message =>
        message.phase === selectedPhase &&
        !isHiddenSystemUserMessage(message) &&
        message.role === 'assistant' &&
        message.content.trim().length > 0,
    );
    const hasApprovedArtifact = artifacts.some(
      artifact =>
        artifact.phase === selectedPhase && artifact.content.trim().length > 0,
    );

    if (
      hasPersistedAssistantOutput ||
      hasApprovedArtifact ||
      interactionUiStatus === 'completed' ||
      interactionUiStatus === 'expired'
    ) {
      clearPilotRecoverySnapshot(selectedPhase);
    }
  }, [
    activityId,
    artifacts,
    clearPilotRecoverySnapshot,
    interactionUiStatus,
    messages,
    selectedPhase,
  ]);

  const isPrdReadingPreviewMode =
    viewMode === 'reading' && selectedPhase === 'gerando-prd';
  const prdReadingRecoveryBaseline =
    stableRecoverySnapshotContent || recoverySnapshotContent;
  const prdReadingStableDraftContent = isPilotInteractionPhase(selectedPhase)
    ? mergeRecoveryStreamingContent(
        prdReadingRecoveryBaseline,
        streamUi.streamingContent,
      )
    : streamUi.streamingContent;
  const prdReadingDraftArtifactMessage = [...messages]
    .reverse()
    .find(
      message =>
        message.phase === selectedPhase &&
        message.role === 'assistant' &&
        message.content.trim().length > 0,
    );
  const prdReadingDraftContentFromMessages = sanitizeArtifactDraftContent(
    prdReadingDraftArtifactMessage?.content ??
      prdReadingStableDraftContent ??
      '',
  );
  const prdReadingSelectedArtifact = selectPreferredArtifactForPhase(
    artifacts,
    selectedPhase,
  );
  const prdReadingRawContent =
    prdReadingSelectedArtifact?.content ?? prdReadingDraftContentFromMessages;
  const prdReadingDisplayContent =
    extractReadablePrdArtifactContent(prdReadingRawContent);
  const techSpecReadingRecoveryBaseline =
    stableRecoverySnapshotContent || recoverySnapshotContent;
  const techSpecReadingStableDraftContent = isPilotInteractionPhase(
    selectedPhase,
  )
    ? mergeRecoveryStreamingContent(
        techSpecReadingRecoveryBaseline,
        streamUi.streamingContent,
      )
    : streamUi.streamingContent;
  const techSpecReadingDraftArtifactMessage = [...messages]
    .reverse()
    .find(
      message =>
        message.phase === 'gerando-techspec' &&
        message.role === 'assistant' &&
        message.content.trim().length > 0,
    );
  const techSpecReadingDraftContentFromMessages = sanitizeArtifactDraftContent(
    techSpecReadingDraftArtifactMessage?.content ??
      techSpecReadingStableDraftContent ??
      '',
  );
  const techSpecReadingSelectedArtifact = selectPreferredArtifactForPhase(
    artifacts,
    'gerando-techspec',
  );
  const techSpecReadingRawContent =
    techSpecReadingSelectedArtifact?.content ??
    techSpecReadingDraftContentFromMessages;
  const techSpecReadingDisplayContent = extractReadableTechSpecContent(
    techSpecReadingRawContent,
  );
  const taskPlanReadingRecoveryBaseline =
    stableRecoverySnapshotContent || recoverySnapshotContent;
  const taskPlanReadingStableDraftContent = isPilotInteractionPhase(
    selectedPhase,
  )
    ? mergeRecoveryStreamingContent(
        taskPlanReadingRecoveryBaseline,
        streamUi.streamingContent,
      )
    : streamUi.streamingContent;
  const taskPlanReadingDraftArtifactMessage = [...messages]
    .reverse()
    .find(
      message =>
        message.phase === 'dividindo-tasks' &&
        message.role === 'assistant' &&
        message.content.trim().length > 0,
    );
  const taskPlanReadingSelectedArtifact = selectPreferredArtifactForPhase(
    artifacts,
    'dividindo-tasks',
  );
  const taskPlanReadingRawContent =
    taskPlanReadingSelectedArtifact?.content ??
    taskPlanReadingDraftArtifactMessage?.content ??
    taskPlanReadingStableDraftContent ??
    '';
  const taskPlanReadingSummarizedDisplay = summarizeToolUseMarkersForDisplay(
    taskPlanReadingRawContent,
  );
  const taskPlanReadingDisplayContent = sanitizeArtifactDraftContent(
    taskPlanReadingSummarizedDisplay.content,
  );
  const canEditPrdReadingBeforeGuards =
    isPrdReadingPreviewMode &&
    activity?.currentPhase === selectedPhase &&
    activity?.status === 'active' &&
    !streamUi.isStreaming &&
    !streamUi.isTaskStreaming;

  const isTechSpecReadingPreviewMode =
    viewMode === 'reading' && selectedPhase === 'gerando-techspec';
  const canEditTechSpecReadingBeforeGuards =
    isTechSpecReadingPreviewMode &&
    activity?.currentPhase === 'gerando-techspec' &&
    activity?.status === 'active' &&
    !streamUi.isStreaming &&
    !streamUi.isTaskStreaming;

  useEffect(() => {
    if (!isPrdReadingPreviewMode) {
      setIsPrdReadingEditing(false);
      setPrdReadingDraftContent('');
      return;
    }

    if (!isPrdReadingEditing) {
      setPrdReadingDraftContent(prdReadingDisplayContent);
    }
  }, [isPrdReadingEditing, isPrdReadingPreviewMode, prdReadingDisplayContent]);

  useEffect(() => {
    if (
      !canEditPrdReadingBeforeGuards &&
      isPrdReadingEditing &&
      !isPrdReadingSaving
    ) {
      setPrdReadingDraftContent(prdReadingDisplayContent);
      setIsPrdReadingEditing(false);
    }
  }, [
    canEditPrdReadingBeforeGuards,
    isPrdReadingEditing,
    isPrdReadingSaving,
    prdReadingDisplayContent,
  ]);

  useEffect(() => {
    if (!isTechSpecReadingPreviewMode) {
      setIsTechSpecReadingEditing(false);
      setTechSpecReadingDraftContent('');
      return;
    }

    if (!isTechSpecReadingEditing) {
      setTechSpecReadingDraftContent(techSpecReadingDisplayContent);
    }
  }, [
    isTechSpecReadingEditing,
    isTechSpecReadingPreviewMode,
    techSpecReadingDisplayContent,
  ]);

  useEffect(() => {
    if (
      !canEditTechSpecReadingBeforeGuards &&
      isTechSpecReadingEditing &&
      !isTechSpecReadingSaving
    ) {
      setTechSpecReadingDraftContent(techSpecReadingDisplayContent);
      setIsTechSpecReadingEditing(false);
    }
  }, [
    canEditTechSpecReadingBeforeGuards,
    isTechSpecReadingEditing,
    isTechSpecReadingSaving,
    techSpecReadingDisplayContent,
  ]);

  const handleStartPrdReadingEdit = useCallback(() => {
    if (!canEditPrdReadingBeforeGuards) {
      return;
    }
    setPrdReadingDraftContent(prdReadingDisplayContent);
    setIsPrdReadingEditing(true);
  }, [canEditPrdReadingBeforeGuards, prdReadingDisplayContent]);

  const handleCancelPrdReadingEdit = useCallback(() => {
    setPrdReadingDraftContent(prdReadingDisplayContent);
    setIsPrdReadingEditing(false);
  }, [prdReadingDisplayContent]);

  const handleSavePrdReadingEdit = useCallback(async () => {
    if (!activityId || !canEditPrdReadingBeforeGuards || isPrdReadingSaving) {
      return;
    }

    const content = prdReadingDraftContent;
    if (content.trim().length === 0) {
      setError('O conteúdo do PRD não pode ficar vazio.');
      return;
    }

    setIsPrdReadingSaving(true);
    setError(undefined);
    try {
      await api.savePrdDraft(activityId, content);
      await loadMessages('gerando-prd');
      setIsPrdReadingEditing(false);
    } catch (e) {
      setError(
        `Erro ao salvar PRD: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setIsPrdReadingSaving(false);
    }
  }, [
    activityId,
    api,
    canEditPrdReadingBeforeGuards,
    isPrdReadingSaving,
    loadMessages,
    prdReadingDraftContent,
  ]);

  const handleStartTechSpecReadingEdit = useCallback(() => {
    if (!canEditTechSpecReadingBeforeGuards) {
      return;
    }
    setTechSpecReadingDraftContent(techSpecReadingDisplayContent);
    setIsTechSpecReadingEditing(true);
  }, [canEditTechSpecReadingBeforeGuards, techSpecReadingDisplayContent]);

  const handleCancelTechSpecReadingEdit = useCallback(() => {
    setTechSpecReadingDraftContent(techSpecReadingDisplayContent);
    setIsTechSpecReadingEditing(false);
  }, [techSpecReadingDisplayContent]);

  const handleSaveTechSpecReadingEdit = useCallback(async () => {
    if (
      !activityId ||
      !canEditTechSpecReadingBeforeGuards ||
      isTechSpecReadingSaving
    ) {
      return;
    }

    const content = techSpecReadingDraftContent;
    if (content.trim().length === 0) {
      setError('O conteúdo do TechSpec não pode ficar vazio.');
      return;
    }

    setIsTechSpecReadingSaving(true);
    setError(undefined);
    try {
      await api.saveTechSpecDraft(activityId, content);
      await loadMessages('gerando-techspec');
      setIsTechSpecReadingEditing(false);
    } catch (e) {
      setError(
        `Erro ao salvar TechSpec: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    } finally {
      setIsTechSpecReadingSaving(false);
    }
  }, [
    activityId,
    api,
    canEditTechSpecReadingBeforeGuards,
    isTechSpecReadingSaving,
    loadMessages,
    techSpecReadingDraftContent,
  ]);

  const currentPhaseMessages = messages.filter(m => m.phase === selectedPhase);
  const hasPersistedAssistantOutputForSelectedPhase =
    hasPersistedAssistantOutputForPhase(selectedPhase);
  const isCurrentPilotPhase =
    Boolean(activity) &&
    selectedPhase === activity?.currentPhase &&
    isPilotInteractionPhase(selectedPhase);
  const hasAuthoritativePilotStreamTransport =
    isCurrentPilotPhase &&
    (isLiveTransitionPending || isChatStreamActive || isRecoveryStreamActive);
  const selectedArtifact = selectPreferredArtifactForPhase(
    artifacts,
    selectedPhase,
  );
  const hasSettledPilotOutput =
    isCurrentPilotPhase &&
    hasPersistedAssistantOutputForSelectedPhase &&
    !hasAuthoritativePilotStreamTransport &&
    persistedInteractionState?.status !== 'streaming' &&
    !streamUi.pendingQuestions?.length &&
    !streamUi.pendingApproaches &&
    !pilotRecoveryStreamAbortRef.current;

  useEffect(() => {
    if (!hasSettledPilotOutput) {
      return;
    }

    pilotRecoveryRequestedRef.current = false;
    if (pilotRecoveryPollRef.current) {
      clearInterval(pilotRecoveryPollRef.current);
      pilotRecoveryPollRef.current = null;
    }

    if (
      interactionUiStatus === 'recovering' ||
      interactionUiStatus === 'streaming'
    ) {
      setInteractionUiStatus(streamUi.phaseCompleted ? 'completed' : 'idle');
    }
  }, [hasSettledPilotOutput, interactionUiStatus, streamUi.phaseCompleted]);

  useEffect(() => {
    if (!activity || rbacLoading || rbacError) {
      return;
    }

    lastSuccessfulApprovePermissionRef.current[activity.azureProject] =
      hasPermission(activity.azureProject, 'phase.approve');
  }, [activity, hasPermission, rbacError, rbacLoading]);

  const shouldConvergeArtifactApproval =
    Boolean(activity) &&
    (selectedPhase === 'gerando-prd' || selectedPhase === 'gerando-techspec') &&
    selectedPhase === activity?.currentPhase &&
    activity.status === 'active' &&
    !hasAuthoritativePilotStreamTransport &&
    !streamUi.isTaskStreaming &&
    persistedInteractionState?.status !== 'streaming' &&
    (streamUi.phaseCompleted ||
      hasPersistedAssistantOutputForSelectedPhase ||
      selectedArtifact !== undefined) &&
    (Boolean(streamUi.pendingQuestions?.length) ||
      Boolean(streamUi.pendingApproaches) ||
      interactionUiStatus === 'awaiting_user' ||
      interactionUiStatus === 'recovering' ||
      interactionUiStatus === 'streaming' ||
      persistedInteractionState?.status === 'awaiting_user' ||
      postAnswerRecovery.stage !== 'idle' ||
      (streamUi.phaseCompleted &&
        !hasPersistedAssistantOutputForSelectedPhase &&
        selectedArtifact === undefined));

  useEffect(() => {
    if (!shouldConvergeArtifactApproval) {
      return;
    }

    const hasActiveSubmittedPostAnswerRecovery =
      submittedPendingInteractionRef.current?.phase === selectedPhase &&
      postAnswerRecovery.stage !== 'idle' &&
      postAnswerRecovery.stage !== 'completed' &&
      postAnswerRecovery.stage !== 'awaiting_new_interaction' &&
      postAnswerRecovery.stage !== 'failed_timeout' &&
      persistedInteractionState?.status !== 'completed' &&
      !streamUi.phaseCompleted &&
      selectedArtifact === undefined;

    if (hasActiveSubmittedPostAnswerRecovery) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const clearStaleApprovalBlockers = () => {
      if (streamUi.pendingQuestions?.length) {
        dispatchStreamUi({ type: 'chat_clear_questions' });
      }
      if (streamUi.pendingApproaches) {
        dispatchStreamUi({ type: 'chat_clear_approaches' });
      }

      if (pilotRecoveryStreamAbortRef.current) {
        pilotRecoveryStreamAbortRef.current.abort();
        pilotRecoveryStreamAbortRef.current = null;
      }
      pilotRecoveryStreamAttemptRef.current = null;
      pilotRecoveryEventReceivedRef.current = false;
      pilotRecoveryRequestedRef.current = false;

      stopPostAnswerRecoveryAutomation();
      dispatchPostAnswerRecovery({ type: 'reset' });
      setPostAnswerRecoveryElapsedMs(0);
      clearSubmittedPendingInteraction(selectedPhase);
      setPendingInteractionModalOpen(false);
      setPersistedInteractionState(current =>
        current?.status === 'awaiting_user' || current?.status === 'streaming'
          ? null
          : current,
      );
      setInteractionUiStatus(streamUi.phaseCompleted ? 'completed' : 'idle');
    };

    const runConvergence = async () => {
      clearStaleApprovalBlockers();
      await Promise.all([
        loadActivity(),
        loadMessages(selectedPhase),
        loadArtifacts(),
      ]);
    };

    void runConvergence();

    const retryTimer = setInterval(() => {
      if (cancelled) {
        return;
      }
      attempts += 1;
      if (attempts >= APPROVAL_CONVERGENCE_MAX_ATTEMPTS) {
        clearInterval(retryTimer);
        return;
      }
      void runConvergence();
    }, APPROVAL_CONVERGENCE_RETRY_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(retryTimer);
    };
  }, [
    clearSubmittedPendingInteraction,
    loadActivity,
    loadArtifacts,
    loadMessages,
    persistedInteractionState?.status,
    postAnswerRecovery.stage,
    selectedPhase,
    shouldConvergeArtifactApproval,
    stopPostAnswerRecoveryAutomation,
    streamUi.pendingApproaches,
    streamUi.pendingQuestions,
    streamUi.phaseCompleted,
  ]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Frontier" subtitle="Carregando..." />
        <Content>
          <Progress />
          <Grid container spacing={2} className={classes.loadingSkeleton}>
            <Grid item xs={12}>
              <Skeleton variant="rect" height={460} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rect" height={180} />
            </Grid>
          </Grid>
        </Content>
      </Page>
    );
  }

  if (lockedByOwner) {
    return (
      <Page themeId="tool">
        <Header title="Frontier" subtitle="Atividade bloqueada" />
        <Content>
          <EmptyState
            title="Atividade pertence a outro usuário"
            description="Apenas o dono da atividade pode acessá-la enquanto ela não estiver concluída. O acesso será liberado quando a atividade for concluída."
            missing="info"
            action={
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to={normalizeRouteRefPath(rootRoute())}
              >
                Voltar para a lista
              </Button>
            }
          />
        </Content>
      </Page>
    );
  }

  if (!activity) {
    return (
      <Page themeId="tool">
        <Header title="Frontier" subtitle="Atividade não encontrada" />
        <Content>
          <EmptyState
            title="Atividade não encontrada"
            description={error || 'A atividade solicitada não foi encontrada.'}
            missing="info"
          />
        </Content>
      </Page>
    );
  }

  const projectLabel =
    projectLabels[activity.azureProject] ?? activity.azureProject;
  const createdBy = createdByLabel ?? formatUserEntityRef(activity.userId);

  // Show preparing state while clone is in progress
  if (activity.status === 'preparing') {
    return (
      <Page themeId="tool">
        <Content>
          <ActivityHeader
            titulo={activity.title}
            responsavel={createdBy}
            projeto={projectLabel}
            repositorio={activity.azureRepository}
            provider={activity.provider}
            issue={activity.issue ?? null}
            showProviderBadge
            aiEngine={activity.aiEngine}
            model={activity.aiModel}
          />
          <WarningPanel
            title="Preparando workspace"
            message="O repositório está sendo clonado. Aguarde alguns instantes..."
            severity="info"
          />
          <Progress />
        </Content>
      </Page>
    );
  }

  const phaseOrder = pipelineDefinition.phases.map(phase => phase.id);
  const phaseLabels = pipelineDefinition.phases.reduce<Record<Phase, string>>(
    (acc, phase) => {
      acc[phase.id] = phase.label;
      return acc;
    },
    {} as Record<Phase, string>,
  );
  const selectedPhaseDefinition = pipelineDefinition.phases.find(
    phase => phase.id === selectedPhase,
  );
  const currentPhaseDefinition = pipelineDefinition.phases.find(
    phase => phase.id === activity.currentPhase,
  );
  const selectedIndex = phaseOrder.indexOf(selectedPhase);
  const currentIndex = phaseOrder.indexOf(activity.currentPhase);
  const isPhaseCompleted =
    selectedIndex !== -1 && currentIndex !== -1 && selectedIndex < currentIndex;
  const isActivityDone = activity.status === 'completed';
  const isQaAutoExecutionActive =
    selectedPhase === 'qa' &&
    activity.currentPhase === 'qa' &&
    streamUi.executionState === 'running';
  const isChatStreaming =
    isQaAutoExecutionActive ||
    (isCurrentPilotPhase
      ? hasAuthoritativePilotStreamTransport
      : streamUi.isStreaming);
  const pilotCanBypassPassiveOwnershipLock =
    isCurrentPilotPhase && isPilotTabPassive && canReclaimPilotOwnership();
  const isPilotPassiveState =
    isCurrentPilotPhase &&
    isPilotTabPassive &&
    !pilotCanBypassPassiveOwnershipLock;
  const pilotHasRecoveryEvidence =
    isCurrentPilotPhase && hasPilotRecoveryEvidence();
  const hasPilotAwaitingInteraction =
    isCurrentPilotPhase &&
    (interactionUiStatus === 'awaiting_user' ||
      interactionUiStatus === 'resumable' ||
      persistedInteractionState?.status === 'awaiting_user' ||
      Boolean(streamUi.pendingQuestions?.length) ||
      Boolean(streamUi.pendingApproaches));
  const hasPilotStreamingInteraction =
    isCurrentPilotPhase &&
    !hasSettledPilotOutput &&
    (hasAuthoritativePilotStreamTransport ||
      (interactionUiStatus === 'streaming' &&
        (persistedInteractionState?.status === 'streaming' ||
          Boolean(pilotRecoveryStreamAbortRef.current) ||
          pilotRecoveryRequestedRef.current)));
  const isPilotRecoveringState =
    !hasSettledPilotOutput &&
    pilotHasRecoveryEvidence &&
    interactionUiStatus === 'recovering';
  const isChatDisabled =
    isPhaseCompleted ||
    isActivityDone ||
    isPilotRecoveringState ||
    isPilotPassiveState ||
    // ADR-013: brief input-disabled window while the same SSE session hands
    // control from the previous pilot phase to the next one.
    isLiveTransitionPending;
  const effectivePhaseSyncWarning =
    phaseSyncWarning === PASSIVE_STREAM_WARNING && !isPilotPassiveState
      ? undefined
      : phaseSyncWarning;

  // Hide system-generated context messages (buildInitialMessage / buildAutoStartMessage)
  // from the chat UI. These are persisted as user messages but should not be displayed.
  const chatVisibleMessages = currentPhaseMessages.filter(
    m => !isHiddenSystemUserMessage(m),
  );
  const summarizedChatMessages = (() => {
    const summaryAccumulator: ToolUseSummary = {
      tools: [],
      totalEvents: 0,
    };
    const toolSet = new Set<string>();

    const normalizedMessages = chatVisibleMessages.map(message => {
      if (message.role !== 'assistant' || !message.content) {
        return message;
      }

      const summarized = summarizeToolUseMarkersForDisplay(message.content);
      if (summarized.summary) {
        summaryAccumulator.totalEvents += summarized.summary.totalEvents;
        for (const tool of summarized.summary.tools) {
          if (!toolSet.has(tool)) {
            toolSet.add(tool);
            summaryAccumulator.tools.push(tool);
          }
        }
      }

      if (summarized.content === message.content) {
        return message;
      }

      return {
        ...message,
        content: summarized.content,
      };
    });

    return {
      messages: normalizedMessages,
      summary: summaryAccumulator.totalEvents > 0 ? summaryAccumulator : null,
    };
  })();
  const interactionResolvedLabel = formatInteractionTimestamp(
    persistedInteractionState?.resolvedAt,
  );
  const pendingInteractionHint =
    persistedInteractionState?.resumeMode === 'summary_required'
      ? 'O contexto ativo ficou antigo. Ao responder, o PRD será retomado com um resumo do checkpoint.'
      : pendingInteractionType === 'question'
      ? 'Responda quando quiser para continuar o PRD sem perder o checkpoint atual.'
      : undefined;
  const pendingInteractionNotice = undefined;
  const pendingInteractionTitle =
    pendingInteractionType === 'approach'
      ? 'Escolha uma abordagem'
      : 'O agente precisa de informações';
  // ADR-013: banner string resolves against the currently selected phase so
  // PRD→TechSpec (and similar pilot-to-pilot transitions) no longer show
  // "PRD" hard-coded while the user is already looking at the next phase.
  const currentPhaseLabel = phaseLabels[selectedPhase] ?? 'PRD';
  const recoverySnapshotBaseline =
    stableRecoverySnapshotContent || recoverySnapshotContent;
  const effectiveRecoveryStreamingContent =
    isCurrentPilotPhase &&
    recoverySnapshotBaseline &&
    (interactionUiStatus === 'recovering' ||
      interactionUiStatus === 'streaming' ||
      interactionUiStatus === 'awaiting_user' ||
      interactionUiStatus === 'resumable' ||
      persistedInteractionState?.status === 'streaming' ||
      persistedInteractionState?.status === 'awaiting_user' ||
      pilotHasRecoveryEvidence)
      ? mergeRecoveryStreamingContent(
          recoverySnapshotBaseline,
          streamUi.streamingContent,
        )
      : streamUi.streamingContent;
  const isArtifactPhase =
    selectedPhase === 'gerando-prd' || selectedPhase === 'gerando-techspec';
  const isTaskPlanPhase = selectedPhase === 'dividindo-tasks';
  const hasRegisteredTaskPlan = streamUi.tasks.length > 0;
  const hasActivePostAnswerRecovery =
    isCurrentPilotPhase && postAnswerRecovery.stage !== 'idle';
  const postAnswerRecoveryUiState = hasActivePostAnswerRecovery
    ? getPostAnswerRecoveryUiState(postAnswerRecovery.stage)
    : null;
  const awaitingInteractionStatusMessage =
    pendingInteractionType === 'approach' ||
    persistedInteractionState?.type === 'approach' ||
    Boolean(streamUi.pendingApproaches)
      ? 'Aguardando abordagem...'
      : 'Aguardando nova pergunta...';
  const hasUsefulVisiblePilotContent =
    effectiveRecoveryStreamingContent.trim().length > 0 ||
    hasPersistedAssistantOutputForSelectedPhase ||
    selectedArtifact !== undefined;
  const effectivePilotRecoveryUiState = !isCurrentPilotPhase
    ? null
    : interactionUiStatus === 'expired'
    ? 'expired'
    : postAnswerRecoveryUiState === 'awaiting_user'
    ? 'awaiting_user'
    : postAnswerRecoveryUiState === 'completed_from_persistence' &&
      persistedInteractionState?.status === 'completed'
    ? 'completed_from_persistence'
    : postAnswerRecoveryUiState === 'backend_sync'
    ? 'backend_sync'
    : postAnswerRecoveryUiState === 'live_streaming' || isLiveTransitionPending
    ? 'live_streaming'
    : interactionUiStatus === 'resumable' ||
      interactionUiStatus === 'awaiting_user' ||
      persistedInteractionState?.status === 'awaiting_user'
    ? 'awaiting_user'
    : persistedInteractionState?.status === 'completed'
    ? 'completed_from_persistence'
    : isPilotRecoveringState
    ? 'backend_sync'
    : hasPilotStreamingInteraction
    ? 'live_streaming'
    : null;
  const showPostAnswerReconnectNow =
    isCurrentPilotPhase &&
    (postAnswerRecovery.stage === 'failed_timeout' ||
      authoritativeSyncFailureState?.kind === 'persistent');
  const postAnswerRecoveryActionLabel =
    authoritativeSyncFailureState?.kind === 'persistent'
      ? 'Tentar novamente'
      : 'Reconectar';
  const pilotInteractionStatusMessage = isCurrentPilotPhase
    ? isPilotPassiveState
      ? `Outra aba está conduzindo este stream. Esta aba permanece em modo passivo e acompanha mensagens, artefatos e métricas do ${currentPhaseLabel} automaticamente.`
      : authoritativeSyncFailureState?.kind === 'persistent'
      ? 'Nao foi possivel atualizar o estado. Tentando de novo em alguns segundos.'
      : authoritativeSyncFailureState?.kind === 'transient'
      ? 'Reconectando ao backend...'
      : isLiveTransitionPending
      ? `Preparando ${currentPhaseLabel}...`
      : effectivePilotRecoveryUiState === 'backend_sync'
      ? `Atualizando estado do ${currentPhaseLabel}...`
      : effectivePilotRecoveryUiState === 'awaiting_user'
      ? persistedInteractionState?.resumeMode === 'summary_required'
        ? `Aguardando sua resposta. O ${currentPhaseLabel} será retomado com um resumo do checkpoint quando você responder.`
        : awaitingInteractionStatusMessage
      : effectivePilotRecoveryUiState === 'completed_from_persistence' &&
        hasUsefulVisiblePilotContent
      ? 'Conteudo recuperado do backend.'
      : effectivePilotRecoveryUiState === 'expired'
      ? [
          interactionResolvedLabel
            ? `A interação expirou em ${interactionResolvedLabel}.`
            : 'A interação expirou.',
          getInteractionTerminalReasonLabel(
            persistedInteractionState?.terminalReason,
          ),
        ].join(' ')
      : undefined
    : undefined;
  const pilotInteractionStatusTone = !isCurrentPilotPhase
    ? undefined
    : isPilotPassiveState
    ? 'passive'
    : authoritativeSyncFailureState?.kind === 'persistent'
    ? 'warning'
    : authoritativeSyncFailureState?.kind === 'transient'
    ? 'sync'
    : isLiveTransitionPending
    ? 'active'
    : effectivePilotRecoveryUiState === 'backend_sync'
    ? 'sync'
    : effectivePilotRecoveryUiState === 'awaiting_user'
    ? 'passive'
    : effectivePilotRecoveryUiState === 'expired'
    ? 'warning'
    : 'active';
  const pilotInteractionStatusBusy =
    isCurrentPilotPhase &&
    (isLiveTransitionPending ||
      effectivePilotRecoveryUiState === 'backend_sync' ||
      effectivePilotRecoveryUiState === 'live_streaming' ||
      authoritativeSyncFailureState?.kind === 'transient' ||
      authoritativeSyncFailureState?.kind === 'persistent');
  const chatDisabledPlaceholder = isLiveTransitionPending
    ? `Preparando ${currentPhaseLabel}...`
    : effectivePilotRecoveryUiState === 'backend_sync'
    ? `Atualizando estado do ${currentPhaseLabel}...`
    : isPilotPassiveState
    ? 'Outra aba está conduzindo este stream...'
    : undefined;
  const chatEmptyStateMessage =
    isCurrentPilotPhase && isPilotPassiveState
      ? `Outra aba está conduzindo este stream. Esta aba acompanha o histórico e o estado atual do ${currentPhaseLabel}.`
      : isCurrentPilotPhase && isLiveTransitionPending
      ? recoverySnapshotBaseline
        ? `Recuperando o último contexto visível enquanto o backend atualiza a execução do ${currentPhaseLabel}.`
        : `A execução do ${currentPhaseLabel} continua no backend. O histórico parcial e as próximas perguntas aparecerão aqui assim que o estado for atualizado.`
      : isCurrentPilotPhase && effectivePilotRecoveryUiState === 'backend_sync'
      ? recoverySnapshotBaseline
        ? `Recuperando o último contexto visível enquanto o backend atualiza o ${currentPhaseLabel}.`
        : `Atualizando estado do ${currentPhaseLabel}...`
      : selectedPhase === 'gerando-techspec' ||
        selectedPhase === 'dividindo-tasks'
      ? ''
      : undefined;
  const stablePilotDraftContent =
    isCurrentPilotPhase || PILOT_INTERACTION_PHASES.includes(selectedPhase)
      ? mergeRecoveryStreamingContent(
          recoverySnapshotBaseline,
          streamUi.streamingContent,
        )
      : streamUi.streamingContent;
  // Show approve button when the selected phase has durable output and allows
  // approval. For Tasks, require both the registered task plan and persisted
  // review output so the CTA cannot appear before tasks.md is visible.
  // ADR-009: for artifact phases (gerando-prd / gerando-techspec), "phase has
  // content" must be computed from persisted state only — not from
  // streamingContent or temp-assistant fallback. Pronto/QA keep the old
  // permissive fallback so their streaming-driven approve gate is preserved.
  const hasPhaseContent = isTaskPlanPhase
    ? hasRegisteredTaskPlan &&
      (hasPersistedAssistantOutputForSelectedPhase ||
        selectedArtifact !== undefined)
    : isArtifactPhase
    ? hasPersistedAssistantOutputForSelectedPhase ||
      selectedArtifact !== undefined
    : currentPhaseMessages.length > 0 ||
      stablePilotDraftContent.length > 0 ||
      selectedArtifact !== undefined;
  const chatStreamingDisplayContent =
    hasPersistedAssistantOutputForSelectedPhase && !isChatStreaming
      ? ''
      : effectiveRecoveryStreamingContent;
  const summarizedStreamingDisplay = summarizeToolUseMarkersForDisplay(
    chatStreamingDisplayContent,
  );
  const chatToolUseSummary = mergeToolUseSummaries(
    summarizedChatMessages.summary,
    summarizedStreamingDisplay.summary,
  );
  // ADR-009: when selectedPhase is an artifact phase, draftArtifactMessage
  // must read from persisted assistant messages only (temp-assistant-* is the
  // optimistic post-stream fallback and does not count as persisted).
  const draftArtifactMessage = [...messages]
    .reverse()
    .find(
      message =>
        message.phase === selectedPhase &&
        message.role === 'assistant' &&
        message.content.trim().length > 0 &&
        (!isArtifactPhase || !message.id.startsWith('temp-assistant-')),
    );
  // Strip [Using tool: ...] markers from message content for reading mode display.
  // For Pronto/QA, fall back to streamingContent because the worker only emits
  // text chunks (the assistant message may not be persisted yet but
  // streamingContent is preserved via chat_complete). For artifact phases, do
  // NOT fall back — the artifact panel reads persisted state exclusively per
  // ADR-009, so the rendered content matches what the backend actually saved.
  const rawDraftContent = isArtifactPhase
    ? draftArtifactMessage?.content ?? ''
    : draftArtifactMessage?.content ?? stablePilotDraftContent ?? '';
  const draftContent = sanitizeArtifactDraftContent(rawDraftContent);
  // Fase PR: quando o backend persistiu o snapshot do Pull Request
  // (pr_title/pr_description gravados no create_pull_request), a Leitura exibe
  // esse conteúdo — não o texto livre do agente, que é só narração do fluxo.
  const persistedPrReadingContent =
    selectedPhase === 'pronto' ? buildPrReadingContent(activity) : null;
  const artifactDisplayState: ArtifactDisplayState = persistedPrReadingContent
    ? {
        source: 'approved',
        content: persistedPrReadingContent,
        updatedAt: activity.updatedAt,
      }
    : selectedArtifact
    ? {
        source: 'approved',
        // For QA/Pronto, prefer the full markdown report from messages over the artifact summary
        content:
          (selectedPhase === 'qa' || selectedPhase === 'pronto') && draftContent
            ? draftContent
            : selectedArtifact.content,
        updatedAt: selectedArtifact.createdAt,
      }
    : draftContent
    ? {
        source: 'draft',
        content: draftContent,
        updatedAt: draftArtifactMessage?.timestamp,
      }
    : {
        source: 'none',
        content: '',
      };
  const artifactSourceLabel =
    artifactDisplayState.source === 'approved'
      ? 'Aprovado'
      : artifactDisplayState.source === 'draft'
      ? 'Rascunho'
      : 'Sem artefato';
  const canUseViewModes = READABILITY_PRIORITY_PHASES.includes(selectedPhase);
  const effectiveViewMode: ViewMode = canUseViewModes ? viewMode : 'chat';
  const isReadingMode = effectiveViewMode === 'reading';
  const isPrdReadingMode = isReadingMode && selectedPhase === 'gerando-prd';
  const isTechSpecReadingMode =
    isReadingMode && selectedPhase === 'gerando-techspec';
  const isTaskPlanReadingMode =
    isReadingMode && selectedPhase === 'dividindo-tasks';
  const canEditPrdReading =
    isPrdReadingMode &&
    selectedPhase === activity.currentPhase &&
    activity.status === 'active' &&
    !isChatStreaming &&
    !streamUi.isTaskStreaming;
  const canEditTechSpecReading =
    isTechSpecReadingMode &&
    selectedPhase === activity.currentPhase &&
    activity.status === 'active' &&
    !isChatStreaming &&
    !streamUi.isTaskStreaming;
  const approveButtonLabel = approving
    ? 'Aprovando...'
    : `Aprovar ${
        phaseLabels[selectedPhase]?.split(' ').pop() ?? selectedPhase
      }`;
  const phaseHasUsableOutput = isTaskPlanPhase
    ? hasRegisteredTaskPlan &&
      (hasPersistedAssistantOutputForSelectedPhase ||
        selectedArtifact !== undefined)
    : artifactDisplayState.source !== 'none';
  const phaseHasRecoverableState =
    isCurrentPilotPhase &&
    (isPilotRecoveringState ||
      pilotHasRecoveryEvidence ||
      hasPilotStreamingInteraction ||
      hasPilotAwaitingInteraction);
  const isCheckpointResumableExpiredInteraction =
    interactionUiStatus === 'expired' &&
    (persistedInteractionState?.resumeMode === 'summary_required' ||
      persistedInteractionState?.terminalReason === 'session_lost' ||
      persistedInteractionState?.terminalReason === 'worker_disconnected' ||
      persistedInteractionState?.terminalReason === 'approaches_timeout');
  const showToolbarRestartButton =
    !isActivityDone &&
    selectedPhase === activity.currentPhase &&
    (selectedPhase === 'gerando-prd' || selectedPhase === 'gerando-techspec');
  const disableToolbarRestartButton = restarting;
  const showStartButton =
    currentPhaseMessages.length === 0 &&
    !isChatDisabled &&
    !isChatStreaming &&
    !streamUi.isTaskStreaming &&
    selectedPhase === activity.currentPhase &&
    Boolean(selectedPhaseDefinition?.actions.canStart) &&
    !phaseHasRecoverableState &&
    !hasActivePostAnswerRecovery &&
    // Require description for gerando-prd phase
    (selectedPhase !== 'gerando-prd' || briefingDescription.trim().length > 0);

  // D2 — gate de fases skill-dependentes por engine. Gate por CAPABILITY via
  // o contract (canEngineNameRunPhase), NÃO por `if engine === 'codex'`: a
  // lista de fases skill-dependentes vive só no contract (fonte única) e um
  // engine futuro sem skills é coberto automaticamente. Bloqueia o botão de
  // iniciar com tooltip clara quando a fase atual não é compatível.
  const engineGateBlocked = !canEngineNameRunPhase(
    activity.aiEngine ?? 'claude-code',
    selectedPhase,
  );
  // Tooltip nomeia a ENGINE ATIVA (não cita "Codex" fixo — com três engines o
  // texto errado confundia). Não há troca de engine em atividade existente:
  // a orientação é criar a atividade com uma engine que suporte a fase.
  const engineGateBlockedTooltip = engineGateBlocked
    ? `Esta fase usa skills customizadas que a engine ${
        AI_ENGINE_LABELS[activity.aiEngine ?? 'claude-code']
      } não suporta nesta versão. Pule esta fase ou crie a atividade com uma engine compatível (ex.: Claude Code).`
    : undefined;

  // Botão "Reconectar" visível quando o SSE caiu no meio de uma interação
  // (everStartedStreaming=true mas phaseCompleted=false e sem interação pendente).
  const showReconnectButton =
    !isActivityDone &&
    !isChatStreaming &&
    !isChatDisabled &&
    streamUi.everStartedStreaming &&
    !streamUi.phaseCompleted &&
    !streamUi.rateLimitInfo &&
    !streamUi.pendingQuestions?.length &&
    !streamUi.pendingApproaches &&
    !isCurrentPilotPhase &&
    interactionUiStatus !== 'expired' &&
    selectedPhase === activity.currentPhase;
  const showExpiredInteractionNotice =
    !isActivityDone &&
    !isChatStreaming &&
    selectedPhase === activity.currentPhase &&
    isPilotInteractionPhase(selectedPhase) &&
    interactionUiStatus === 'expired';
  const shouldResumeExpiredFromCheckpoint =
    showExpiredInteractionNotice && isCheckpointResumableExpiredInteraction;
  const canResumeExpiredInteraction = shouldResumeExpiredFromCheckpoint;
  const isApproveBlockedByPilotInteraction =
    isCurrentPilotPhase &&
    (hasPilotStreamingInteraction ||
      hasPilotAwaitingInteraction ||
      (isPilotRecoveringState && !phaseHasUsableOutput));

  // Botão "Aprovar" oculto quando a fase ainda não produziu saída persistida:
  // não faz sentido aprovar uma fase que não gerou artefato utilizável.
  // Para fases de artefato (PRD/TechSpec): exige source !== 'none', ou seja, artefato
  // aprovado no DB, mensagem de assistente no DB, ou streamingContent preservado via
  // chat_complete (para worker mode onde o backend salva apenas text chunks, não tool_use).
  // O backend agora aceita aprovação quando a fase tem qualquer mensagem (mensagensFase.length > 0).
  // For Pronto (requiresHumanApproval): only show after confirmed completion or page reload.
  // This prevents the button from appearing during SSE drops when the agent is still working.
  const isCurrentProntoPhase =
    selectedPhase === 'pronto' && selectedPhase === activity.currentPhase;
  const hasPersistedPrUrl = Boolean(activity.prUrl?.trim());
  const prontoReady =
    !isCurrentProntoPhase ||
    (hasPersistedPrUrl &&
      (streamUi.phaseCompleted ||
        (!streamUi.everStartedStreaming &&
          (currentPhaseMessages.length > 0 ||
            selectedArtifact !== undefined))));
  const isPrdReadingApprovalBlocked =
    isPrdReadingMode && (isPrdReadingEditing || isPrdReadingSaving);
  const isTechSpecReadingApprovalBlocked =
    isTechSpecReadingMode &&
    (isTechSpecReadingEditing || isTechSpecReadingSaving);
  const cachedApprovePermission = activity
    ? lastSuccessfulApprovePermissionRef.current[activity.azureProject]
    : undefined;
  const currentApprovePermission = !activity
    ? true
    : hasPermission(activity.azureProject, 'phase.approve');
  // Fail-soft: keep the last successful permission decision while RBAC reloads
  // or transiently errors, so approval does not disappear mid-session.
  const rbacCanApprove = !activity
    ? true
    : rbacLoading || rbacError
    ? cachedApprovePermission ?? false
    : currentApprovePermission;
  const phaseConformityRecoveryView =
    selectedPhase === activity.currentPhase
      ? getPhaseConformityRecoveryView(activity.phaseConformity)
      : null;
  const phaseConformityReady = isPhaseConformityReadyForApproval(
    activity,
    selectedPhase,
  );
  const canShowApproval =
    !isActivityDone &&
    !restarting &&
    !isChatStreaming &&
    !isApproveBlockedByPilotInteraction &&
    selectedPhase === activity.currentPhase &&
    // QA is agent-approved in Frontier Web. The shared contract still keeps
    // canApprove=true for compatibility/fallbacks, but the UI must not expose
    // a human approval CTA during the QA phase.
    selectedPhase !== 'qa' &&
    Boolean(currentPhaseDefinition?.actions.canApprove) &&
    hasPhaseContent &&
    (!isArtifactPhase || artifactDisplayState.source !== 'none') &&
    phaseConformityReady &&
    prontoReady &&
    rbacCanApprove;
  const showApproveButton = canShowApproval;
  const hasTaskPlanToolProgress = isTaskPlanPhase && toolCount > 0;
  const showPilotStreamingIndicator =
    !isCurrentPilotPhase ||
    (!isLiveTransitionPending &&
      effectivePilotRecoveryUiState === 'live_streaming' &&
      (!hasUsefulVisiblePilotContent || hasTaskPlanToolProgress));

  const renderFinalizedView = () => {
    const ORDERED_PHASES: Phase[] = [
      'gerando-prd',
      'gerando-techspec',
      'dividindo-tasks',
      'executando',
      'qa',
      'pronto',
    ];

    const byPhase = ORDERED_PHASES.map(phase => {
      const phaseMetrics = metrics.filter(m => m.phase === phase);
      if (phaseMetrics.length === 0) return null;
      return {
        phase,
        label: PHASE_LABELS[phase] ?? phase,
        totalTokens: phaseMetrics.reduce((s, m) => s + m.totalTokens, 0),
        estimatedCost: phaseMetrics.reduce((s, m) => s + m.estimatedCost, 0),
        executions: phaseMetrics.length,
      };
    }).filter(Boolean) as Array<{
      phase: Phase;
      label: string;
      totalTokens: number;
      estimatedCost: number;
      executions: number;
    }>;

    const grandTotalTokens = byPhase.reduce((s, r) => s + r.totalTokens, 0);
    const grandTotalCost = byPhase.reduce((s, r) => s + r.estimatedCost, 0);
    const grandTotalExec = byPhase.reduce((s, r) => s + r.executions, 0);

    const fmtTokens = (n: number) =>
      n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
    const fmtCost = (c: number) => `$${c.toFixed(4)}`;

    return (
      <>
        <div className={classes.debugContainer}>
          <div
            className={classes.debugHeader}
            role="button"
            tabIndex={0}
            onClick={() => setFinalizedExpanded(prev => !prev)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setFinalizedExpanded(prev => !prev);
              }
            }}
          >
            <div>
              <Typography
                variant="subtitle2"
                className={classes.finalizedTitle}
              >
                Atividade Finalizada
              </Typography>
              <Typography variant="caption" color="textSecondary">
                PR criado em <strong>{activity!.azureRepository}</strong> (
                {activity!.azureProject})
              </Typography>
            </div>
            <IconButton
              size="small"
              aria-label={finalizedExpanded ? 'Recolher' : 'Expandir'}
            >
              {finalizedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {finalizedExpanded && (
            <div className={classes.debugContent}>
              <Typography
                variant="overline"
                className={classes.finalizedSectionTitle}
              >
                Custo por fase
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Fase</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Tokens</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Execuções</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Custo</strong>
                    </TableCell>
                  </TableRow>
                  {byPhase.map(row => (
                    <TableRow key={row.phase}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">
                        {fmtTokens(row.totalTokens)}
                      </TableCell>
                      <TableCell align="right">{row.executions}</TableCell>
                      <TableCell align="right">
                        {fmtCost(row.estimatedCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {byPhase.length > 0 && (
                    <TableRow className={classes.finalizedTotalRow}>
                      <TableCell>Total Geral</TableCell>
                      <TableCell align="right">
                        {fmtTokens(grandTotalTokens)}
                      </TableCell>
                      <TableCell align="right">{grandTotalExec}</TableCell>
                      <TableCell
                        align="right"
                        className={classes.finalizedTotalCost}
                      >
                        {fmtCost(grandTotalCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {byPhase.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhuma métrica disponível
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderConversationPanel = () => (
    <div className={classes.chatContainer}>
      {selectedPhase === 'executando' ? (
        <TaskListPanel
          tasks={streamUi.tasks}
          executionState={streamUi.executionState}
          onStartExecution={handleStartExecution}
          onPause={handlePause}
          onResume={handleResume}
          onRetry={handleRetry}
          isStreaming={streamUi.isTaskStreaming}
          streamingTaskNumber={streamUi.streamingTaskNumber}
          streamingContent={streamUi.taskStreamingContent}
          reviewResult={streamUi.reviewResult}
          isReviewLoading={streamUi.isReviewLoading}
          activeTaskSlots={
            Object.keys(streamUi.activeTaskSlots).length > 0
              ? Object.fromEntries(
                  Object.entries(streamUi.activeTaskSlots).map(([k, v]) => [
                    k,
                    {
                      content: v.content,
                      slot: v.slot,
                      totalSlots: v.totalSlots,
                    },
                  ]),
                )
              : undefined
          }
          workerConnected={workerConnected}
        />
      ) : selectedPhase === 'gerando-prd' &&
        activityId &&
        !isChatStreaming &&
        currentPhaseMessages.length === 0 &&
        !summarizedStreamingDisplay.content &&
        !streamUi.pendingQuestions?.length &&
        !streamUi.pendingApproaches &&
        !streamUi.everStartedStreaming &&
        !hasActivePostAnswerRecovery ? (
        <PrdBriefingComposer
          activityId={activityId}
          description={briefingDescription}
          onDescriptionChange={setBriefingDescription}
          files={contextFiles}
          onUpload={handleContextFileUpload}
          onDelete={handleContextFileDelete}
          workerConnected={workerConnected}
          onStart={handleStartPhase}
          disableStart={!workerConnected || engineGateBlocked}
          disableStartTooltip={
            engineGateBlocked
              ? engineGateBlockedTooltip
              : 'Conecte o worker para iniciar via Backstage'
          }
        />
      ) : (
        <>
          <ModelFallbackNotice
            message={streamUi.modelFallbackNotice}
            onClose={() =>
              dispatchStreamUi({ type: 'chat_clear_model_fallback' })
            }
          />
          <ChatPanel
            messages={summarizedChatMessages.messages}
            streamingContent={summarizedStreamingDisplay.content}
            isStreaming={isChatStreaming}
            disabled={isChatDisabled}
            disabledPlaceholder={chatDisabledPlaceholder}
            emptyStateMessage={chatEmptyStateMessage}
            onSendMessage={(text: string, skillId?: string) =>
              handleSendMessage(text, { skillId })
            }
            availableSkills={availableSkills}
            onLoadSkills={loadAvailableSkills}
            draftText={chatDraft}
            onDraftChange={setChatDraft}
            showStartButton={showStartButton}
            onStartPhase={handleStartPhase}
            disableStart={!workerConnected || engineGateBlocked}
            disableStartTooltip={
              engineGateBlocked
                ? engineGateBlockedTooltip
                : 'Conecte o worker para iniciar via Backstage'
            }
            phaseLabel={phaseLabels[selectedPhase]}
            isError={activity.status === 'error'}
            statusMessage={
              isQaAutoExecutionActive
                ? 'QA em execução automática, acompanhando progresso...'
                : pilotInteractionStatusMessage
            }
            statusTone={
              isQaAutoExecutionActive ? 'active' : pilotInteractionStatusTone
            }
            statusBusy={isQaAutoExecutionActive || pilotInteractionStatusBusy}
            recoveryState={
              postAnswerRecovery.stage !== 'idle'
                ? getPostAnswerRecoveryStageLabel(postAnswerRecovery.stage) ??
                  undefined
                : undefined
            }
            recoveryElapsedMs={
              postAnswerRecovery.stage !== 'idle'
                ? postAnswerRecoveryElapsedMs
                : undefined
            }
            recoveryRetryCount={
              postAnswerRecovery.stage !== 'idle'
                ? postAnswerRecovery.retryCount
                : undefined
            }
            showReconnectNow={showPostAnswerReconnectNow}
            onReconnectNow={handleRecoveryStatusAction}
            recoveryActionLabel={postAnswerRecoveryActionLabel}
            toolUseSummary={chatToolUseSummary}
            currentTool={currentTool}
            toolCount={toolCount}
            isThinking={streamUi.isThinking}
            showStreamingIndicator={showPilotStreamingIndicator}
            stabilizeInitialLayout={
              isCurrentPilotPhase &&
              summarizedChatMessages.messages.length === 0 &&
              summarizedStreamingDisplay.content.length === 0 &&
              !streamUi.pendingQuestions?.length &&
              !streamUi.pendingApproaches
            }
            pendingQuestions={streamUi.pendingQuestions}
            pendingApproaches={streamUi.pendingApproaches}
            pendingInteractionHint={pendingInteractionHint}
            pendingInteractionNotice={pendingInteractionNotice}
            onAnswerQuestion={handleAnswerQuestion}
            onSelectApproach={handleSelectApproach}
            onOpenPendingInteraction={reopenPendingInteraction}
            hidePendingInteractionInline={pendingInteractionModalOpen}
            rateLimitInfo={streamUi.rateLimitInfo}
            enableSmartAutoScroll={isCurrentPilotPhase}
            scrollPersistenceKey={
              isCurrentPilotPhase && activityId
                ? `${activityId}:${selectedPhase}`
                : undefined
            }
          />
        </>
      )}

      {showExpiredInteractionNotice && (
        <div className={classes.restartContainer}>
          <Typography variant="body2" color="textSecondary">
            {pilotInteractionStatusMessage ??
              'A interação foi encerrada e precisa ser atualizada antes de continuar.'}
          </Typography>
          {canResumeExpiredInteraction && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ReplayIcon />}
              onClick={handleResumeFromCheckpoint}
              disabled={restarting}
              aria-label="Retomar do checkpoint"
            >
              Retomar do checkpoint
            </Button>
          )}
        </div>
      )}

      {showReconnectButton && (
        <div className={classes.restartContainer}>
          <Typography variant="body2" color="textSecondary">
            A conexão com o agente foi interrompida. Sincronize para ver o
            estado atual.
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ReplayIcon />}
            onClick={handleReconnect}
            aria-label="Sincronizar estado"
          >
            Sincronizar
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Page themeId="tool">
      <Content>
        <ActivityHeader
          titulo={activity.title}
          responsavel={createdBy}
          projeto={projectLabel}
          repositorio={activity.azureRepository}
          provider={activity.provider}
          issue={activity.issue ?? null}
          showProviderBadge
          aiEngine={activity.aiEngine}
          model={activity.aiModel}
        />

        {error && (
          <Alert
            severity="error"
            className={classes.errorAlert}
            onClose={() => setError(undefined)}
          >
            {error}
          </Alert>
        )}

        {phaseConformityRepairNotice &&
          isPhaseConformityRepairNoticeCurrent(
            phaseConformityRepairNotice,
            activityId,
            activity.currentPhase,
            selectedPhase,
          ) && (
            <Alert severity="info" className={classes.errorAlert}>
              {phaseConformityRepairNotice.content}
            </Alert>
          )}

        {phaseConformityRecoveryView && (
          <Alert
            severity={phaseConformityRecoveryView.severity}
            className={classes.errorAlert}
            action={
              phaseConformityRecoveryView.action && rbacCanApprove ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handlePhaseConformityRecovery}
                  disabled={revalidatingConformity}
                >
                  {revalidatingConformity
                    ? phaseConformityRecoveryView.action.pendingLabel
                    : phaseConformityRecoveryView.action.label}
                </Button>
              ) : undefined
            }
          >
            <strong>{phaseConformityRecoveryView.message}</strong>
            {phaseConformityRecoveryView.details
              ? ` Pendências: ${phaseConformityRecoveryView.details}.`
              : ''}
          </Alert>
        )}

        {effectivePhaseSyncWarning && (
          <Alert
            severity="warning"
            className={classes.errorAlert}
            onClose={() => setPhaseSyncWarning(undefined)}
          >
            {effectivePhaseSyncWarning}
          </Alert>
        )}

        {activity.status === 'error' && activity.errorMessage && (
          <Alert severity="error" className={classes.errorAlert}>
            <strong>Atividade em erro:</strong> {activity.errorMessage}
          </Alert>
        )}

        {activity.cloneStatus === 'failed' && (
          <Alert
            severity="error"
            className={classes.errorAlert}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRetryClone}
                disabled={retryingClone}
              >
                {retryingClone ? 'Reclonando...' : 'Tentar novamente'}
              </Button>
            }
          >
            <strong>Clone do repositório falhou:</strong>{' '}
            {activity.cloneError ??
              'Não foi possível clonar o repositório. Tente novamente para retomar.'}
          </Alert>
        )}

        {activity.cloneStatus === 'cloning' && (
          <Alert severity="info" className={classes.errorAlert}>
            Clonando repositório... Esta etapa precisa terminar antes da PRD
            iniciar.
          </Alert>
        )}

        <PhaseStepper
          pipelineDefinition={pipelineDefinition}
          currentPhase={activity.currentPhase}
          selectedPhase={selectedPhase}
          activityStatus={activity.status}
          onPhaseClick={handlePhaseClick}
        />

        <ControlBar
          activityStatus={activity.status}
          viewMode={effectiveViewMode}
          onViewModeChange={setViewMode}
          canUseViewModes={canUseViewModes}
          disableReading={
            isArtifactPhase &&
            artifactDisplayState.source === 'none' &&
            !isChatStreaming
          }
          showApproveButton={showApproveButton}
          approveButtonLabel={approveButtonLabel}
          approving={approving}
          disableApprove={
            isPrdReadingApprovalBlocked || isTechSpecReadingApprovalBlocked
          }
          isChatStreaming={isChatStreaming}
          isTaskStreaming={streamUi.isTaskStreaming}
          onApprove={handleApprovePhase}
          showRestartButton={showToolbarRestartButton}
          restartButtonLabel={restarting ? 'Reiniciando...' : 'Reiniciar'}
          onRestart={handleRestartPhase}
          disableRestart={disableToolbarRestartButton}
        />

        {selectedPhase !== 'finalizado' && isReadingMode && (
          <>
            {(isPhaseCompleted || isActivityDone) && (
              <MetricsPanel
                metrics={metrics}
                selectedPhase={selectedPhase}
                layout="horizontal"
              />
            )}
          </>
        )}

        {isActivityDone && (
          <Alert severity="success" style={{ marginBottom: 16 }}>
            <Typography variant="body2">
              Atividade concluída. O Pull Request foi criado.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          {selectedPhase === 'finalizado' ? (
            <Grid item xs={12}>
              {renderFinalizedView()}
            </Grid>
          ) : (
            <Grid item xs={12}>
              {isReadingMode ? (
                <Paper elevation={0} className={classes.artifactMainCard}>
                  {isPrdReadingMode ? (
                    <>
                      <div className={classes.prdReadingActions}>
                        {canEditPrdReading && (
                          <>
                            {isPrdReadingEditing ? (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCancelPrdReadingEdit}
                                  disabled={isPrdReadingSaving}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  onClick={handleSavePrdReadingEdit}
                                  disabled={isPrdReadingSaving}
                                >
                                  {isPrdReadingSaving
                                    ? 'Salvando...'
                                    : 'Salvar'}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={handleStartPrdReadingEdit}
                              >
                                Editar
                              </Button>
                            )}
                          </>
                        )}
                      </div>

                      {isPrdReadingEditing ? (
                        <TextField
                          className={classes.prdReadingEditor}
                          value={prdReadingDraftContent}
                          onChange={event =>
                            setPrdReadingDraftContent(event.target.value)
                          }
                          multiline
                          minRows={16}
                          variant="outlined"
                          placeholder="Edite o conteúdo do PRD..."
                          inputProps={{ 'aria-label': 'Editor de PRD' }}
                        />
                      ) : (
                        <div className={classes.prdReadingPreview}>
                          <MarkdownContent content={prdReadingDisplayContent} />
                        </div>
                      )}
                    </>
                  ) : isTechSpecReadingMode ? (
                    <>
                      <div className={classes.prdReadingActions}>
                        {canEditTechSpecReading && (
                          <>
                            {isTechSpecReadingEditing ? (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCancelTechSpecReadingEdit}
                                  disabled={isTechSpecReadingSaving}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  onClick={handleSaveTechSpecReadingEdit}
                                  disabled={isTechSpecReadingSaving}
                                >
                                  {isTechSpecReadingSaving
                                    ? 'Salvando...'
                                    : 'Salvar'}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={handleStartTechSpecReadingEdit}
                              >
                                Editar
                              </Button>
                            )}
                          </>
                        )}
                      </div>

                      {isTechSpecReadingEditing ? (
                        <TextField
                          className={classes.prdReadingEditor}
                          value={techSpecReadingDraftContent}
                          onChange={event =>
                            setTechSpecReadingDraftContent(event.target.value)
                          }
                          multiline
                          minRows={16}
                          variant="outlined"
                          placeholder="Edite o conteúdo do TechSpec..."
                          inputProps={{ 'aria-label': 'Editor de TechSpec' }}
                        />
                      ) : (
                        <div className={classes.prdReadingPreview}>
                          <MarkdownContent
                            content={techSpecReadingDisplayContent}
                          />
                        </div>
                      )}
                    </>
                  ) : isTaskPlanReadingMode ? (
                    <div className={classes.prdReadingPreview}>
                      {taskPlanReadingDisplayContent ? (
                        <MarkdownContent
                          content={taskPlanReadingDisplayContent}
                        />
                      ) : null}
                      {showPilotStreamingIndicator ? (
                        <div className={classes.taskPlanReadingIndicator}>
                          <StreamingIndicator
                            isStreaming={isChatStreaming}
                            isThinking={streamUi.isThinking}
                            currentTool={currentTool}
                            toolCount={toolCount}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className={classes.artifactMeta}>
                        <Typography
                          variant="caption"
                          className={classes.artifactMetaText}
                        >
                          Origem: {artifactSourceLabel}
                          {artifactDisplayState.updatedAt
                            ? ` | Última atualização: ${new Date(
                                artifactDisplayState.updatedAt,
                              ).toLocaleString('pt-BR')}`
                            : ''}
                          {` | Fase atual: ${
                            phaseLabels[selectedPhase] ?? selectedPhase
                          }`}
                        </Typography>
                      </div>

                      <div className={classes.prdReadingPreview}>
                        <MarkdownContent
                          content={artifactDisplayState.content}
                        />
                      </div>
                    </>
                  )}
                </Paper>
              ) : (
                <>{renderConversationPanel()}</>
              )}
            </Grid>
          )}
        </Grid>
      </Content>

      {/* Modal priorizado da interação pendente do PRD */}
      <Dialog
        open={pendingInteractionModalOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.questionsDialogPaper }}
        onClose={dismissPendingInteractionModal}
        aria-label={pendingInteractionTitle}
      >
        <DialogContent className={classes.questionsDialogContent}>
          {(streamUi.pendingQuestions?.length ||
            streamUi.pendingApproaches) && (
            <PendingInteractionSurface
              title={pendingInteractionTitle}
              helperText={pendingInteractionHint}
              noticeText={pendingInteractionNotice}
              secondaryActionLabel="Responder depois"
              onSecondaryAction={dismissPendingInteractionModal}
            >
              {streamUi.pendingQuestions &&
              streamUi.pendingQuestions.length > 0 ? (
                <QuestionCard
                  questions={streamUi.pendingQuestions}
                  onSubmit={handleAnswerQuestion}
                />
              ) : streamUi.pendingApproaches ? (
                <ApproachViewer
                  question={streamUi.pendingApproaches.question}
                  approaches={streamUi.pendingApproaches.approaches}
                  onSelect={handleSelectApproach}
                  stackedLayout
                />
              ) : null}
            </PendingInteractionSurface>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
};
